import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiCheck, FiUsers, FiSend, FiShield, FiUserPlus, FiChevronRight, FiChevronDown, FiBriefcase, FiUser, FiArrowRight, FiMapPin } from 'react-icons/fi';
import SearchableDropdown, { DropdownOption } from '../../../components/ui/SearchableDropdown';
import PhoneInput from '../../../components/ui/PhoneInput';
import { loadTable, saveRow, addRow, removeRow, checkApi, isApiReady, getTableData, saveTableData, getNextId } from '../../db-viewer/data/dataAccess';
import { confirmDiscardChanges, setFormDirty, registerSaveHandler, submitSaver } from '../../../lib/unsavedChanges';
import { canAddPersonalClient } from '../../../lib/entitlements';
import { getAccountOwner as resolveAccountOwner } from '../utils/ownerEntities';
import { businessDefaultCurrency } from '../utils/baseCurrency';
import { fetchTableData, insertRow, updateRow, isApiAvailable } from '../../db-viewer/data/dbViewerService';
import './FamilyBusinessesPage.css';
import '../../profile/ProfilePage.css';

// =============================================
// Tab definitions for Family & Businesses
// =============================================
const TABS = [
  { key: 'about-yourself',        label: 'About Yourself' },
  { key: 'your-family',           label: 'Your Family' },
  { key: 'your-businesses',       label: 'Your business(es)' },
  { key: 'your-clients',          label: 'Your clients' },
  // 'manage-account-access' moved to Settings → Account Permissions (ManageAccountAccessTab)
] as const;

type TabKey = typeof TABS[number]['key'];

// =============================================
// About Yourself Tab
// Reads/writes directly to the `users` table (localStorage)
// Account Owner = owner_id IS NULL
// Connected to: user_type table (role), user_rights table (permissions)
// =============================================

/** The logged-in user's account owner row (multi-tenant safe — see ownerEntities). */
function getAccountOwner(usersData?: Record<string, any>[]): Record<string, any> | null {
  return resolveAccountOwner(usersData || getTableData('users'));
}

/** Get countries list from site_mstr_countries table */
function getCountries(countriesData?: Record<string, any>[]): string[] {
  const countries = countriesData || getTableData('site_mstr_countries');
  return countries.map(c => c.name).filter(Boolean).sort();
}

// Base currency of a business (Your Businesses + a client's businesses). Blank follows
// the country — of the business address, else of its owner — so the default moves with
// the address; picking a currency here is a deliberate override (e.g. a Canadian
// company that keeps its books in USD). Shown in DMS Filing, Bookkeeping and reports.
const BaseCurrencyField: React.FC<{
  value: string;
  country: string;                 // the business address country on the form
  ownerId: number | string | null | undefined;   // who the business belongs to (owner or client)
  users: Record<string, any>[];
  onChange: (code: string) => void;
}> = ({ value, country, ownerId, users, onChange }) => {
  const [currencies, setCurrencies] = useState<Record<string, any>[]>(() => getTableData('site_mstr_currencies'));
  useEffect(() => {
    let cancelled = false;
    loadTable('site_mstr_currencies').then(rows => { if (!cancelled && rows.length) setCurrencies(rows); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const fallback = businessDefaultCurrency({ busi_address_country: country, owner_id: ownerId }, users);
  const options = currencies
    // eslint-disable-next-line eqeqeq
    .filter(c => c.is_deleted != 1 && c.is_active != 0 && c.currency_code)
    .map(c => ({ code: String(c.currency_code).trim().toUpperCase(), name: c.currency_name || '', symbol: c.currency_symbol || '' }))
    .sort((a, b) => a.code.localeCompare(b.code));
  // Keep a saved code selectable even if it is not in the active list.
  if (value && !options.some(o => o.code === value)) options.unshift({ code: value, name: '', symbol: '' });
  return (
    <fieldset className="fb-fieldset">
      <legend>Base Currency</legend>
      <select value={value} onChange={e => onChange(e.target.value)} className="fb-input fb-input-full">
        <option value="">{fallback ? `Country default — ${fallback}` : 'Country default (set the business country)'}</option>
        {options.map(o => (
          <option key={o.code} value={o.code}>{o.code}{o.name ? ` — ${o.name}` : ''}{o.symbol ? ` (${o.symbol})` : ''}</option>
        ))}
      </select>
      <span className="fb-field-hint">
        The currency this business keeps its books in. Used as the default in document filing, bookkeeping and reports.
      </span>
    </fieldset>
  );
};


// =============================================
// Phone & Email Types
// =============================================

// Phone entry: type + number pair
interface PhoneEntry {
  type: string;
  number: string;
}

const MOBILE_PRESETS = ['Primary Mobile', 'Secondary Mobile'];
const LANDLINE_PRESETS = ['Home Phone', 'Business Phone'];
const MAX_PHONES_PER_SECTION = 10; // practical cap

// Build dropdown options for a mobile phone entry
// First 2 use preset names, extras get auto-numbered "Mobile 3", "Mobile 4", etc.
function getMobileTypeOptions(phones: PhoneEntry[], currentIdx: number): string[] {
  const used = phones.filter((_, i) => i !== currentIdx).map(p => p.type);
  // Start with unused presets
  const options = MOBILE_PRESETS.filter(t => !used.includes(t));
  // For entries beyond presets, generate numbered labels
  const currentType = phones[currentIdx]?.type;
  if (currentType && !MOBILE_PRESETS.includes(currentType) && !options.includes(currentType)) {
    options.push(currentType);
  }
  return options.length > 0 ? options : [currentType || `Mobile ${currentIdx + 1}`];
}

function getLandlineTypeOptions(phones: PhoneEntry[], currentIdx: number): string[] {
  const used = phones.filter((_, i) => i !== currentIdx).map(p => p.type);
  const options = LANDLINE_PRESETS.filter(t => !used.includes(t));
  const currentType = phones[currentIdx]?.type;
  if (currentType && !LANDLINE_PRESETS.includes(currentType) && !options.includes(currentType)) {
    options.push(currentType);
  }
  return options.length > 0 ? options : [currentType || `Phone ${currentIdx + 1}`];
}

/** Generate the next mobile label: "Primary Mobile", "Secondary Mobile", "Mobile 3", "Mobile 4"... */
function nextMobileLabel(phones: PhoneEntry[]): string {
  const used = phones.map(p => p.type);
  for (const preset of MOBILE_PRESETS) {
    if (!used.includes(preset)) return preset;
  }
  // All presets used — generate numbered
  let n = 3;
  while (used.includes(`Mobile ${n}`)) n++;
  return `Mobile ${n}`;
}

function nextLandlineLabel(phones: PhoneEntry[]): string {
  const used = phones.map(p => p.type);
  for (const preset of LANDLINE_PRESETS) {
    if (!used.includes(preset)) return preset;
  }
  let n = 3;
  while (used.includes(`Phone ${n}`)) n++;
  return `Phone ${n}`;
}

/** Validate email format — minimum u@d.c (1 char local, 1 char domain, 1 char TLD) */
function isValidEmail(email: string): boolean {
  if (!email) return false;
  // Regex: at least 1 char @ at least 1 char . at least 1 char
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/** Postal code format by country (common patterns) */
function isValidPostalCode(code: string, country: string): { valid: boolean; hint: string } {
  if (!code) return { valid: true, hint: '' };
  const trimmed = code.trim();
  const c = country.toLowerCase();
  // Canada: A1A 1A1
  if (c.includes('canada')) {
    const valid = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(trimmed);
    return { valid, hint: 'Format: A1A 1A1 (e.g. M5V 3L9)' };
  }
  // USA: 5 digits or 5+4
  if (c.includes('united states') || c.includes('usa')) {
    const valid = /^\d{5}(-\d{4})?$/.test(trimmed);
    return { valid, hint: 'Format: 12345 or 12345-6789' };
  }
  // UK: various patterns
  if (c.includes('united kingdom') || c.includes('uk')) {
    const valid = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s?\d[A-Za-z]{2}$/.test(trimmed);
    return { valid, hint: 'Format: SW1A 1AA' };
  }
  // India: 6 digits
  if (c.includes('india')) {
    const valid = /^\d{6}$/.test(trimmed);
    return { valid, hint: 'Format: 6 digits (e.g. 400001)' };
  }
  // Australia: 4 digits
  if (c.includes('australia')) {
    const valid = /^\d{4}$/.test(trimmed);
    return { valid, hint: 'Format: 4 digits (e.g. 2000)' };
  }
  // Generic: allow any non-empty
  return { valid: true, hint: '' };
}

// =============================================
// Business province-move capture (shared by own + client business forms)
// When a business's province changes on save, prompt for the effective date and
// record a period in user_business_location_history so sales tax follows the
// business by document date (resolved in ProcessReceiptsTab). Works for own and
// client businesses alike — both live in user_businesses, keyed by business_id.
// =============================================
function useBusinessMoveCapture(createdBy: number | null, onMessage?: (m: string) => void) {
  const [prompt, setPrompt] = useState<{ businessId: number; oldState: string; newState: string; country: string; prevSince: string } | null>(null);
  const [dateValue, setDateValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Call after saving a business: opens the prompt only when the province changed.
  const checkBusinessMove = useCallback((businessId: number, oldState: string, newState: string, country: string, prevSince: string) => {
    const a = String(oldState || '').trim().toUpperCase();
    const b = String(newState || '').trim().toUpperCase();
    if (a && b && a !== b && country) setPrompt({ businessId, oldState: a, newState: b, country, prevSince: (prevSince || '').slice(0, 10) || '2000-01-01' });
  }, []);

  const record = useCallback(async (moveDate: string) => {
    if (!prompt || !moveDate) return;
    setSaving(true);
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const dayBefore = (() => { const d = new Date(moveDate + 'T00:00:00'); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); })();
    try {
      const hist = await loadTable('user_business_location_history');
      // eslint-disable-next-line eqeqeq
      const open = hist.filter((h: any) => h.business_id == prompt.businessId && Number(h.is_deleted) !== 1 && !h.effective_to);
      if (open.length) {
        for (const o of open) await saveRow('user_business_location_history', o.location_id, { effective_to: dayBefore, updated_at: now }, 'location_id');
      } else {
        await addRow('user_business_location_history', {
          business_id: prompt.businessId, country: prompt.country, state_code: prompt.oldState,
          effective_from: prompt.prevSince, effective_to: dayBefore, created_by: createdBy,
          is_active: 1, is_deleted: 0, created_at: now, updated_at: now,
        });
      }
      await addRow('user_business_location_history', {
        business_id: prompt.businessId, country: prompt.country, state_code: prompt.newState,
        effective_from: moveDate, effective_to: null, created_by: createdBy,
        is_active: 1, is_deleted: 0, created_at: now, updated_at: now,
      });
      onMessage?.(`Recorded move to ${prompt.newState} effective ${moveDate}. Documents before that keep ${prompt.oldState} tax.`);
    } catch {
      onMessage?.('Could not record the location history.');
    } finally {
      setSaving(false);
      setPrompt(null);
      setDateValue('');
    }
  }, [prompt, createdBy, onMessage]);

  const moveModal = prompt ? (
    <div className="fb-move-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !saving) { setPrompt(null); setDateValue(''); } }}>
      <div className="fb-move-modal" role="dialog" aria-modal="true">
        <h3 className="fb-move-title">Province changed</h3>
        <p className="fb-move-text">
          This business moved from <strong>{prompt.oldState}</strong> to <strong>{prompt.newState}</strong>.
          When did the move take effect? Documents dated before this keep <strong>{prompt.oldState}</strong> sales tax;
          on/after it use <strong>{prompt.newState}</strong>.
        </p>
        <label className="fb-move-label">Effective date of move
          <input type="date" className="fb-move-date" value={dateValue} onChange={e => setDateValue(e.target.value)} autoFocus />
        </label>
        <div className="fb-move-actions">
          <button className="fb-move-skip" disabled={saving} onClick={() => { setPrompt(null); setDateValue(''); }}>Skip</button>
          <button className="fb-move-save" disabled={saving || !dateValue} onClick={() => record(dateValue)}>{saving ? 'Saving…' : 'Record move'}</button>
        </div>
      </div>
    </div>
  ) : null;

  return { checkBusinessMove, moveModal };
}

// =============================================
// About Yourself Tab — Read-only summary, edits happen in Profile
// =============================================
const AboutYourselfTab: React.FC = () => {
  const [owner, setOwner] = useState<Record<string, any> | null>(() => getAccountOwner());
  const location = useLocation();

  // Load from PostgreSQL if available
  useEffect(() => {
    loadTable('users').then(rows => {
      const o = getAccountOwner(rows);
      if (o) setOwner(o);
    }).catch(() => {});
  }, []);
  const isAdmin = location.pathname.startsWith('/admin');
  const profilePath = isAdmin ? '/admin/profile' : '/dashboard/profile';

  if (!owner) {
    return <div className="fb-no-owner">No account owner found. Please set up your account first.</div>;
  }

  const fullName = [owner.first_name, owner.middle_name, owner.last_name].filter(Boolean).join(' ') || '—';
  const phone = owner.mobile_number || '—';
  const email = owner.email || '—';
  const address = [owner.address_street, owner.address_city, owner.address_state, owner.address_postal_code, owner.address_country]
    .filter(Boolean).join(', ') || '—';

  return (
    <div className="profile-summary-card">
      <div className="profile-summary-header">
        <h3>Your Personal Information</h3>
        <p>This data is shared across all services. To make changes, edit your Profile.</p>
      </div>

      <div className="profile-summary-grid">
        <div className="profile-summary-item">
          <div className="profile-summary-label">Full Name</div>
          <div className="profile-summary-value">{fullName}</div>
        </div>
        <div className="profile-summary-item">
          <div className="profile-summary-label">Nickname</div>
          <div className={`profile-summary-value ${!owner.nickname ? 'empty' : ''}`}>{owner.nickname || 'Not set'}</div>
        </div>
        <div className="profile-summary-item">
          <div className="profile-summary-label">Email</div>
          <div className="profile-summary-value">{email}</div>
        </div>
        <div className="profile-summary-item">
          <div className="profile-summary-label">Primary Mobile</div>
          <div className="profile-summary-value">{phone}</div>
        </div>
        <div className="profile-summary-item">
          <div className="profile-summary-label">Gender</div>
          <div className={`profile-summary-value ${!owner.gender ? 'empty' : ''}`}>{owner.gender || 'Not set'}</div>
        </div>
        <div className="profile-summary-item">
          <div className="profile-summary-label">Username</div>
          <div className={`profile-summary-value ${!owner.user_name ? 'empty' : ''}`}>{owner.user_name || 'Not set'}</div>
        </div>
        <div className="profile-summary-item full-width">
          <div className="profile-summary-label">Address</div>
          <div className={`profile-summary-value ${address === '—' ? 'empty' : ''}`}>{address}</div>
        </div>
      </div>

      <div className="profile-summary-actions">
        <Link to={profilePath} className="profile-edit-link">
          <FiEdit2 size={16} /> Edit in Profile
        </Link>
      </div>
    </div>
  );
};

// =============================================
// Your Family Tab
// Family members = rows in users table with owner_id = account owner's user_id
// user_type_id = 9 (End User), relationship filled in
// Follows ALL the same contact rules as AboutYourselfTab:
//   - Dynamic phone entries (mobile + landline dropdowns, unlimited add)
//   - Email format validation + verification flow
//   - Address with country, postal code validation, verify address
//   - Username format (4+.4+), nickname unique within account
//   - No duplicate phone numbers across ANY field
// Permissions:
//   - data_access_scope = 'all'  → Account Owner can CRUD all data
//   - data_access_scope = 'self' → Family member can only CRUD own data
// =============================================

const RELATIONSHIP_OPTIONS = ['Spouse', 'Child', 'Parent', 'Sibling', 'Other'];

interface FamilyFormFields {
  first_name: string;
  middle_name: string;
  last_name: string;
  relationship: string;
  email: string;
  gender: string;
  nickname: string;
  screen_name: string;
  user_name: string;
  // Address
  address_country: string;
  address_street: string;
  address_city: string;
  address_state: string;
  address_postal_code: string;
  country_code: string;
  // Mailing address
  mail_address_street: string;
  mail_address_city: string;
  mail_address_state: string;
  mail_address_postal_code: string;
  mail_address_country: string;
}

const EMPTY_FAMILY_FORM: FamilyFormFields = {
  first_name: '', middle_name: '', last_name: '',
  relationship: '', email: '', gender: '',
  nickname: '', screen_name: '', user_name: '',
  address_country: '', address_street: '', address_city: '',
  address_state: '', address_postal_code: '', country_code: '',
  mail_address_street: '', mail_address_city: '', mail_address_state: '',
  mail_address_postal_code: '', mail_address_country: '',
};

/** Get all family members under the account owner (excludes personal clients) */
function getFamilyMembers(ownerId: number | string, usersData?: Record<string, any>[]): Record<string, any>[] {
  const users = usersData || getTableData('users');
  // eslint-disable-next-line eqeqeq
  return users.filter(u => u.owner_id == ownerId && u.is_deleted !== 1 && u.relationship !== 'Personal Client');
}

// Order for the Your Family tiles: spouse first, then the children, then everyone else,
// by name within each group. (There is no birth date on a family member yet, so the
// children cannot be put oldest-to-youngest.)
function sortFamilyMembers(members: Record<string, any>[]): Record<string, any>[] {
  const group = (m: Record<string, any>) => {
    const rel = String(m.relationship || '').trim().toLowerCase();
    if (rel === 'spouse' || rel === 'partner') return 0;
    if (rel === 'child' || rel === 'son' || rel === 'daughter') return 1;
    return 2;
  };
  const name = (m: Record<string, any>) => `${m.first_name || ''} ${m.last_name || ''}`.trim().toLowerCase();
  return members.slice().sort((x, y) => group(x) - group(y) || name(x).localeCompare(name(y)));
}

/** Check if nickname is unique within account owner's portal */
function isNicknameUnique(ownerId: number | string, nickname: string, excludeUserId?: number | string, usersData?: Record<string, any>[]): boolean {
  if (!nickname) return true;
  const users = usersData || getTableData('users');
  const upper = nickname.toUpperCase();
  return !users.some(u => {
    // eslint-disable-next-line eqeqeq
    const sameAccount = u.owner_id == ownerId || u.user_id == ownerId;
    // eslint-disable-next-line eqeqeq
    const notSelf = excludeUserId ? u.user_id != excludeUserId : true;
    return sameAccount && notSelf && (u.nickname || '').toUpperCase() === upper && u.is_deleted !== 1;
  });
}

/**
 * Check an email is not taken by ANOTHER account. People in the same account share
 * freely: pass `accountId` and members added under it (owner_id == accountId) never
 * count as a clash — a spouse and children commonly use one family address. The
 * account head's own row still counts unless `headToo` (forms that ask "use your
 * own email?" handle the head separately).
 */
function isEmailUnique(email: string, excludeUserId?: number | string, usersData?: Record<string, any>[], accountId?: number | string | null, headToo?: boolean): boolean {
  if (!email) return true;
  const users = usersData || getTableData('users');
  const lower = email.toLowerCase();
  return !users.some(u => {
    // eslint-disable-next-line eqeqeq
    const notSelf = excludeUserId ? u.user_id != excludeUserId : true;
    // eslint-disable-next-line eqeqeq
    const sameAccount = accountId != null && (u.owner_id == accountId || (!!headToo && u.user_id == accountId));
    return notSelf && !sameAccount && (u.email || '').toLowerCase() === lower && u.is_deleted !== 1;
  });
}

/** Check if screen name is unique platform-wide */
function isScreenNameUnique(screenName: string, excludeUserId?: number | string, usersData?: Record<string, any>[]): boolean {
  if (!screenName) return true;
  const users = usersData || getTableData('users');
  const lower = screenName.toLowerCase();
  return !users.some(u => {
    // eslint-disable-next-line eqeqeq
    const notSelf = excludeUserId ? u.user_id != excludeUserId : true;
    return notSelf && (u.screen_name || '').toLowerCase() === lower && u.is_deleted !== 1;
  });
}

/** Check if an email matches the account owner's email */
function emailMatchesOwner(email: string, owner: Record<string, any> | null): boolean {
  if (!email || !owner) return false;
  return (owner.email || '').toLowerCase() === email.toLowerCase();
}

/** Check if a phone number conflicts with the account owner's phones */
function phoneConflictsWithOwner(number: string, owner: Record<string, any>): boolean {
  if (!number) return false;
  const clean = number.replace(/[^+\d]/g, '');
  if (!clean) return false;
  const ownerPhones = [
    owner.mobile_number, owner.secondary_mobile,
    owner.home_phone, owner.business_phone,
  ].filter(Boolean).map((p: string) => p.replace(/[^+\d]/g, ''));
  // Also check owner's extra_phones JSONB
  const extras: PhoneEntry[] = Array.isArray(owner.extra_phones) ? owner.extra_phones : [];
  extras.forEach(p => {
    if (p.number) ownerPhones.push(p.number.replace(/[^+\d]/g, ''));
  });
  return ownerPhones.includes(clean);
}

const YourFamilyTab: React.FC = () => {
  const [useApi, setUseApi] = useState(false);
  const [allUsers, setAllUsers] = useState<Record<string, any>[]>(() => getTableData('users'));
  const [owner, setOwner] = useState<Record<string, any> | null>(() => getAccountOwner());
  const ownerId = owner?.user_id;
  const countries = useMemo(() => getCountries(), []);
  const [members, setMembers] = useState<Record<string, any>[]>(() => ownerId ? getFamilyMembers(ownerId) : []);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [form, setForm] = useState<FamilyFormFields>(EMPTY_FAMILY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [savedMsg, setSavedMsg] = useState('');

  // Check API availability and load from PostgreSQL
  useEffect(() => {
    isApiAvailable().then(available => {
      setUseApi(available);
      if (available) {
        fetchTableData('users').then(rows => {
          setAllUsers(rows);
          const o = getAccountOwner(rows);
          setOwner(o);
          if (o?.user_id) setMembers(getFamilyMembers(o.user_id, rows));
        }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  // ---- Dynamic phone lists (same pattern as AboutYourselfTab) ----
  const [fMobilePhones, setFMobilePhones] = useState<PhoneEntry[]>([{ type: 'Primary Mobile', number: '' }]);
  const [fLandlinePhones, setFLandlinePhones] = useState<PhoneEntry[]>([{ type: 'Home Phone', number: '' }]);
  const [fPhoneError, setFPhoneError] = useState('');

  // ---- Email verification state ----
  const [fEmailError, setFEmailError] = useState('');
  const [fEmailVerifyState, setFEmailVerifyState] = useState<'idle' | 'sent' | 'verified'>('idle');
  const [fVerifyCode, setFVerifyCode] = useState('');
  const [fGeneratedCode, setFGeneratedCode] = useState('');
  const [fVerifyError, setFVerifyError] = useState('');

  // ---- Address verification state ----
  const [fAddressVerified, setFAddressVerified] = useState(false);
  const [fAddressVerifying, setFAddressVerifying] = useState(false);
  const [fPostalError, setFPostalError] = useState('');

  // ---- Mailing address state ----
  const [fMailSameAsPhysical, setFMailSameAsPhysical] = useState(false);
  const [fMailAddNew, setFMailAddNew] = useState(false);
  const [fMailPostalError, setFMailPostalError] = useState('');

  // ---- Owner-match confirmation for phone/email ----
  const [fOwnerPhoneConfirmed, setFOwnerPhoneConfirmed] = useState<Set<string>>(new Set());
  const [fOwnerEmailConfirmed, setFOwnerEmailConfirmed] = useState(false);

  const refreshMembers = useCallback(async () => {
    if (!ownerId) return;
    if (useApi) {
      try {
        const rows = await fetchTableData('users');
        setAllUsers(rows);
        setMembers(getFamilyMembers(ownerId, rows));
      } catch {
        setMembers(getFamilyMembers(ownerId));
      }
    } else {
      setMembers(getFamilyMembers(ownerId));
    }
  }, [ownerId, useApi]);

  const handleChange = useCallback((field: keyof FamilyFormFields, value: string) => {
    setFormDirty(true);
    setForm(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => ({ ...prev, [field]: '' }));
    // Reset address verified on address change
    if (field.startsWith('address_')) setFAddressVerified(false);
  }, []);

  // Phone format helper
  const formatPhone = (raw: string): string => {
    if (!raw) return '';
    return raw.replace(/[^+\d]/g, '');
  };

  // ---- Phone handlers ----
  const handleFMobileChange = useCallback((idx: number, field: 'type' | 'number', value: string) => {
    setFMobilePhones(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
    setFPhoneError('');
  }, []);

  const handleFLandlineChange = useCallback((idx: number, field: 'type' | 'number', value: string) => {
    setFLandlinePhones(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
    setFPhoneError('');
  }, []);

  const addFMobile = useCallback(() => {
    setFMobilePhones(prev => {
      if (prev.length >= MAX_PHONES_PER_SECTION) return prev;
      return [...prev, { type: nextMobileLabel(prev), number: '' }];
    });
  }, []);

  const addFLandline = useCallback(() => {
    setFLandlinePhones(prev => {
      if (prev.length >= MAX_PHONES_PER_SECTION) return prev;
      return [...prev, { type: nextLandlineLabel(prev), number: '' }];
    });
  }, []);

  const removeFMobile = useCallback((idx: number) => {
    setFMobilePhones(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
    setFPhoneError('');
  }, []);

  const removeFLandline = useCallback((idx: number) => {
    setFLandlinePhones(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
    setFPhoneError('');
  }, []);

  // ---- Email verification ----
  const handleFSendVerification = useCallback(() => {
    if (!isValidEmail(form.email)) {
      setFEmailError('Please enter a valid email (e.g. user@domain.com or u@d.c)');
      return;
    }
    setFEmailError('');
    const code = String(Math.floor(100000 + Math.random() * 900000));
    setFGeneratedCode(code);
    setFEmailVerifyState('sent');
    setFVerifyError('');
    console.log('[BK] Family email verification code (mock):', code, 'to:', form.email);
  }, [form.email]);

  const handleFVerifyCode = useCallback(() => {
    if (fVerifyCode.trim() === fGeneratedCode) {
      setFEmailVerifyState('verified');
      setFVerifyError('');
      console.log('[BK] Family email verified successfully');
    } else {
      setFVerifyError('Invalid code. Please check the code from the verification link.');
    }
  }, [fVerifyCode, fGeneratedCode]);

  // ---- Address verification (mock) ----
  const handleFVerifyAddress = useCallback(() => {
    setFAddressVerifying(true);
    setTimeout(() => {
      const hasAll = form.address_street && form.address_city &&
        form.address_state && form.address_postal_code && form.address_country;
      if (hasAll) {
        setFAddressVerified(true);
        console.log('[BK] Family address verified (mock)');
      }
      setFAddressVerifying(false);
    }, 1500);
  }, [form]);

  // ---- Postal code validation ----
  const handleFPostalChange = useCallback((value: string) => {
    handleChange('address_postal_code', value);
    if (value && form.address_country) {
      const { valid, hint } = isValidPostalCode(value, form.address_country);
      setFPostalError(valid ? '' : hint);
    } else {
      setFPostalError('');
    }
  }, [handleChange, form.address_country]);

  // ---- Mailing address handlers ----
  const handleFMailPostalChange = useCallback((value: string) => {
    handleChange('mail_address_postal_code', value);
    if (value && form.mail_address_country) {
      const { valid, hint } = isValidPostalCode(value, form.mail_address_country);
      setFMailPostalError(valid ? '' : hint);
    } else {
      setFMailPostalError('');
    }
  }, [handleChange, form.mail_address_country]);

  const handleFMailSameToggle = useCallback((checked: boolean) => {
    setFMailSameAsPhysical(checked);
    if (checked) {
      setFMailAddNew(false);
      // Copy physical address to mailing fields
      setForm(prev => ({
        ...prev,
        mail_address_street: prev.address_street,
        mail_address_city: prev.address_city,
        mail_address_state: prev.address_state,
        mail_address_postal_code: prev.address_postal_code,
        mail_address_country: prev.address_country,
      }));
      setFMailPostalError('');
    }
  }, []);

  const handleFMailAddNewToggle = useCallback((checked: boolean) => {
    setFMailAddNew(checked);
    if (checked) {
      setFMailSameAsPhysical(false);
      setForm(prev => ({
        ...prev,
        mail_address_street: '',
        mail_address_city: '',
        mail_address_state: '',
        mail_address_postal_code: '',
        mail_address_country: '',
      }));
      setFMailPostalError('');
    }
  }, []);

  // ---- Open / Close form ----
  const openAddForm = useCallback(() => {
    // New family member inherits address from owner
    setForm({
      ...EMPTY_FAMILY_FORM,
      address_country: owner?.address_country || '',
      address_street: owner?.address_street || '',
      address_city: owner?.address_city || '',
      address_state: owner?.address_state || '',
      address_postal_code: owner?.address_postal_code || '',
      country_code: owner?.country_code || '',
    });
    setFMobilePhones([{ type: 'Primary Mobile', number: '' }]);
    setFLandlinePhones([{ type: 'Home Phone', number: '' }]);
    setEditingId(null);
    setFormErrors({});
    setFPhoneError('');
    setFEmailError('');
    setFEmailVerifyState('idle');
    setFVerifyCode('');
    setFVerifyError('');
    setFAddressVerified(false);
    setFPostalError('');
    setFMailSameAsPhysical(false);
    setFMailAddNew(false);
    setFMailPostalError('');
    setFOwnerPhoneConfirmed(new Set());
    setFOwnerEmailConfirmed(false);
    setShowForm(true);
  }, [owner]);

  const openEditForm = useCallback((member: Record<string, any>) => {
    setForm({
      first_name: member.first_name || '',
      middle_name: member.middle_name || '',
      last_name: member.last_name || '',
      relationship: member.relationship || '',
      email: member.email || '',
      gender: member.gender || '',
      nickname: member.nickname || '',
      screen_name: member.screen_name || '',
      user_name: member.user_name || '',
      address_country: member.address_country || '',
      address_street: member.address_street || '',
      address_city: member.address_city || '',
      address_state: member.address_state || '',
      address_postal_code: member.address_postal_code || '',
      country_code: member.country_code || '',
      // Mailing address
      mail_address_street: member.mail_address_street || '',
      mail_address_city: member.mail_address_city || '',
      mail_address_state: member.mail_address_state || '',
      mail_address_postal_code: member.mail_address_postal_code || '',
      mail_address_country: member.mail_address_country || '',
    });
    // Load mailing address checkbox state
    setFMailSameAsPhysical(member.mail_same_as_physical === 1);
    setFMailAddNew(!!(member.mail_address_street || member.mail_address_city) && member.mail_same_as_physical !== 1);
    setFMailPostalError('');
    // Load phones from member record
    const mobiles: PhoneEntry[] = [];
    if (member.mobile_number) mobiles.push({ type: 'Primary Mobile', number: member.mobile_number });
    if (member.secondary_mobile) mobiles.push({ type: 'Secondary Mobile', number: member.secondary_mobile });
    const extras: PhoneEntry[] = Array.isArray(member.extra_phones) ? member.extra_phones : [];
    extras.filter((p: PhoneEntry) => p.type?.startsWith('Mobile')).forEach((p: PhoneEntry) => mobiles.push(p));
    if (mobiles.length === 0) mobiles.push({ type: 'Primary Mobile', number: '' });
    setFMobilePhones(mobiles);

    const landlines: PhoneEntry[] = [];
    if (member.home_phone) landlines.push({ type: 'Home Phone', number: member.home_phone });
    if (member.business_phone) landlines.push({ type: 'Business Phone', number: member.business_phone });
    extras.filter((p: PhoneEntry) => p.type?.startsWith('Phone')).forEach((p: PhoneEntry) => landlines.push(p));
    if (landlines.length === 0) landlines.push({ type: 'Home Phone', number: '' });
    setFLandlinePhones(landlines);

    setEditingId(member.user_id);
    setFormErrors({});
    setFPhoneError('');
    setFEmailError('');
    setFEmailVerifyState(member.email_verified === 1 ? 'verified' : 'idle');
    setFVerifyCode('');
    setFVerifyError('');
    setFAddressVerified(member.address_verified === 1);
    setFPostalError('');
    setFOwnerPhoneConfirmed(new Set());
    setFOwnerEmailConfirmed(false);
    setShowForm(true);
  }, []);

  const closeForm = useCallback(() => {
    setFormDirty(false);
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FAMILY_FORM);
    setFormErrors({});
    setFPhoneError('');
    setFEmailError('');
    setFEmailVerifyState('idle');
    setFPostalError('');
    setFMailSameAsPhysical(false);
    setFMailAddNew(false);
    setFMailPostalError('');
    setFOwnerPhoneConfirmed(new Set());
    setFOwnerEmailConfirmed(false);
  }, []);

  // ---- Submit ----
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ownerId || !owner) return;

    const errors: Record<string, string> = {};
    if (!form.first_name.trim()) errors.first_name = 'First name is required';
    if (!form.last_name.trim()) errors.last_name = 'Last name is required';

    // Email format validation (optional but validated if provided)
    if (form.email.trim() && !isValidEmail(form.email.trim())) {
      errors.email = 'Invalid email format (minimum: u@d.c)';
    } else if (!isEmailUnique(form.email.trim(), editingId ?? undefined, allUsers, ownerId)) {
      if (emailMatchesOwner(form.email.trim(), owner) && !fOwnerEmailConfirmed) {
        errors.email = '__owner_email__';
      } else if (!emailMatchesOwner(form.email.trim(), owner)) {
        errors.email = 'This email is already in use';
      }
    }

    // Nickname uniqueness within account
    if (form.nickname.trim() && !isNicknameUnique(ownerId, form.nickname.trim(), editingId ?? undefined, allUsers)) {
      errors.nickname = 'This nickname is already used in your account';
    }

    // Screen name uniqueness
    if (form.screen_name.trim() && !isScreenNameUnique(form.screen_name.trim(), editingId ?? undefined, allUsers)) {
      errors.screen_name = 'This screen name is already taken';
    }

    // Username format (4+.4+)
    if (form.user_name.trim() && !/^[a-zA-Z0-9]{4,}\.[a-zA-Z0-9]{4,}$/.test(form.user_name.trim())) {
      errors.user_name = 'Format: 4+ letters/numbers, a dot, then 4+ letters/numbers';
    }

    // Check for duplicate phone numbers across ALL fields
    const allPhoneNums = [...fMobilePhones, ...fLandlinePhones]
      .map(p => formatPhone(p.number))
      .filter(Boolean);
    if (allPhoneNums.length !== new Set(allPhoneNums).size) {
      setFPhoneError('Same phone number cannot be used in multiple fields. Each number must be unique.');
      setFormErrors(errors);
      return;
    }

    // Check phone numbers don't conflict with owner's phones
    for (const p of [...fMobilePhones, ...fLandlinePhones]) {
      const clean = formatPhone(p.number);
      if (clean && phoneConflictsWithOwner(p.number, owner) && !fOwnerPhoneConfirmed.has(clean)) {
        setFPhoneError(`__owner_phone__:${clean}`);
        setFormErrors(errors);
        return;
      }
    }
    setFPhoneError('');

    // Postal code validation
    if (form.address_postal_code && form.address_country) {
      const { valid, hint } = isValidPostalCode(form.address_postal_code, form.address_country);
      if (!valid) {
        setFPostalError(hint);
        setFormErrors(errors);
        return;
      }
    }

    // Mailing postal code validation (when Add New is checked)
    if (fMailAddNew && !fMailSameAsPhysical && form.mail_address_postal_code && form.mail_address_country) {
      const { valid, hint } = isValidPostalCode(form.mail_address_postal_code, form.mail_address_country);
      if (!valid) {
        setFMailPostalError(hint);
        setFormErrors(errors);
        return;
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);

    // Auto-generate nickname if blank
    const nickname = form.nickname.trim() ||
      ((form.first_name[0] || '') + (form.last_name[0] || '')).toUpperCase();

    // Auto-generate username if blank
    let userName = form.user_name.trim();
    if (!userName) {
      const fnClean = form.first_name.toLowerCase().replace(/[^a-z0-9]/g, '');
      const lnClean = form.last_name.toLowerCase().replace(/[^a-z0-9]/g, '');
      userName = (fnClean.slice(0, 4).padEnd(4, 'x')) + '.' + (lnClean.slice(0, 4).padEnd(4, 'x'));
    }

    // Build phone fields — same logic as AboutYourselfTab
    const mPrimary = fMobilePhones.find(p => p.type === 'Primary Mobile');
    const mSecondary = fMobilePhones.find(p => p.type === 'Secondary Mobile');
    const extraMobiles = fMobilePhones
      .filter(p => p.type !== 'Primary Mobile' && p.type !== 'Secondary Mobile' && p.number.trim())
      .map(p => ({ type: p.type, number: formatPhone(p.number) }));
    const lHome = fLandlinePhones.find(p => p.type === 'Home Phone');
    const lBusiness = fLandlinePhones.find(p => p.type === 'Business Phone');
    const extraLandlines = fLandlinePhones
      .filter(p => p.type !== 'Home Phone' && p.type !== 'Business Phone' && p.number.trim())
      .map(p => ({ type: p.type, number: formatPhone(p.number) }));
    const allExtras = [...extraMobiles, ...extraLandlines];

    // Build the common fields object for both insert and update
    const commonFields: Record<string, any> = {
      first_name: form.first_name.trim(),
      middle_name: form.middle_name.trim() || null,
      last_name: form.last_name.trim(),
      relationship: form.relationship,
      email: form.email.trim(),
      gender: form.gender,
      nickname: nickname,
      screen_name: form.screen_name.trim() || null,
      user_name: userName,
      display_name: `${form.first_name.trim()} ${form.last_name.trim()}`,
      mobile_number: formatPhone(mPrimary?.number || '') || null,
      secondary_mobile: formatPhone(mSecondary?.number || '') || null,
      home_phone: formatPhone(lHome?.number || '') || null,
      business_phone: formatPhone(lBusiness?.number || '') || null,
      extra_phones: allExtras.length > 0 ? allExtras : null,
      country_code: form.country_code || null,
      address_street: form.address_street || null,
      address_city: form.address_city || null,
      address_state: form.address_state || null,
      address_postal_code: form.address_postal_code || null,
      address_country: form.address_country || null,
      address_verified: fAddressVerified ? 1 : 0,
      mail_same_as_physical: fMailSameAsPhysical ? 1 : 0,
      mail_address_street: fMailSameAsPhysical ? (form.address_street || null) : (form.mail_address_street || null),
      mail_address_city: fMailSameAsPhysical ? (form.address_city || null) : (form.mail_address_city || null),
      mail_address_state: fMailSameAsPhysical ? (form.address_state || null) : (form.mail_address_state || null),
      mail_address_postal_code: fMailSameAsPhysical ? (form.address_postal_code || null) : (form.mail_address_postal_code || null),
      mail_address_country: fMailSameAsPhysical ? (form.address_country || null) : (form.mail_address_country || null),
      email_verified: fEmailVerifyState === 'verified' ? 1 : 0,
    };

    if (useApi) {
      // ---- PostgreSQL path ----
      try {
        if (editingId) {
          await updateRow('users', editingId, { ...commonFields, updated_at: now });
          console.log('[BK] Updated family member user_id:', editingId, '(PostgreSQL)');
        } else {
          const newRowData: Record<string, any> = {
            ...commonFields,
            owner_id: ownerId,
            user_type_id: 9,
            password_hash: 'HASH_invite_pending',
            data_access_scope: 'self',
            notification: 1,
            is_active: 1,
            is_deleted: 0,
          };
          const inserted = await insertRow('users', newRowData);
          console.log('[BK] Added family member user_id:', inserted.user_id, 'under owner_id:', ownerId, '(PostgreSQL)');
        }
      } catch (err: any) {
        setSavedMsg(err.message || 'Save failed');
        setTimeout(() => setSavedMsg(''), 3000);
        return;
      }
    } else {
      // ---- localStorage path ----
      const users = getTableData('users');
      if (editingId) {
        // eslint-disable-next-line eqeqeq
        const idx = users.findIndex(u => u.user_id == editingId);
        if (idx >= 0) {
          users[idx] = { ...users[idx], ...commonFields, updated_at: now };
          saveTableData('users', users);
          console.log('[BK] Updated family member user_id:', editingId);
        }
      } else {
        const newId = getNextId('users', users);
        const newRow: Record<string, any> = {
          user_id: newId,
          owner_id: ownerId,
          user_type_id: 9,
          ...commonFields,
          busi_legal_name: null, busi_operating_name: null, busi_type: null, busi_registration_number: null,
          extra_phones: allExtras.length > 0 ? allExtras : null,
          email_history: [], failed_email_attempts: [],
          service_id: null, service_mods_id: null, service_group_id: null, type_subuser_id: null,
          notification: 1,
          password_hash: 'HASH_invite_pending',
          email_otp: null, mobile_otp: null,
          mobile_verified: 0, home_phone_verified: 0,
          social_provider: null, social_provider_id: null,
          profile_pic: null,
          signup_date: now, signup_ip: null,
          last_login: null, last_login_ip: null,
          session_token: null, session_expires_at: null,
          failed_login_attempts: 0, locked_until: null,
          vpn_blocked: 0,
          data_access_scope: 'self',
          is_active: 0, is_deleted: 0,
          created_at: now, updated_at: now,
        };
        users.push(newRow);
        saveTableData('users', users);
        console.log('[BK] Added family member user_id:', newId, 'under owner_id:', ownerId, 'scope: self');
      }
    }

    refreshMembers();
    closeForm();
    setSavedMsg(editingId ? 'Family member updated!' : 'Family member added!');
    setTimeout(() => setSavedMsg(''), 3000);
  }, [form, ownerId, owner, editingId, fMobilePhones, fLandlinePhones, useApi, allUsers,
      fEmailVerifyState, fAddressVerified, fMailSameAsPhysical, fMailAddNew,
      fOwnerPhoneConfirmed, fOwnerEmailConfirmed, refreshMembers, closeForm]);

  const handleRemove = useCallback(async (memberId: number | string) => {
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    if (useApi) {
      try {
        await updateRow('users', memberId, { is_deleted: 1, updated_at: now });
        console.log('[BK] Soft-deleted family member user_id:', memberId, '(PostgreSQL)');
      } catch (err: any) {
        setSavedMsg(err.message || 'Remove failed');
        setTimeout(() => setSavedMsg(''), 3000);
        return;
      }
    } else {
      const users = getTableData('users');
      // eslint-disable-next-line eqeqeq
      const idx = users.findIndex(u => u.user_id == memberId);
      if (idx >= 0) {
        users[idx].is_deleted = 1;
        users[idx].updated_at = now;
        saveTableData('users', users);
        console.log('[BK] Soft-deleted family member user_id:', memberId);
      }
    }
    refreshMembers();
    setSavedMsg('Family member removed.');
    setTimeout(() => setSavedMsg(''), 3000);
  }, [refreshMembers, useApi]);

  // "Save & continue" for this form (modal). submitSaver detects success via the dirty flag.
  useEffect(() => {
    registerSaveHandler(submitSaver(handleSubmit));
    return () => registerSaveHandler(null);
  }, [handleSubmit]);

  if (!owner || !ownerId) {
    return <div className="fb-no-owner">No account owner found. Please set up your account first.</div>;
  }

  return (
    <div className="fb-family-section">
      {/* Info Banner */}
      <div className="fb-info-banner">
        <p><strong>Add family members who share this account.</strong></p>
        <p>Family members are added as sub-users (End User) under your account. They can log in independently with their own email.</p>
        <p className="fb-info-perm">
          <strong>Permissions:</strong> Account Owner can add, edit, or delete any data.
          Family members can only add, edit, or delete data pertaining to themselves.
        </p>
      </div>

      {/* Action Bar */}
      <div className="fb-family-action-bar">
        <span className="fb-family-count">
          <FiUsers size={16} />
          {members.length} family member{members.length !== 1 ? 's' : ''}
        </span>
        {savedMsg && <span className="fb-saved-msg">{savedMsg}</span>}
      </div>

      {/* ============ Add / Edit Form ============ */}
      {showForm && (
        <div className="fb-family-form-card">
          <div className="fb-family-form-header">
            <h4>{editingId ? 'Edit Family Member' : 'Add Family Member'}</h4>
            <button className="fb-btn-icon" onClick={closeForm} title="Cancel">
              <FiX size={18} />
            </button>
          </div>
          <form className="fb-form" onSubmit={handleSubmit}>
            {/* ---- Name Row ---- */}
            <fieldset className="fb-fieldset">
              <legend>Name *</legend>
              <div className="fb-name-row">
                <div>
                  <input type="text" placeholder="First Name" value={form.first_name}
                    onChange={e => handleChange('first_name', e.target.value)} required className="fb-input" />
                  {formErrors.first_name && <span className="fb-field-error">{formErrors.first_name}</span>}
                </div>
                <input type="text" placeholder="Middle Name" value={form.middle_name}
                  onChange={e => handleChange('middle_name', e.target.value)} className="fb-input" />
                <div>
                  <input type="text" placeholder="Last Name" value={form.last_name}
                    onChange={e => handleChange('last_name', e.target.value)} required className="fb-input" />
                  {formErrors.last_name && <span className="fb-field-error">{formErrors.last_name}</span>}
                </div>
              </div>
            </fieldset>

            {/* ---- Relationship ---- */}
            <fieldset className="fb-fieldset">
              <legend>Relationship</legend>
              <select value={form.relationship} onChange={e => handleChange('relationship', e.target.value)}
                className="fb-input fb-input-full">
                <option value="">Select Relationship</option>
                {RELATIONSHIP_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              {formErrors.relationship && <span className="fb-field-error">{formErrors.relationship}</span>}
            </fieldset>

            {/* ---- Nickname / Acronym ---- */}
            <fieldset className="fb-fieldset">
              <legend>Nickname or Acronym for Name</legend>
              <input type="text" placeholder="e.g. SM (auto-generated from initials if blank)"
                value={form.nickname} onChange={e => handleChange('nickname', e.target.value)}
                maxLength={20} className="fb-input fb-input-full" />
              <small className="fb-field-hint">Must be unique within your account. Auto-suggested from initials if left blank.</small>
              {formErrors.nickname && <span className="fb-field-error">{formErrors.nickname}</span>}
            </fieldset>

            {/* ---- Screen Name ---- */}
            <fieldset className="fb-fieldset">
              <legend>Screen Name</legend>
              <input type="text" placeholder="Public screen name (used in discussions)"
                value={form.screen_name} onChange={e => handleChange('screen_name', e.target.value)}
                maxLength={100} className="fb-input fb-input-full" />
              <small className="fb-field-hint">Must be unique across the platform. No profanity or system-reserved words.</small>
              {formErrors.screen_name && <span className="fb-field-error">{formErrors.screen_name}</span>}
            </fieldset>

            {/* ---- Gender ---- */}
            <fieldset className="fb-fieldset">
              <legend>Gender</legend>
              <select value={form.gender} onChange={e => handleChange('gender', e.target.value)}
                className="fb-input fb-input-full">
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Non-binary">Non-binary</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
              {formErrors.gender && <span className="fb-field-error">{formErrors.gender}</span>}
            </fieldset>

            {/* ---- Mobile Phones (dynamic, same as About Yourself) ---- */}
            <fieldset className="fb-fieldset">
              <legend>Mobile Phone(s)</legend>
              <small className="fb-field-hint">Verified via SMS. No number can be shared across fields.</small>
              {fMobilePhones.map((phone, idx) => (
                <div key={idx} className="fb-phone-entry">
                  <select
                    value={phone.type}
                    onChange={e => handleFMobileChange(idx, 'type', e.target.value)}
                    className="fb-input fb-phone-type-select"
                  >
                    {getMobileTypeOptions(fMobilePhones, idx).map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                    {!getMobileTypeOptions(fMobilePhones, idx).includes(phone.type) && (
                      <option value={phone.type}>{phone.type}</option>
                    )}
                  </select>
                  <PhoneInput
                    value={phone.number}
                    onChange={v => handleFMobileChange(idx, 'number', v)}
                    defaultCountry={form.address_country || owner?.address_country}
                    className="fb-phone-number-input"
                    inputClassName="fb-input"
                  />
                  {fMobilePhones.length > 1 && (
                    <button type="button" className="fb-btn-icon fb-btn-danger" onClick={() => removeFMobile(idx)} title="Remove">
                      <FiX size={16} />
                    </button>
                  )}
                </div>
              ))}
              {fMobilePhones.length < MAX_PHONES_PER_SECTION && (
                <button type="button" className="fb-btn-add-phone" onClick={addFMobile}>
                  <FiPlus size={14} /> Add another mobile
                </button>
              )}
            </fieldset>

            {/* ---- Other Phones (dynamic, same as About Yourself) ---- */}
            <fieldset className="fb-fieldset">
              <legend>Other Phone(s)</legend>
              <small className="fb-field-hint">Home Phone verified via phone call. No mobiles here — use mobile section above.</small>
              {fLandlinePhones.map((phone, idx) => (
                <div key={idx} className="fb-phone-entry">
                  <select
                    value={phone.type}
                    onChange={e => handleFLandlineChange(idx, 'type', e.target.value)}
                    className="fb-input fb-phone-type-select"
                  >
                    {getLandlineTypeOptions(fLandlinePhones, idx).map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                    {!getLandlineTypeOptions(fLandlinePhones, idx).includes(phone.type) && (
                      <option value={phone.type}>{phone.type}</option>
                    )}
                  </select>
                  <PhoneInput
                    value={phone.number}
                    onChange={v => handleFLandlineChange(idx, 'number', v)}
                    defaultCountry={form.address_country || owner?.address_country}
                    className="fb-phone-number-input"
                    inputClassName="fb-input"
                  />
                  {fLandlinePhones.length > 1 && (
                    <button type="button" className="fb-btn-icon fb-btn-danger" onClick={() => removeFLandline(idx)} title="Remove">
                      <FiX size={16} />
                    </button>
                  )}
                </div>
              ))}
              {fLandlinePhones.length < MAX_PHONES_PER_SECTION && (
                <button type="button" className="fb-btn-add-phone" onClick={addFLandline}>
                  <FiPlus size={14} /> Add another phone
                </button>
              )}
            </fieldset>
            {fPhoneError && !fPhoneError.startsWith('__owner_phone__') && <p className="fb-phone-error">{fPhoneError}</p>}
            {fPhoneError && fPhoneError.startsWith('__owner_phone__') && (
              <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '8px 12px', marginTop: 6 }}>
                <span style={{ color: '#e65100', fontWeight: 500 }}>This is your personal phone number.</span>{' '}
                <span>Use it for this family member too?</span>
                <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                  <button type="button" className="fb-btn-verify" style={{ fontSize: 13, padding: '4px 14px' }}
                    onClick={() => { const num = fPhoneError.split(':')[1]; setFOwnerPhoneConfirmed(prev => new Set(prev).add(num)); setFPhoneError(''); }}>
                    Yes, use it
                  </button>
                  <button type="button" style={{ fontSize: 13, padding: '4px 14px', background: '#eee', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                    onClick={() => { const num = fPhoneError.split(':')[1]; const allPhones = [...fMobilePhones, ...fLandlinePhones]; const idx = allPhones.findIndex(p => formatPhone(p.number) === num); if (idx < fMobilePhones.length) { const updated = [...fMobilePhones]; updated[idx] = { ...updated[idx], number: '' }; setFMobilePhones(updated); } else { const li = idx - fMobilePhones.length; const updated = [...fLandlinePhones]; updated[li] = { ...updated[li], number: '' }; setFLandlinePhones(updated); } setFPhoneError(''); }}>
                    No, I'll enter a different one
                  </button>
                </div>
              </div>
            )}

            {/* ---- User Name ---- */}
            <fieldset className="fb-fieldset">
              <legend>User Name</legend>
              <input
                type="text"
                placeholder="firstname.lastname (e.g. john.smith)"
                value={form.user_name}
                onChange={e => handleChange('user_name', e.target.value)}
                className="fb-input fb-input-full"
              />
              <small className="fb-field-hint">Format: 4+ letters/numbers, a dot, then 4+ letters/numbers. Auto-generated if left blank.</small>
              {formErrors.user_name && <span className="fb-field-error">{formErrors.user_name}</span>}
            </fieldset>

            {/* ---- Email with verification ---- */}
            <fieldset className="fb-fieldset">
              <legend>Email</legend>
              <div className="fb-email-row">
                <input
                  type="email"
                  placeholder="user@domain.com (minimum: u@d.c)"
                  value={form.email}
                  onChange={e => {
                    handleChange('email', e.target.value);
                    setFEmailError('');
                    setFOwnerEmailConfirmed(false);
                    if (fEmailVerifyState === 'verified') setFEmailVerifyState('idle');
                  }}
                  className={`fb-input fb-email-input ${formErrors.email && formErrors.email !== '__owner_email__' ? 'fb-input-error' : ''}`}
                />
                {fEmailVerifyState === 'verified' ? (
                  <span className="fb-verified-badge"><FiCheck size={14} /> Verified</span>
                ) : (
                  <button type="button" className="fb-btn-verify" onClick={handleFSendVerification}>
                    Verify Email
                  </button>
                )}
              </div>
              {fEmailError && <span className="fb-field-error">{fEmailError}</span>}
              {formErrors.email && formErrors.email !== '__owner_email__' && <span className="fb-field-error">{formErrors.email}</span>}
              {formErrors.email === '__owner_email__' && (
                <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '8px 12px', marginTop: 6 }}>
                  <span style={{ color: '#e65100', fontWeight: 500 }}>This is your personal email.</span>{' '}
                  <span>Use it for this family member too?</span>
                  <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                    <button type="button" className="fb-btn-verify" style={{ fontSize: 13, padding: '4px 14px' }}
                      onClick={() => { setFOwnerEmailConfirmed(true); setFormErrors(prev => { const n = { ...prev }; delete n.email; return n; }); }}>
                      Yes, use it
                    </button>
                    <button type="button" style={{ fontSize: 13, padding: '4px 14px', background: '#eee', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                      onClick={() => { handleChange('email', ''); setFOwnerEmailConfirmed(false); setFormErrors(prev => { const n = { ...prev }; delete n.email; return n; }); }}>
                      No, I'll enter a different one
                    </button>
                  </div>
                </div>
              )}
              <small className="fb-field-hint">They'll use this email to log in. We'll send a verification link.</small>

              {fEmailVerifyState === 'sent' && (
                <div className="fb-verify-code-section">
                  <p className="fb-verify-instruction">
                    A verification link has been sent to <strong>{form.email}</strong>.
                    Click the link to see a 6-digit code, then enter it below:
                  </p>
                  <div className="fb-verify-code-row">
                    <input
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={fVerifyCode}
                      onChange={e => { setFVerifyCode(e.target.value); setFVerifyError(''); }}
                      maxLength={6}
                      className="fb-input fb-verify-code-input"
                    />
                    <button type="button" className="fb-btn-verify" onClick={handleFVerifyCode}>
                      Confirm Code
                    </button>
                  </div>
                  {fVerifyError && <span className="fb-field-error">{fVerifyError}</span>}
                  <small className="fb-field-hint" style={{ color: '#1a7f37', fontWeight: 600 }}>(Dev mode — your code is: {fGeneratedCode})</small>
                </div>
              )}
            </fieldset>

            {/* ---- Address with verification ---- */}
            <fieldset className="fb-fieldset fb-address-fieldset">
              <legend>Address</legend>
              <small className="fb-field-hint fb-address-hint">
                Pre-filled from account owner's address. Change if different.
              </small>
              <div className="fb-address-stack">
                <select
                  value={form.address_country}
                  onChange={e => { handleChange('address_country', e.target.value); setFPostalError(''); }}
                  className="fb-input fb-input-full"
                >
                  <option value="">Select Country</option>
                  {countries.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <input type="text" placeholder="Street Address"
                  value={form.address_street}
                  onChange={e => handleChange('address_street', e.target.value)}
                  className="fb-input fb-input-full" />
                <input type="text" placeholder="City / Town / Village"
                  value={form.address_city}
                  onChange={e => handleChange('address_city', e.target.value)}
                  className="fb-input fb-input-full" />
                <input type="text" placeholder="State / Province"
                  value={form.address_state}
                  onChange={e => handleChange('address_state', e.target.value)}
                  className="fb-input fb-input-full" />
                <div className="fb-postal-row">
                  <input type="text" placeholder="Zip Code / Postal Code"
                    value={form.address_postal_code}
                    onChange={e => handleFPostalChange(e.target.value)}
                    className="fb-input fb-postal-input" />
                  {fPostalError && <span className="fb-field-error fb-postal-error">{fPostalError}</span>}
                </div>
              </div>
              {/* Address verification button */}
              <div className="fb-address-verify-row">
                {fAddressVerified ? (
                  <span className="fb-verified-badge"><FiCheck size={14} /> Address Verified</span>
                ) : (
                  <button
                    type="button"
                    className="fb-btn-verify"
                    onClick={handleFVerifyAddress}
                    disabled={fAddressVerifying || !form.address_street || !form.address_city || !form.address_country}
                  >
                    {fAddressVerifying ? 'Verifying...' : 'Verify Address'}
                  </button>
                )}
                <small className="fb-field-hint">Uses Google Places API (or equivalent) based on country.</small>
              </div>
            </fieldset>

            {/* ---- Mailing Address ---- */}
            <fieldset className="fb-fieldset fb-address-fieldset fb-mailing-fieldset">
              <legend>Mailing Address</legend>
              <div className="fb-mailing-checkboxes">
                <label className="fb-checkbox-label">
                  <input
                    type="checkbox"
                    checked={fMailSameAsPhysical}
                    onChange={e => handleFMailSameToggle(e.target.checked)}
                  />
                  Same as Physical
                </label>
                <label className="fb-checkbox-label">
                  <input
                    type="checkbox"
                    checked={fMailAddNew}
                    onChange={e => handleFMailAddNewToggle(e.target.checked)}
                  />
                  Add New
                </label>
              </div>
              {fMailSameAsPhysical && (
                <small className="fb-field-hint fb-mail-same-hint">
                  Mailing address will use the physical address above.
                </small>
              )}
              {fMailAddNew && !fMailSameAsPhysical && (
                <div className="fb-address-stack">
                  <select
                    value={form.mail_address_country}
                    onChange={e => { handleChange('mail_address_country', e.target.value); setFMailPostalError(''); }}
                    className="fb-input fb-input-full"
                  >
                    <option value="">Select Country</option>
                    {countries.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <input type="text" placeholder="Street Address"
                    value={form.mail_address_street}
                    onChange={e => handleChange('mail_address_street', e.target.value)}
                    className="fb-input fb-input-full" />
                  <input type="text" placeholder="City / Town / Village"
                    value={form.mail_address_city}
                    onChange={e => handleChange('mail_address_city', e.target.value)}
                    className="fb-input fb-input-full" />
                  <input type="text" placeholder="State / Province"
                    value={form.mail_address_state}
                    onChange={e => handleChange('mail_address_state', e.target.value)}
                    className="fb-input fb-input-full" />
                  <div className="fb-postal-row">
                    <input type="text" placeholder="Zip Code / Postal Code"
                      value={form.mail_address_postal_code}
                      onChange={e => handleFMailPostalChange(e.target.value)}
                      className="fb-input fb-postal-input" />
                    {fMailPostalError && <span className="fb-field-error fb-postal-error">{fMailPostalError}</span>}
                  </div>
                </div>
              )}
            </fieldset>

            {/* ---- Form Actions ---- */}
            <div className="fb-form-actions">
              <button type="submit" className="fb-btn-submit">
                <FiCheck size={16} /> {editingId ? 'Update' : 'Add Member'}
              </button>
              <button type="button" className="fb-btn-cancel" onClick={closeForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* ============ Family Members List ============ */}
      {members.length === 0 && !showForm ? (
        <div className="fb-family-empty">
          <FiUsers size={40} />
          <p>No family members added yet.</p>
          <button className="fb-btn-add" onClick={openAddForm}>
            <FiPlus size={16} /> Add Your First Family Member
          </button>
        </div>
      ) : members.length > 0 && (
        <div className="fb-family-list fb-family-grid">
          {/* First tile: add a member */}
          {!showForm && (
            <button type="button" className="fb-family-add-tile" onClick={openAddForm}>
              <FiPlus size={20} />
              <span>Add Family Member</span>
            </button>
          )}
          {sortFamilyMembers(members).map(m => (
            <div key={m.user_id} className="fb-family-card">
              <div className="fb-family-card-clickable" onClick={() => openEditForm(m)}>
                <div className="fb-family-card-avatar">
                  {(m.first_name?.[0] || '?')}{(m.last_name?.[0] || '')}
                </div>
                <div className="fb-family-card-info">
                  <div className="fb-family-card-name">
                    {m.first_name} {m.middle_name ? m.middle_name + ' ' : ''}{m.last_name}
                    {m.nickname && <span className="fb-family-card-nickname">({m.nickname})</span>}
                  </div>
                  <div className="fb-family-card-relationship">{m.relationship}</div>
                  <div className="fb-family-card-details">
                    <span>{m.email}</span>
                    {m.email_verified === 1 && <span className="fb-family-verified-tag">Email verified</span>}
                    {m.mobile_number && <span>{m.mobile_number}</span>}
                  </div>
                  <div className="fb-family-card-meta">
                    {m.address_city && <span>{m.address_city}{m.address_state ? `, ${m.address_state}` : ''}</span>}
                    {m.address_verified === 1 && <span className="fb-family-verified-tag">Addr verified</span>}
                    <span className="fb-family-scope-tag" title="Data access scope">
                      {m.data_access_scope === 'all' ? 'Full access' : 'Self only'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="fb-family-card-actions">
                <button className="fb-btn-icon" onClick={() => openEditForm(m)} title="Edit">
                  <FiEdit2 size={16} />
                </button>
                <button className="fb-btn-icon fb-btn-danger" onClick={() => handleRemove(m.user_id)} title="Remove">
                  <FiTrash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================
// Your Business(es) Tab
// Businesses are stored in a separate `user_businesses` table
// owner_id FK → users.user_id of the account owner
// Follows same validation rules as AboutYourselfTab:
//   - Email format + verification flow (6-digit code)
//   - Phone format validation
//   - Address with country, postal code validation, verify address
//   - Acronym unique within account owner's portal
//   - Mailing address with Same as Physical / Add New
// =============================================

const BUSINESS_TYPES = ['Limited company', 'Partnership', 'Sole Proprietorship', 'Trust'];

interface BusinessFormFields {
  busi_legal_name: string;
  busi_operating_name: string;
  busi_display_name: string;
  busi_acronym: string;
  busi_type: string;
  busi_registration_number: string;
  busi_phone: string;
  busi_email: string;
  base_currency: string;   // ISO 4217; '' = follow the country
  // Physical address
  busi_country_code: string;
  busi_address_1: string;
  busi_address_2: string;
  busi_address_country: string;
  busi_address_state: string;
  busi_address_postal_code: string;
  busi_address_city: string;
  busi_address_county: string;
  // Mailing address
  busi_mail_address_1: string;
  busi_mail_address_2: string;
  busi_mail_address_country: string;
  busi_mail_address_state: string;
  busi_mail_address_postal_code: string;
  busi_mail_address_city: string;
  busi_mail_address_county: string;
}

const EMPTY_BUSINESS_FORM: BusinessFormFields = {
  busi_legal_name: '', busi_operating_name: '', busi_display_name: '',
  busi_acronym: '', busi_type: '', busi_registration_number: '',
  busi_phone: '', busi_email: '', base_currency: '',
  busi_country_code: '', busi_address_1: '', busi_address_2: '',
  busi_address_country: '', busi_address_state: '', busi_address_postal_code: '',
  busi_address_city: '', busi_address_county: '',
  busi_mail_address_1: '', busi_mail_address_2: '',
  busi_mail_address_country: '', busi_mail_address_state: '', busi_mail_address_postal_code: '',
  busi_mail_address_city: '', busi_mail_address_county: '',
};

// ---------------------------------------------
// Locations / Units / Staff (nested form model)
// A business -> locations (branches w/ address) -> units (departments) -> staff.
// Persisted to user_business_location / user_business_unit / user_business_unit_staff.
// ---------------------------------------------
interface StaffRow {
  unit_staff_id?: number | string;
  staff_user_id: string;   // '' = free-text name (no linked account person)
  staff_name: string;
  staff_role: string;
}
interface UnitRow {
  unit_id?: number | string;
  unit_name: string;
  unit_code: string;
  tracks_own_profitability: boolean;
  staff: StaffRow[];
}
interface LocationRow {
  location_id?: number | string;
  loc_name: string;
  loc_code: string;
  loc_phone: string;
  loc_email: string;
  loc_address_1: string;
  loc_address_2: string;
  loc_country: string;
  loc_state: string;
  loc_postal_code: string;
  loc_city: string;
  loc_county: string;
  tracks_own_profitability: boolean;
  has_multiple_units: boolean;
  units: UnitRow[];
}
const newStaffRow = (): StaffRow => ({ staff_user_id: '', staff_name: '', staff_role: '' });
const newUnitRow = (): UnitRow => ({ unit_name: '', unit_code: '', tracks_own_profitability: false, staff: [] });
const newLocationRow = (defaultCountry = ''): LocationRow => ({
  loc_name: '', loc_code: '', loc_phone: '', loc_email: '',
  loc_address_1: '', loc_address_2: '', loc_country: defaultCountry, loc_state: '',
  loc_postal_code: '', loc_city: '', loc_county: '',
  tracks_own_profitability: false, has_multiple_units: false, units: [],
});

// ---------------------------------------------
// Employees (business-scoped roster; migration 102)
// Persisted to user_business_employee + user_business_employee_location (multi-
// location assignment). Nickname is the privacy-safe display name for scheduling
// and future staff messaging. Name / Position / Email are mandatory.
// ---------------------------------------------
const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Casual', 'Contractor', 'Seasonal'];
const EMP_STATUSES = [{ v: 'active', l: 'Active' }, { v: 'on_leave', l: 'On leave' }, { v: 'inactive', l: 'Inactive' }];
const PAY_TYPES = [{ v: 'hourly', l: 'Hourly' }, { v: 'salary', l: 'Salary' }, { v: 'commission', l: 'Commission' }, { v: 'piece', l: 'Piece rate' }];
const PAY_PERIODS = [{ v: 'per_hour', l: 'per hour' }, { v: 'per_year', l: 'per year' }, { v: 'per_month', l: 'per month' }, { v: 'per_piece', l: 'per piece' }];
const DEFAULT_PAY_PERIOD: Record<string, string> = { hourly: 'per_hour', salary: 'per_year', commission: 'per_month', piece: 'per_piece' };

interface EmployeeRow {
  employee_id?: number | string;
  emp_name: string;
  emp_nickname: string;
  emp_code: string;
  position: string;
  email: string;
  phone: string;
  employment_type: string;
  status: string;
  pay_type: string;
  pay_rate: string;
  pay_period: string;
  pay_currency: string;
  hire_date: string;
  end_date: string;
  emp_address_1: string;
  emp_address_2: string;
  emp_country: string;
  emp_state: string;
  emp_postal_code: string;
  emp_city: string;
  emp_county: string;
  notes: string;
  assignments: string[];                                   // ['loc:1','unit:5'] (multi)
  _origAssignments: { id: number | string; value: string }[]; // original link rows for diffing
}
const newEmployeeRow = (defaultCountry = '', currency = 'CAD'): EmployeeRow => ({
  emp_name: '', emp_nickname: '', emp_code: '', position: '', email: '', phone: '',
  employment_type: '', status: 'active', pay_type: '', pay_rate: '', pay_period: '', pay_currency: currency,
  hire_date: '', end_date: '',
  emp_address_1: '', emp_address_2: '', emp_country: defaultCountry, emp_state: '',
  emp_postal_code: '', emp_city: '', emp_county: '',
  notes: '', assignments: [], _origAssignments: [],
});

/** Get all businesses under the account owner (excludes client businesses) */
function getOwnerBusinesses(ownerId: number | string, businessesData?: Record<string, any>[]): Record<string, any>[] {
  const businesses = businessesData || getTableData('user_businesses');
  // eslint-disable-next-line eqeqeq
  return businesses.filter(b => b.owner_id == ownerId && b.is_deleted != 1 && b.is_client_business != 1);
}

/** Check if acronym is unique within account owner's portal (across users AND businesses) */
function isAcronymUnique(ownerId: number | string, acronym: string, excludeBusinessId?: number | string, usersData?: Record<string, any>[], businessesData?: Record<string, any>[]): boolean {
  if (!acronym) return true;
  const upper = acronym.toUpperCase();
  // Check users table (nicknames)
  const users = usersData || getTableData('users');
  const userConflict = users.some(u => {
    // eslint-disable-next-line eqeqeq
    const sameAccount = u.owner_id == ownerId || u.user_id == ownerId;
    return sameAccount && (u.nickname || '').toUpperCase() === upper && u.is_deleted !== 1;
  });
  if (userConflict) return false;
  // Check businesses table
  const businesses = businessesData || getTableData('user_businesses');
  return !businesses.some(b => {
    // eslint-disable-next-line eqeqeq
    const sameOwner = b.owner_id == ownerId;
    // eslint-disable-next-line eqeqeq
    const notSelf = excludeBusinessId ? b.business_id != excludeBusinessId : true;
    return sameOwner && notSelf && (b.busi_acronym || '').toUpperCase() === upper && b.is_deleted !== 1;
  });
}

/** Check if business email is unique globally (across users and businesses) */
function isBusinessEmailUnique(email: string, excludeBusinessId?: number | string, usersData?: Record<string, any>[], businessesData?: Record<string, any>[], accountId?: number | string | null): boolean {
  if (!email) return true;
  const lower = email.toLowerCase();
  // Same account (accountId): family members and sibling businesses may share an email.
  // eslint-disable-next-line eqeqeq
  const sameAccount = (row: Record<string, any>) => accountId != null && row.owner_id == accountId;
  // Check users table
  const users = usersData || getTableData('users');
  if (users.some(u => !sameAccount(u) && (u.email || '').toLowerCase() === lower && u.is_deleted !== 1)) return false;
  // Check businesses table
  const businesses = businessesData || getTableData('user_businesses');
  return !businesses.some(b => {
    // eslint-disable-next-line eqeqeq
    const notSelf = excludeBusinessId ? b.business_id != excludeBusinessId : true;
    return notSelf && !sameAccount(b) && (b.busi_email || '').toLowerCase() === lower && b.is_deleted !== 1;
  });
}

function isBusinessEmailMatchesOwner(email: string, ownerId: number | string, usersData?: Record<string, any>[]): boolean {
  if (!email) return false;
  const lower = email.toLowerCase();
  const users = usersData || getTableData('users');
  // eslint-disable-next-line eqeqeq
  const owner = users.find(u => u.user_id == ownerId && u.is_deleted !== 1);
  return owner ? (owner.email || '').toLowerCase() === lower : false;
}

/** Check if business phone conflicts with owner's phones */
function isBusinessPhoneUnique(phone: string, ownerId: number | string, excludeBusinessId?: number | string, usersData?: Record<string, any>[], businessesData?: Record<string, any>[]): boolean {
  if (!phone) return true;
  const clean = phone.replace(/[^+\d]/g, '');
  if (!clean) return true;
  // Check owner's personal phones
  const users = usersData || getTableData('users');
  // eslint-disable-next-line eqeqeq
  const owner = users.find(u => u.user_id == ownerId);
  if (owner) {
    const ownerPhones = [
      owner.mobile_number, owner.secondary_mobile,
      owner.home_phone, owner.business_phone,
    ].filter(Boolean).map((p: string) => p.replace(/[^+\d]/g, ''));
    const extras: { type: string; number: string }[] = Array.isArray(owner.extra_phones) ? owner.extra_phones : [];
    extras.forEach(p => { if (p.number) ownerPhones.push(p.number.replace(/[^+\d]/g, '')); });
    if (ownerPhones.includes(clean)) return false;
  }
  // Check other businesses' phones
  const businesses = businessesData || getTableData('user_businesses');
  return !businesses.some(b => {
    // eslint-disable-next-line eqeqeq
    const sameOwner = b.owner_id == ownerId;
    // eslint-disable-next-line eqeqeq
    const notSelf = excludeBusinessId ? b.business_id != excludeBusinessId : true;
    return sameOwner && notSelf && b.busi_phone &&
      b.busi_phone.replace(/[^+\d]/g, '') === clean && b.is_deleted !== 1;
  });
}

function isBusinessPhoneMatchesOwner(phone: string, ownerId: number | string, usersData?: Record<string, any>[]): boolean {
  if (!phone) return false;
  const clean = phone.replace(/[^+\d]/g, '');
  if (!clean) return false;
  const users = usersData || getTableData('users');
  // eslint-disable-next-line eqeqeq
  const owner = users.find(u => u.user_id == ownerId);
  if (!owner) return false;
  const ownerPhones = [
    owner.mobile_number, owner.secondary_mobile,
    owner.home_phone, owner.business_phone,
  ].filter(Boolean).map((p: string) => p.replace(/[^+\d]/g, ''));
  const extras: { type: string; number: string }[] = Array.isArray(owner.extra_phones) ? owner.extra_phones : [];
  extras.forEach(p => { if (p.number) ownerPhones.push(p.number.replace(/[^+\d]/g, '')); });
  return ownerPhones.includes(clean);
}

const YourBusinessesTab: React.FC = () => {
  const [owner, setOwner] = useState(() => getAccountOwner());
  const ownerId = owner?.user_id;
  const [allUsers, setAllUsers] = useState<Record<string, any>[]>(() => getTableData('users'));
  const [allBusinesses, setAllBusinesses] = useState<Record<string, any>[]>(() => getTableData('user_businesses'));
  const [countries, setCountries] = useState<string[]>(() => getCountries());
  const [businesses, setBusinesses] = useState(() => ownerId ? getOwnerBusinesses(ownerId) : []);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [form, setForm] = useState<BusinessFormFields>(EMPTY_BUSINESS_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [savedMsg, setSavedMsg] = useState('');
  const [selectedBizId, setSelectedBizId] = useState<string>('');

  // Load from PostgreSQL if available
  useEffect(() => {
    Promise.all([
      loadTable('users'),
      loadTable('user_businesses'),
      loadTable('site_mstr_countries'),
    ]).then(([usersRows, bizRows, countriesRows]) => {
      setAllUsers(usersRows);
      setAllBusinesses(bizRows);
      const o = getAccountOwner(usersRows);
      if (o) {
        setOwner(o);
        setBusinesses(getOwnerBusinesses(o.user_id, bizRows));
      }
      setCountries(getCountries(countriesRows));
    }).catch(() => {});
  }, []);

  // ---- Email verification state ----
  const [bEmailVerifyState, setBEmailVerifyState] = useState<'idle' | 'sent' | 'verified'>('idle');
  const [bVerifyCode, setBVerifyCode] = useState('');
  const [bGeneratedCode, setBGeneratedCode] = useState('');
  const [bVerifyError, setBVerifyError] = useState('');

  // ---- Owner-match confirmation for phone/email ----
  const [ownerPhoneConfirmed, setOwnerPhoneConfirmed] = useState(false);
  const [ownerEmailConfirmed, setOwnerEmailConfirmed] = useState(false);

  // ---- Address verification state ----
  const [bAddressVerified, setBAddressVerified] = useState(false);
  const [bAddressVerifying, setBAddressVerifying] = useState(false);
  const [bPostalError, setBPostalError] = useState('');

  // ---- Mailing address state ----
  const [bMailSameAsPhysical, setBMailSameAsPhysical] = useState(false);
  const [bMailAddNew, setBMailAddNew] = useState(false);
  const [bMailPostalError, setBMailPostalError] = useState('');

  // ---- Locations & Units state ----
  const [activeBizFormTab, setActiveBizFormTab] = useState<'details' | 'locations' | 'employees'>('details');
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  // Ids of persisted rows the user removed while the form is open (soft-deleted on save)
  const removedIdsRef = useRef<{ loc: Set<number | string>; unit: Set<number | string>; staff: Set<number | string>; emp: Set<number | string>; empLoc: Set<number | string> }>({
    loc: new Set(), unit: new Set(), staff: new Set(), emp: new Set(), empLoc: new Set(),
  });
  const resetRemovedIds = useCallback(() => {
    removedIdsRef.current = { loc: new Set(), unit: new Set(), staff: new Set(), emp: new Set(), empLoc: new Set() };
  }, []);

  // People in this account (owner + family members) for the staff picker
  const staffPeopleOptions = useMemo<DropdownOption[]>(() => {
    if (!ownerId) return [];
    // eslint-disable-next-line eqeqeq
    const people = allUsers.filter(u => (u.user_id == ownerId || u.owner_id == ownerId) && u.is_deleted != 1);
    return people.map(u => {
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
        || u.screen_name || u.email || `User ${u.user_id}`;
      // eslint-disable-next-line eqeqeq
      const label = u.user_id == ownerId ? `${name} (owner)` : name;
      return { value: String(u.user_id), label };
    });
  }, [allUsers, ownerId]);
  const personName = useCallback((userIdStr: string): string => {
    const opt = staffPeopleOptions.find(o => o.value === userIdStr);
    return opt ? opt.label.replace(/ \(owner\)$/, '') : '';
  }, [staffPeopleOptions]);

  // ---- Location handlers ----
  const addLocation = useCallback(() => {
    setFormDirty(true);
    setLocations(prev => [...prev, newLocationRow(form.busi_address_country || owner?.address_country || '')]);
  }, [form.busi_address_country, owner]);
  const updateLocation = useCallback((idx: number, patch: Partial<LocationRow>) => {
    setFormDirty(true);
    setLocations(prev => prev.map((loc, i) => i === idx ? { ...loc, ...patch } : loc));
  }, []);
  const removeLocation = useCallback((idx: number) => {
    setFormDirty(true);
    setLocations(prev => {
      const loc = prev[idx];
      if (loc?.location_id) removedIdsRef.current.loc.add(loc.location_id);
      (loc?.units || []).forEach(u => {
        if (u.unit_id) removedIdsRef.current.unit.add(u.unit_id);
        (u.staff || []).forEach(s => { if (s.unit_staff_id) removedIdsRef.current.staff.add(s.unit_staff_id); });
      });
      return prev.filter((_, i) => i !== idx);
    });
  }, []);
  // Toggling "has multiple units" off marks any saved units/staff for deletion.
  const toggleLocationUnits = useCallback((idx: number, checked: boolean) => {
    setFormDirty(true);
    setLocations(prev => prev.map((loc, i) => {
      if (i !== idx) return loc;
      if (!checked) {
        (loc.units || []).forEach(u => {
          if (u.unit_id) removedIdsRef.current.unit.add(u.unit_id);
          (u.staff || []).forEach(s => { if (s.unit_staff_id) removedIdsRef.current.staff.add(s.unit_staff_id); });
        });
        return { ...loc, has_multiple_units: false, units: [] };
      }
      return { ...loc, has_multiple_units: true, units: loc.units.length ? loc.units : [newUnitRow()] };
    }));
  }, []);

  // ---- Unit handlers ----
  const addUnit = useCallback((locIdx: number) => {
    setFormDirty(true);
    setLocations(prev => prev.map((loc, i) => i === locIdx ? { ...loc, units: [...loc.units, newUnitRow()] } : loc));
  }, []);
  const updateUnit = useCallback((locIdx: number, unitIdx: number, patch: Partial<UnitRow>) => {
    setFormDirty(true);
    setLocations(prev => prev.map((loc, i) => i === locIdx
      ? { ...loc, units: loc.units.map((u, j) => j === unitIdx ? { ...u, ...patch } : u) }
      : loc));
  }, []);
  const removeUnit = useCallback((locIdx: number, unitIdx: number) => {
    setFormDirty(true);
    setLocations(prev => prev.map((loc, i) => {
      if (i !== locIdx) return loc;
      const unit = loc.units[unitIdx];
      if (unit?.unit_id) removedIdsRef.current.unit.add(unit.unit_id);
      (unit?.staff || []).forEach(s => { if (s.unit_staff_id) removedIdsRef.current.staff.add(s.unit_staff_id); });
      return { ...loc, units: loc.units.filter((_, j) => j !== unitIdx) };
    }));
  }, []);

  // ---- Staff handlers ----
  const addStaff = useCallback((locIdx: number, unitIdx: number) => {
    setFormDirty(true);
    setLocations(prev => prev.map((loc, i) => i === locIdx
      ? { ...loc, units: loc.units.map((u, j) => j === unitIdx ? { ...u, staff: [...u.staff, newStaffRow()] } : u) }
      : loc));
  }, []);
  const updateStaff = useCallback((locIdx: number, unitIdx: number, staffIdx: number, patch: Partial<StaffRow>) => {
    setFormDirty(true);
    setLocations(prev => prev.map((loc, i) => i === locIdx
      ? { ...loc, units: loc.units.map((u, j) => j === unitIdx
          ? { ...u, staff: u.staff.map((s, k) => k === staffIdx ? { ...s, ...patch } : s) }
          : u) }
      : loc));
  }, []);
  const removeStaff = useCallback((locIdx: number, unitIdx: number, staffIdx: number) => {
    setFormDirty(true);
    setLocations(prev => prev.map((loc, i) => i === locIdx
      ? { ...loc, units: loc.units.map((u, j) => {
          if (j !== unitIdx) return u;
          const st = u.staff[staffIdx];
          if (st?.unit_staff_id) removedIdsRef.current.staff.add(st.unit_staff_id);
          return { ...u, staff: u.staff.filter((_, k) => k !== staffIdx) };
        }) }
      : loc));
  }, []);

  // Load a business's locations/units/staff into the nested form state
  const loadLocationsForBusiness = useCallback(async (businessId: number | string) => {
    try {
      const [locRows, unitRows, staffRows] = await Promise.all([
        loadTable('user_business_location'),
        loadTable('user_business_unit'),
        loadTable('user_business_unit_staff'),
      ]);
      // eslint-disable-next-line eqeqeq
      const locs = locRows.filter(l => l.business_id == businessId && l.is_deleted != 1);
      const nested: LocationRow[] = locs
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map(l => {
          // eslint-disable-next-line eqeqeq
          const units = unitRows.filter(u => u.location_id == l.location_id && u.is_deleted != 1)
            .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
            .map(u => ({
              unit_id: u.unit_id,
              unit_name: u.unit_name || '',
              unit_code: u.unit_code || '',
              tracks_own_profitability: u.tracks_own_profitability === 1,
              // eslint-disable-next-line eqeqeq
              staff: staffRows.filter(s => s.unit_id == u.unit_id && s.is_deleted != 1).map(s => ({
                unit_staff_id: s.unit_staff_id,
                staff_user_id: s.staff_user_id != null ? String(s.staff_user_id) : '',
                staff_name: s.staff_name || '',
                staff_role: s.staff_role || '',
              })),
            }));
          return {
            location_id: l.location_id,
            loc_name: l.loc_name || '', loc_code: l.loc_code || '',
            loc_phone: l.loc_phone || '', loc_email: l.loc_email || '',
            loc_address_1: l.loc_address_1 || '', loc_address_2: l.loc_address_2 || '',
            loc_country: l.loc_country || '', loc_state: l.loc_state || '',
            loc_postal_code: l.loc_postal_code || '', loc_city: l.loc_city || '',
            loc_county: l.loc_county || '',
            tracks_own_profitability: l.tracks_own_profitability === 1,
            has_multiple_units: l.has_multiple_units === 1 || units.length > 0,
            units,
          };
        });
      setLocations(nested);
    } catch {
      setLocations([]);
    }
  }, []);

  // Persist the nested locations/units/staff after the business row is saved.
  const persistLocations = useCallback(async (businessId: number | string) => {
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const removed = removedIdsRef.current;
    // Soft-delete removed rows (children first)
    for (const id of Array.from(removed.staff)) { try { await removeRow('user_business_unit_staff', id); } catch {} }
    for (const id of Array.from(removed.unit)) { try { await removeRow('user_business_unit', id); } catch {} }
    for (const id of Array.from(removed.loc)) { try { await removeRow('user_business_location', id); } catch {} }

    for (let li = 0; li < locations.length; li++) {
      const loc = locations[li];
      const locFields: Record<string, any> = {
        business_id: businessId,
        loc_name: loc.loc_name.trim() || null,
        loc_code: loc.loc_code.trim() || null,
        loc_phone: loc.loc_phone.trim() || null,
        loc_email: loc.loc_email.trim() || null,
        loc_address_1: loc.loc_address_1.trim() || null,
        loc_address_2: loc.loc_address_2.trim() || null,
        loc_country: loc.loc_country || null,
        loc_state: loc.loc_state.trim() || null,
        loc_postal_code: loc.loc_postal_code.trim() || null,
        loc_city: loc.loc_city.trim() || null,
        loc_county: loc.loc_county.trim() || null,
        tracks_own_profitability: loc.tracks_own_profitability ? 1 : 0,
        has_multiple_units: loc.has_multiple_units ? 1 : 0,
        display_order: li,
        updated_at: now,
      };
      let locId = loc.location_id;
      if (locId) {
        await saveRow('user_business_location', locId, locFields);
      } else {
        const ins = await addRow('user_business_location', { ...locFields, is_active: 1, is_deleted: 0, created_by: ownerId, created_at: now });
        locId = ins.location_id;
      }
      const units = loc.has_multiple_units ? loc.units : [];
      for (let ui = 0; ui < units.length; ui++) {
        const unit = units[ui];
        const unitFields: Record<string, any> = {
          location_id: locId,
          business_id: businessId,
          unit_name: unit.unit_name.trim() || null,
          unit_code: unit.unit_code.trim() || null,
          tracks_own_profitability: unit.tracks_own_profitability ? 1 : 0,
          display_order: ui,
          updated_at: now,
        };
        let unitId = unit.unit_id;
        if (unitId) {
          await saveRow('user_business_unit', unitId, unitFields);
        } else {
          const ins = await addRow('user_business_unit', { ...unitFields, is_active: 1, is_deleted: 0, created_by: ownerId, created_at: now });
          unitId = ins.unit_id;
        }
        for (const st of unit.staff) {
          // Skip empty staff rows (no person picked and no typed name)
          if (!st.staff_user_id && !st.staff_name.trim() && !st.staff_role.trim()) continue;
          const staffFields: Record<string, any> = {
            unit_id: unitId,
            location_id: locId,
            business_id: businessId,
            staff_user_id: st.staff_user_id ? Number(st.staff_user_id) : null,
            staff_name: (st.staff_user_id ? personName(st.staff_user_id) : st.staff_name.trim()) || null,
            staff_role: st.staff_role.trim() || null,
            updated_at: now,
          };
          if (st.unit_staff_id) {
            await saveRow('user_business_unit_staff', st.unit_staff_id, staffFields);
          } else {
            await addRow('user_business_unit_staff', { ...staffFields, is_active: 1, is_deleted: 0, created_by: ownerId, created_at: now });
          }
        }
      }
    }
  }, [locations, ownerId, personName]);

  // ---- Employee assignment options (this business's saved locations/units) ----
  const assignmentOptions = useMemo<DropdownOption[]>(() => {
    const opts: DropdownOption[] = [];
    locations.forEach(loc => {
      if (!loc.location_id) return;   // only persisted locations are assignable
      opts.push({ value: `loc:${loc.location_id}`, label: loc.loc_name || `Location ${loc.location_id}`, depth: 0 });
      (loc.units || []).forEach(u => {
        if (!u.unit_id) return;
        opts.push({ value: `unit:${u.unit_id}`, label: `${loc.loc_name || 'Location'} › ${u.unit_name || `Unit ${u.unit_id}`}`, depth: 1 });
      });
    });
    return opts;
  }, [locations]);
  const assignmentLabel = useCallback((value: string): string => {
    const o = assignmentOptions.find(x => x.value === value);
    return o ? o.label : value;
  }, [assignmentOptions]);
  const decodeAssignment = useCallback((value: string): { location_id: number | null; unit_id: number | null } => {
    if (value.startsWith('unit:')) {
      const unit_id = Number(value.slice(5));
      // find parent location from the loaded nested locations
      let location_id: number | null = null;
      for (const loc of locations) {
        // eslint-disable-next-line eqeqeq
        if ((loc.units || []).some(u => u.unit_id == unit_id)) { location_id = Number(loc.location_id); break; }
      }
      return { location_id, unit_id };
    }
    if (value.startsWith('loc:')) return { location_id: Number(value.slice(4)), unit_id: null };
    return { location_id: null, unit_id: null };
  }, [locations]);

  // ---- Employee handlers (all mark the form dirty) ----
  const addEmployee = useCallback(() => {
    setFormDirty(true);
    setEmployees(prev => [...prev, newEmployeeRow(form.busi_address_country || owner?.address_country || '', form.busi_country_code ? 'CAD' : 'CAD')]);
  }, [form.busi_address_country, form.busi_country_code, owner]);
  const updateEmployee = useCallback((idx: number, patch: Partial<EmployeeRow>) => {
    setFormDirty(true);
    setEmployees(prev => prev.map((e, i) => {
      if (i !== idx) return e;
      const next = { ...e, ...patch };
      // Auto-fill pay period from pay type when the period is still blank.
      if (patch.pay_type && !e.pay_period) next.pay_period = DEFAULT_PAY_PERIOD[patch.pay_type] || '';
      return next;
    }));
  }, []);
  const removeEmployee = useCallback((idx: number) => {
    setFormDirty(true);
    setEmployees(prev => {
      const emp = prev[idx];
      if (emp?.employee_id) removedIdsRef.current.emp.add(emp.employee_id);
      (emp?._origAssignments || []).forEach(a => removedIdsRef.current.empLoc.add(a.id));
      return prev.filter((_, i) => i !== idx);
    });
  }, []);
  const toggleEmployeeAssignment = useCallback((idx: number, value: string) => {
    setFormDirty(true);
    setEmployees(prev => prev.map((e, i) => {
      if (i !== idx) return e;
      const has = e.assignments.includes(value);
      return { ...e, assignments: has ? e.assignments.filter(v => v !== value) : [...e.assignments, value] };
    }));
  }, []);

  // Load a business's employees + assignments into the nested form state.
  const loadEmployeesForBusiness = useCallback(async (businessId: number | string) => {
    try {
      const [empRows, empLocRows] = await Promise.all([
        loadTable('user_business_employee'),
        loadTable('user_business_employee_location'),
      ]);
      // eslint-disable-next-line eqeqeq
      const emps = empRows.filter(e => e.business_id == businessId && e.is_deleted != 1);
      const nested: EmployeeRow[] = emps
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map(e => {
          // eslint-disable-next-line eqeqeq
          const links = empLocRows.filter(l => l.employee_id == e.employee_id && l.is_deleted != 1);
          const orig = links.map(l => ({
            id: l.emp_location_id,
            value: l.unit_id != null ? `unit:${l.unit_id}` : (l.location_id != null ? `loc:${l.location_id}` : ''),
          })).filter(a => a.value);
          return {
            employee_id: e.employee_id,
            emp_name: e.emp_name || '', emp_nickname: e.emp_nickname || '', emp_code: e.emp_code || '',
            position: e.position || '', email: e.email || '', phone: e.phone || '',
            employment_type: e.employment_type || '', status: e.status || 'active',
            pay_type: e.pay_type || '', pay_rate: e.pay_rate != null ? String(e.pay_rate) : '',
            pay_period: e.pay_period || '', pay_currency: e.pay_currency || 'CAD',
            hire_date: (e.hire_date || '').slice(0, 10), end_date: (e.end_date || '').slice(0, 10),
            emp_address_1: e.emp_address_1 || '', emp_address_2: e.emp_address_2 || '',
            emp_country: e.emp_country || '', emp_state: e.emp_state || '',
            emp_postal_code: e.emp_postal_code || '', emp_city: e.emp_city || '', emp_county: e.emp_county || '',
            notes: e.notes || '',
            assignments: orig.map(a => a.value),
            _origAssignments: orig,
          };
        });
      setEmployees(nested);
    } catch {
      setEmployees([]);
    }
  }, []);

  // Persist employees + assignment links after the business row is saved.
  const persistEmployees = useCallback(async (businessId: number | string) => {
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const removed = removedIdsRef.current;
    for (const id of Array.from(removed.empLoc)) { try { await removeRow('user_business_employee_location', id); } catch {} }
    for (const id of Array.from(removed.emp)) { try { await removeRow('user_business_employee', id); } catch {} }

    for (let ei = 0; ei < employees.length; ei++) {
      const emp = employees[ei];
      const empFields: Record<string, any> = {
        business_id: businessId,
        emp_name: emp.emp_name.trim(),
        emp_nickname: emp.emp_nickname.trim() || null,
        emp_code: emp.emp_code.trim() || null,
        position: emp.position.trim(),
        email: emp.email.trim(),
        phone: emp.phone.trim() || null,
        employment_type: emp.employment_type || null,
        status: emp.status || 'active',
        pay_type: emp.pay_type || null,
        pay_rate: emp.pay_rate.trim() ? Number(emp.pay_rate) : null,
        pay_period: emp.pay_period || null,
        pay_currency: emp.pay_currency || null,
        hire_date: emp.hire_date || null,
        end_date: emp.end_date || null,
        emp_address_1: emp.emp_address_1.trim() || null,
        emp_address_2: emp.emp_address_2.trim() || null,
        emp_country: emp.emp_country || null,
        emp_state: emp.emp_state.trim() || null,
        emp_postal_code: emp.emp_postal_code.trim() || null,
        emp_city: emp.emp_city.trim() || null,
        emp_county: emp.emp_county.trim() || null,
        notes: emp.notes.trim() || null,
        display_order: ei,
        updated_at: now,
      };
      let empId = emp.employee_id;
      if (empId) {
        await saveRow('user_business_employee', empId, empFields);
      } else {
        const ins = await addRow('user_business_employee', { ...empFields, is_active: 1, is_deleted: 0, created_by: ownerId, created_at: now });
        empId = ins.employee_id;
      }
      // Diff assignments: add new values, soft-delete removed originals.
      const origByValue = new Map(emp._origAssignments.map(a => [a.value, a.id]));
      const currentSet = new Set(emp.assignments);
      // removed originals
      for (const a of emp._origAssignments) {
        if (!currentSet.has(a.value)) { try { await removeRow('user_business_employee_location', a.id); } catch {} }
      }
      // new assignments
      for (const value of emp.assignments) {
        if (origByValue.has(value)) continue;
        const { location_id, unit_id } = decodeAssignment(value);
        await addRow('user_business_employee_location', {
          employee_id: empId, business_id: businessId, location_id, unit_id,
          is_active: 1, is_deleted: 0, created_by: ownerId, created_at: now, updated_at: now,
        });
      }
    }
  }, [employees, ownerId, decodeAssignment]);

  const refreshBusinesses = useCallback(async () => {
    if (!ownerId) return;
    try {
      const bizRows = await loadTable('user_businesses');
      setAllBusinesses(bizRows);
      setBusinesses(getOwnerBusinesses(ownerId, bizRows));
      // Also refresh users for validation
      const usersRows = await loadTable('users');
      setAllUsers(usersRows);
    } catch {
      setBusinesses(getOwnerBusinesses(ownerId));
    }
  }, [ownerId]);

  const handleChange = useCallback((field: keyof BusinessFormFields, value: string) => {
    setFormDirty(true);
    setForm(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => ({ ...prev, [field]: '' }));
    // Reset address verified on address change
    if (field.startsWith('busi_address_')) setBAddressVerified(false);
  }, []);

  const formatPhone = (raw: string): string => {
    if (!raw) return '';
    return raw.replace(/[^+\d]/g, '');
  };

  // ---- Email verification ----
  const handleBSendVerification = useCallback(() => {
    if (!isValidEmail(form.busi_email)) {
      setFormErrors(prev => ({ ...prev, busi_email: 'Please enter a valid email (e.g. info@business.com)' }));
      return;
    }
    setFormErrors(prev => ({ ...prev, busi_email: '' }));
    const code = String(Math.floor(100000 + Math.random() * 900000));
    setBGeneratedCode(code);
    setBEmailVerifyState('sent');
    setBVerifyError('');
    console.log('[BK] Business email verification code (mock):', code, 'to:', form.busi_email);
  }, [form.busi_email]);

  const handleBVerifyCode = useCallback(() => {
    if (bVerifyCode.trim() === bGeneratedCode) {
      setBEmailVerifyState('verified');
      setBVerifyError('');
      console.log('[BK] Business email verified successfully');
    } else {
      setBVerifyError('Invalid code. Please check the code from the verification link.');
    }
  }, [bVerifyCode, bGeneratedCode]);

  // ---- Address verification (mock) ----
  const handleBVerifyAddress = useCallback(() => {
    setBAddressVerifying(true);
    setTimeout(() => {
      const hasAll = form.busi_address_1 && form.busi_address_city &&
        form.busi_address_state && form.busi_address_postal_code && form.busi_address_country;
      if (hasAll) {
        setBAddressVerified(true);
        console.log('[BK] Business address verified (mock)');
      }
      setBAddressVerifying(false);
    }, 1500);
  }, [form]);

  // ---- Postal code validation ----
  const handleBPostalChange = useCallback((value: string) => {
    handleChange('busi_address_postal_code', value);
    if (value && form.busi_address_country) {
      const { valid, hint } = isValidPostalCode(value, form.busi_address_country);
      setBPostalError(valid ? '' : hint);
    } else {
      setBPostalError('');
    }
  }, [handleChange, form.busi_address_country]);

  // ---- Mailing address handlers ----
  const handleBMailPostalChange = useCallback((value: string) => {
    handleChange('busi_mail_address_postal_code', value);
    if (value && form.busi_mail_address_country) {
      const { valid, hint } = isValidPostalCode(value, form.busi_mail_address_country);
      setBMailPostalError(valid ? '' : hint);
    } else {
      setBMailPostalError('');
    }
  }, [handleChange, form.busi_mail_address_country]);

  const handleBMailSameToggle = useCallback((checked: boolean) => {
    setBMailSameAsPhysical(checked);
    if (checked) {
      setBMailAddNew(false);
      setForm(prev => ({
        ...prev,
        busi_mail_address_1: prev.busi_address_1,
        busi_mail_address_2: prev.busi_address_2,
        busi_mail_address_country: prev.busi_address_country,
        busi_mail_address_state: prev.busi_address_state,
        busi_mail_address_postal_code: prev.busi_address_postal_code,
        busi_mail_address_city: prev.busi_address_city,
        busi_mail_address_county: prev.busi_address_county,
      }));
      setBMailPostalError('');
    }
  }, []);

  const handleBMailAddNewToggle = useCallback((checked: boolean) => {
    setBMailAddNew(checked);
    if (checked) {
      setBMailSameAsPhysical(false);
      setForm(prev => ({
        ...prev,
        busi_mail_address_1: '', busi_mail_address_2: '',
        busi_mail_address_country: '', busi_mail_address_state: '',
        busi_mail_address_postal_code: '', busi_mail_address_city: '',
        busi_mail_address_county: '',
      }));
      setBMailPostalError('');
    }
  }, []);

  // ---- Open / Close form ----
  const resetFormState = useCallback(() => {
    setFormErrors({});
    setBEmailVerifyState('idle');
    setBVerifyCode('');
    setBVerifyError('');
    setBAddressVerified(false);
    setBPostalError('');
    setBMailSameAsPhysical(false);
    setBMailAddNew(false);
    setBMailPostalError('');
    setOwnerPhoneConfirmed(false);
    setOwnerEmailConfirmed(false);
  }, []);

  const openAddForm = useCallback(() => {
    // Pre-fill country from owner
    setForm({
      ...EMPTY_BUSINESS_FORM,
      busi_address_country: owner?.address_country || '',
      busi_country_code: owner?.country_code || '',
    });
    setEditingId(null);
    resetFormState();
    setActiveBizFormTab('details');
    setLocations([]);
    setEmployees([]);
    resetRemovedIds();
    setShowForm(true);
  }, [owner, resetFormState, resetRemovedIds]);

  const openEditForm = useCallback((biz: Record<string, any>) => {
    setForm({
      busi_legal_name: biz.busi_legal_name || '',
      busi_operating_name: biz.busi_operating_name || '',
      busi_display_name: biz.busi_display_name || '',
      busi_acronym: biz.busi_acronym || '',
      busi_type: biz.busi_type || '',
      busi_registration_number: biz.busi_registration_number || '',
      busi_phone: biz.busi_phone || '',
      busi_email: biz.busi_email || '',
      base_currency: String(biz.base_currency || '').trim().toUpperCase(),
      busi_country_code: biz.busi_country_code || '',
      busi_address_1: biz.busi_address_1 || '',
      busi_address_2: biz.busi_address_2 || '',
      busi_address_country: biz.busi_address_country || '',
      busi_address_state: biz.busi_address_state || '',
      busi_address_postal_code: biz.busi_address_postal_code || '',
      busi_address_city: biz.busi_address_city || '',
      busi_address_county: biz.busi_address_county || '',
      busi_mail_address_1: biz.busi_mail_address_1 || '',
      busi_mail_address_2: biz.busi_mail_address_2 || '',
      busi_mail_address_country: biz.busi_mail_address_country || '',
      busi_mail_address_state: biz.busi_mail_address_state || '',
      busi_mail_address_postal_code: biz.busi_mail_address_postal_code || '',
      busi_mail_address_city: biz.busi_mail_address_city || '',
      busi_mail_address_county: biz.busi_mail_address_county || '',
    });
    setEditingId(biz.business_id);
    resetFormState();
    setBEmailVerifyState(biz.busi_email_verified === 1 ? 'verified' : 'idle');
    setBAddressVerified(biz.busi_address_verified === 1);
    setBMailSameAsPhysical(biz.busi_mail_same_as_physical === 1);
    setBMailAddNew(!!(biz.busi_mail_address_1 || biz.busi_mail_address_city) && biz.busi_mail_same_as_physical !== 1);
    setActiveBizFormTab('details');
    resetRemovedIds();
    setLocations([]);
    setEmployees([]);
    loadLocationsForBusiness(biz.business_id);
    loadEmployeesForBusiness(biz.business_id);
    setShowForm(true);
  }, [resetFormState, resetRemovedIds, loadLocationsForBusiness, loadEmployeesForBusiness]);

  const closeForm = useCallback(() => {
    setFormDirty(false);
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_BUSINESS_FORM);
    resetFormState();
    setActiveBizFormTab('details');
    setLocations([]);
    setEmployees([]);
    resetRemovedIds();
  }, [resetFormState, resetRemovedIds]);

  // ---- Select existing business to edit ----
  const handleSelectBusiness = useCallback((bizIdStr: string) => {
    setSelectedBizId(bizIdStr);
    if (bizIdStr === '__add_new__') {
      openAddForm();
    } else if (bizIdStr) {
      const biz = businesses.find(b => String(b.business_id) === bizIdStr);
      if (biz) openEditForm(biz);
    } else {
      closeForm();
    }
  }, [businesses, openAddForm, openEditForm, closeForm]);

  // ---- Submit ----
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ownerId || !owner) return;

    const errors: Record<string, string> = {};
    if (!form.busi_legal_name.trim()) errors.busi_legal_name = 'Legal name is required';

    // Email format validation (optional but validated if provided)
    if (form.busi_email.trim() && !isValidEmail(form.busi_email.trim())) {
      errors.busi_email = 'Invalid email format (minimum: u@d.c)';
    } else if (form.busi_email.trim() && !isBusinessEmailUnique(form.busi_email.trim(), editingId ?? undefined, allUsers, allBusinesses, ownerId)) {
      if (isBusinessEmailMatchesOwner(form.busi_email.trim(), ownerId, allUsers) && !ownerEmailConfirmed) {
        errors.busi_email = '__owner_email__';
      } else if (!isBusinessEmailMatchesOwner(form.busi_email.trim(), ownerId, allUsers)) {
        errors.busi_email = 'This email is already in use';
      }
    }

    // Phone uniqueness (optional but validated if provided)
    if (form.busi_phone.trim() && !isBusinessPhoneUnique(form.busi_phone.trim(), ownerId, editingId ?? undefined, allUsers, allBusinesses)) {
      if (isBusinessPhoneMatchesOwner(form.busi_phone.trim(), ownerId, allUsers) && !ownerPhoneConfirmed) {
        errors.busi_phone = '__owner_phone__';
      } else if (!isBusinessPhoneMatchesOwner(form.busi_phone.trim(), ownerId, allUsers)) {
        errors.busi_phone = 'This phone number is already in use';
      }
    }

    // Acronym uniqueness within account
    if (form.busi_acronym.trim() && !isAcronymUnique(ownerId, form.busi_acronym.trim(), editingId ?? undefined, allUsers, allBusinesses)) {
      errors.busi_acronym = 'This acronym is already used in your account';
    }

    // Physical address — all optional

    // Postal code validation
    if (form.busi_address_postal_code && form.busi_address_country) {
      const { valid, hint } = isValidPostalCode(form.busi_address_postal_code, form.busi_address_country);
      if (!valid) {
        setBPostalError(hint);
        errors.busi_address_postal_code = hint;
      }
    }

    // Mailing postal code validation
    if (bMailAddNew && !bMailSameAsPhysical && form.busi_mail_address_postal_code && form.busi_mail_address_country) {
      const { valid, hint } = isValidPostalCode(form.busi_mail_address_postal_code, form.busi_mail_address_country);
      if (!valid) {
        setBMailPostalError(hint);
        errors.busi_mail_address_postal_code = hint;
      }
    }

    // Employee validation — Name, Position, Email are mandatory (+ email format).
    for (let ei = 0; ei < employees.length; ei++) {
      const emp = employees[ei];
      let empErr = '';
      if (!emp.emp_name.trim()) empErr = `Employee ${ei + 1}: name is required.`;
      else if (!emp.position.trim()) empErr = `Employee "${emp.emp_name.trim()}": position is required.`;
      else if (!emp.email.trim()) empErr = `Employee "${emp.emp_name.trim()}": email is required.`;
      else if (!isValidEmail(emp.email.trim())) empErr = `Employee "${emp.emp_name.trim()}": invalid email format.`;
      if (empErr) {
        setActiveBizFormTab('employees');
        setSavedMsg(empErr);
        setTimeout(() => setSavedMsg(''), 4000);
        return;
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);

    const mailFields = {
      busi_mail_same_as_physical: bMailSameAsPhysical ? 1 : 0,
      busi_mail_address_1: bMailSameAsPhysical ? (form.busi_address_1 || null) : (form.busi_mail_address_1 || null),
      busi_mail_address_2: bMailSameAsPhysical ? (form.busi_address_2 || null) : (form.busi_mail_address_2 || null),
      busi_mail_address_country: bMailSameAsPhysical ? (form.busi_address_country || null) : (form.busi_mail_address_country || null),
      busi_mail_address_state: bMailSameAsPhysical ? (form.busi_address_state || null) : (form.busi_mail_address_state || null),
      busi_mail_address_postal_code: bMailSameAsPhysical ? (form.busi_address_postal_code || null) : (form.busi_mail_address_postal_code || null),
      busi_mail_address_city: bMailSameAsPhysical ? (form.busi_address_city || null) : (form.busi_mail_address_city || null),
      busi_mail_address_county: bMailSameAsPhysical ? (form.busi_address_county || null) : (form.busi_mail_address_county || null),
    };

    const commonFields: Record<string, any> = {
      busi_legal_name: form.busi_legal_name.trim(),
      busi_operating_name: form.busi_operating_name.trim() || null,
      busi_display_name: form.busi_display_name.trim() || null,
      busi_acronym: form.busi_acronym.trim() || null,
      busi_type: form.busi_type || null,
      busi_registration_number: form.busi_registration_number.trim() || null,
      base_currency: form.base_currency || null,
      busi_phone: formatPhone(form.busi_phone) || null,
      busi_email: form.busi_email.trim() || null,
      busi_email_verified: bEmailVerifyState === 'verified' ? 1 : 0,
      busi_country_code: form.busi_country_code || null,
      busi_address_1: form.busi_address_1.trim() || null,
      busi_address_2: form.busi_address_2.trim() || null,
      busi_address_country: form.busi_address_country || null,
      busi_address_state: form.busi_address_state.trim() || null,
      busi_address_postal_code: form.busi_address_postal_code.trim() || null,
      busi_address_city: form.busi_address_city.trim() || null,
      busi_address_county: form.busi_address_county.trim() || null,
      busi_address_verified: bAddressVerified ? 1 : 0,
      ...mailFields,
    };

    let savedBusinessId: number | string | null = editingId;
    try {
      if (editingId) {
        // Capture the prior province BEFORE saving so we can record a move (sales
        // tax follows the business across provinces by document date).
        const prevBiz = allBusinesses.find((b: any) => b.business_id == editingId);
        const oldState = String(prevBiz?.busi_address_state || '').trim().toUpperCase();
        const newState = String(form.busi_address_state || '').trim().toUpperCase();
        const prevSince = String(prevBiz?.created_at || '').slice(0, 10) || '2000-01-01';
        await saveRow('user_businesses', editingId, { ...commonFields, updated_at: now });
        console.log('[BK] Updated business business_id:', editingId);
        if (oldState && newState && oldState !== newState && form.busi_address_country) {
          checkBusinessMove(Number(editingId), oldState, newState, String(form.busi_address_country), prevSince);
        }
      } else {
        const newRow: Record<string, any> = {
          ...commonFields,
          owner_id: ownerId,
          is_active: 0, is_deleted: 0,
          created_at: now, updated_at: now,
        };
        const inserted = await addRow('user_businesses', newRow);
        savedBusinessId = inserted.business_id;
        console.log('[BK] Added business business_id:', inserted.business_id || 'new', 'under owner_id:', ownerId);
      }
      // Persist locations / units / staff + employees under the (new or existing) business id
      if (savedBusinessId) { await persistLocations(savedBusinessId); await persistEmployees(savedBusinessId); }
    } catch (err: any) {
      setSavedMsg(err.message || 'Save failed');
      setTimeout(() => setSavedMsg(''), 3000);
      return;
    }

    await refreshBusinesses();
    closeForm();
    setSelectedBizId('');
    setSavedMsg(editingId ? 'Business updated!' : 'Business added!');
    setTimeout(() => setSavedMsg(''), 3000);
  }, [form, ownerId, owner, editingId, bEmailVerifyState, bAddressVerified,
      bMailSameAsPhysical, bMailAddNew, ownerPhoneConfirmed, ownerEmailConfirmed, allUsers, allBusinesses, refreshBusinesses, closeForm, persistLocations, persistEmployees, employees]);

  // Business province-move capture (sales tax follows the business by date).
  const { checkBusinessMove, moveModal } = useBusinessMoveCapture(Number(ownerId) || null, (m) => { setSavedMsg(m); setTimeout(() => setSavedMsg(''), 4000); });

  const handleRemove = useCallback(async (bizId: number | string) => {
    try {
      await removeRow('user_businesses', bizId);
      console.log('[BK] Soft-deleted business business_id:', bizId);
    } catch (err: any) {
      setSavedMsg(err.message || 'Remove failed');
      setTimeout(() => setSavedMsg(''), 3000);
      return;
    }
    await refreshBusinesses();
    if (String(editingId) === String(bizId)) closeForm();
    setSelectedBizId('');
    setSavedMsg('Business removed.');
    setTimeout(() => setSavedMsg(''), 3000);
  }, [refreshBusinesses, editingId, closeForm]);

  // "Save & continue" for this form (modal).
  useEffect(() => {
    registerSaveHandler(submitSaver(handleSubmit));
    return () => registerSaveHandler(null);
  }, [handleSubmit]);

  if (!owner || !ownerId) {
    return <div className="fb-no-owner">No account owner found. Please set up your account first.</div>;
  }

  return (
    <div className="fb-family-container">
      {moveModal}

      {/* Info banner */}
      <div className="fb-info-banner">
        <strong>Register your businesses here.</strong>
        <br />
        Business income and expenses will be tracked separately per business.
        <br />
        <em className="fb-info-perm">
          <strong>Permissions:</strong> Account Owner can add, edit, or delete any business.
        </em>
      </div>

      {/* Business count + Add button */}
      <div className="fb-family-header">
        <span><FiUsers size={16} /> {businesses.length} business{businesses.length !== 1 ? 'es' : ''}</span>
        {/* With businesses listed, the first tile of the grid is the Add button. */}
        {!showForm && businesses.length === 0 && (
          <button className="fb-btn-add" onClick={openAddForm}>
            <FiPlus size={16} /> Add Business
          </button>
        )}
      </div>
      {savedMsg && <div className="fb-saved-msg fb-saved-msg-family">{savedMsg}</div>}

      {/* ============ Business Selector ============ */}
      {(businesses.length > 0 || showForm) && (
        <fieldset className="fb-fieldset">
          <legend>Your Businesses</legend>
          <select
            value={selectedBizId}
            onChange={e => handleSelectBusiness(e.target.value)}
            className="fb-input fb-input-full"
          >
            <option value="">Select a Business</option>
            {businesses.map(b => (
              <option key={b.business_id} value={String(b.business_id)}>
                {b.busi_legal_name}{b.busi_acronym ? ` (${b.busi_acronym})` : ''}
              </option>
            ))}
            <option value="__add_new__">+ Add New Business</option>
          </select>
        </fieldset>
      )}

      {/* ============ Business Form ============ */}
      {showForm && (
        <div className="fb-family-form-wrapper">
          <div className="fb-family-form-header">
            <h3>{editingId ? 'Edit Business' : 'Add Business'}</h3>
            <button className="fb-btn-close" onClick={closeForm}><FiX size={18} /></button>
          </div>
          <form onSubmit={handleSubmit} className="fb-family-form" noValidate>

            {/* ---- In-form tabs: Business Details | Locations & Units ---- */}
            <div className="fb-biz-form-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={activeBizFormTab === 'details'}
                className={`fb-biz-form-tab ${activeBizFormTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveBizFormTab('details')}>
                <FiBriefcase size={15} /> Business Details
              </button>
              <button type="button" role="tab" aria-selected={activeBizFormTab === 'locations'}
                className={`fb-biz-form-tab ${activeBizFormTab === 'locations' ? 'active' : ''}`}
                onClick={() => setActiveBizFormTab('locations')}>
                <FiMapPin size={15} /> Locations &amp; Units
                {locations.length > 0 && <span className="fb-biz-form-tab-count">{locations.length}</span>}
              </button>
              <button type="button" role="tab" aria-selected={activeBizFormTab === 'employees'}
                className={`fb-biz-form-tab ${activeBizFormTab === 'employees' ? 'active' : ''}`}
                onClick={() => setActiveBizFormTab('employees')}>
                <FiUsers size={15} /> Employees
                {employees.length > 0 && <span className="fb-biz-form-tab-count">{employees.length}</span>}
              </button>
            </div>

            <div style={{ display: activeBizFormTab === 'details' ? 'block' : 'none' }}>

            {/* Legal Name */}
            <fieldset className="fb-fieldset">
              <legend>Legal Name of Business</legend>
              <input type="text" placeholder="Full Legal Name *"
                value={form.busi_legal_name}
                onChange={e => handleChange('busi_legal_name', e.target.value)}
                required
                className={`fb-input fb-input-full ${formErrors.busi_legal_name ? 'fb-input-error' : ''}`} />
              {formErrors.busi_legal_name && <span className="fb-field-error">{formErrors.busi_legal_name}</span>}
            </fieldset>

            {/* Operating As Name + Display Name */}
            <div className="fb-name-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <fieldset className="fb-fieldset">
                <legend>Operating as Name</legend>
                <input type="text" placeholder="Do you have a Operating As name"
                  value={form.busi_operating_name}
                  onChange={e => handleChange('busi_operating_name', e.target.value)}
                  className="fb-input fb-input-full" />
              </fieldset>
              <fieldset className="fb-fieldset">
                <legend>Display name in Portal</legend>
                <input type="text" placeholder="How you want to display business name in this portal"
                  value={form.busi_display_name}
                  onChange={e => handleChange('busi_display_name', e.target.value)}
                  className="fb-input fb-input-full" />
              </fieldset>
            </div>

            {/* Acronym */}
            <fieldset className="fb-fieldset">
              <legend>Acronym for Business Name</legend>
              <input type="text" placeholder="Do you want to use a acronym for business name"
                value={form.busi_acronym}
                onChange={e => handleChange('busi_acronym', e.target.value)}
                className={`fb-input fb-input-full ${formErrors.busi_acronym ? 'fb-input-error' : ''}`} />
              {formErrors.busi_acronym && <span className="fb-field-error">{formErrors.busi_acronym}</span>}
              <small className="fb-field-hint">
                Must be unique within your account. E.g. "MBN" for "My Business Name".
              </small>
            </fieldset>

            {/* Type */}
            <fieldset className="fb-fieldset">
              <legend>Type</legend>
              <select
                value={form.busi_type}
                onChange={e => handleChange('busi_type', e.target.value)}
                className="fb-input fb-input-full"
              >
                <option value="">Select Type</option>
                {BUSINESS_TYPES.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </fieldset>

            {/* Registration Number */}
            <fieldset className="fb-fieldset">
              <legend>Business Registration Number</legend>
              <input type="text" placeholder="Business Registration Number"
                value={form.busi_registration_number}
                onChange={e => handleChange('busi_registration_number', e.target.value)}
                className="fb-input fb-input-full" />
            </fieldset>

            <BaseCurrencyField
              value={form.base_currency}
              country={form.busi_address_country}
              ownerId={ownerId}
              users={allUsers}
              onChange={v => handleChange('base_currency', v)}
            />

            {/* Business Phone */}
            <fieldset className="fb-fieldset">
              <legend>Business Phone</legend>
              <PhoneInput
                value={form.busi_phone}
                onChange={v => { handleChange('busi_phone', v); setOwnerPhoneConfirmed(false); }}
                defaultCountry={form.busi_address_country || owner?.address_country}
                hasError={!!(formErrors.busi_phone && formErrors.busi_phone !== '__owner_phone__')}
                inputClassName={`fb-input ${formErrors.busi_phone && formErrors.busi_phone !== '__owner_phone__' ? 'fb-input-error' : ''}`} />
              {formErrors.busi_phone && formErrors.busi_phone !== '__owner_phone__' && (
                <span className="fb-field-error">{formErrors.busi_phone}</span>
              )}
              {formErrors.busi_phone === '__owner_phone__' && (
                <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '8px 12px', marginTop: 6 }}>
                  <span style={{ color: '#e65100', fontWeight: 500 }}>This is your personal phone number.</span>{' '}
                  <span>Use it as your business phone too?</span>
                  <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                    <button type="button" className="fb-btn-verify" style={{ fontSize: 13, padding: '4px 14px' }}
                      onClick={() => { setOwnerPhoneConfirmed(true); setFormErrors(prev => { const n = { ...prev }; delete n.busi_phone; return n; }); }}>
                      Yes, use it
                    </button>
                    <button type="button" style={{ fontSize: 13, padding: '4px 14px', background: '#eee', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                      onClick={() => { handleChange('busi_phone', ''); setFormErrors(prev => { const n = { ...prev }; delete n.busi_phone; return n; }); }}>
                      No, I'll enter a different one
                    </button>
                  </div>
                </div>
              )}
              <small className="fb-field-hint">Pick the country code, then enter the number.</small>
            </fieldset>

            {/* Business Email with verification */}
            <fieldset className="fb-fieldset">
              <legend>Business Email</legend>
              <div className="fb-email-row">
                <input type="email" placeholder="Business Email"
                  value={form.busi_email}
                  onChange={e => {
                    handleChange('busi_email', e.target.value);
                    setOwnerEmailConfirmed(false);
                    if (bEmailVerifyState !== 'idle') setBEmailVerifyState('idle');
                  }}
                  className={`fb-input fb-email-input ${formErrors.busi_email && formErrors.busi_email !== '__owner_email__' ? 'fb-input-error' : ''}`} />
                {bEmailVerifyState === 'verified' ? (
                  <span className="fb-verified-badge"><FiCheck size={14} /> Verified</span>
                ) : bEmailVerifyState === 'sent' ? null : (
                  <button type="button" className="fb-btn-verify" onClick={handleBSendVerification}
                    disabled={!form.busi_email.trim()}>
                    Verify Email
                  </button>
                )}
              </div>
              {formErrors.busi_email && formErrors.busi_email !== '__owner_email__' && (
                <span className="fb-field-error">{formErrors.busi_email}</span>
              )}
              {formErrors.busi_email === '__owner_email__' && (
                <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '8px 12px', marginTop: 6 }}>
                  <span style={{ color: '#e65100', fontWeight: 500 }}>This is your personal email.</span>{' '}
                  <span>Use it as your business email too?</span>
                  <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                    <button type="button" className="fb-btn-verify" style={{ fontSize: 13, padding: '4px 14px' }}
                      onClick={() => { setOwnerEmailConfirmed(true); setFormErrors(prev => { const n = { ...prev }; delete n.busi_email; return n; }); }}>
                      Yes, use it
                    </button>
                    <button type="button" style={{ fontSize: 13, padding: '4px 14px', background: '#eee', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                      onClick={() => { handleChange('busi_email', ''); setOwnerEmailConfirmed(false); setFormErrors(prev => { const n = { ...prev }; delete n.busi_email; return n; }); }}>
                      No, I'll enter a different one
                    </button>
                  </div>
                </div>
              )}
              <small className="fb-field-hint">
                We'll send a link to this email. Click the link to see a code, then enter it below.
              </small>
              {bEmailVerifyState === 'sent' && (
                <div className="fb-verify-code-row">
                  <input type="text" placeholder="Enter 6-digit code"
                    value={bVerifyCode}
                    onChange={e => setBVerifyCode(e.target.value)}
                    maxLength={6}
                    className="fb-input fb-verify-input" />
                  <button type="button" className="fb-btn-verify" onClick={handleBVerifyCode}>
                    Confirm Code
                  </button>
                  {bVerifyError && <span className="fb-field-error">{bVerifyError}</span>}
                  <small className="fb-field-hint" style={{ color: '#1a7f37', fontWeight: 600 }}>(Dev mode — your code is: {bGeneratedCode})</small>
                </div>
              )}
            </fieldset>

            {/* Physical Address */}
            <fieldset className="fb-fieldset fb-address-fieldset">
              <legend>Physical Address</legend>
              <small className="fb-field-hint fb-address-hint">
                Address verified via Google Places (or equivalent by country).
              </small>
              <div className="fb-address-stack">
                <input type="text" placeholder="Address 1"
                  value={form.busi_address_1}
                  onChange={e => handleChange('busi_address_1', e.target.value)}
                  className={`fb-input fb-input-full ${formErrors.busi_address_1 ? 'fb-input-error' : ''}`} />
                {formErrors.busi_address_1 && <span className="fb-field-error">{formErrors.busi_address_1}</span>}
                <input type="text" placeholder="Address 2"
                  value={form.busi_address_2}
                  onChange={e => handleChange('busi_address_2', e.target.value)}
                  className="fb-input fb-input-full" />
                <select
                  value={form.busi_address_country}
                  onChange={e => { handleChange('busi_address_country', e.target.value); setBPostalError(''); }}
                  className={`fb-input fb-input-full ${formErrors.busi_address_country ? 'fb-input-error' : ''}`}
                >
                  <option value="">Select Country</option>
                  {countries.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {formErrors.busi_address_country && <span className="fb-field-error">{formErrors.busi_address_country}</span>}
                <input type="text" placeholder="State / Province"
                  value={form.busi_address_state}
                  onChange={e => handleChange('busi_address_state', e.target.value)}
                  className={`fb-input fb-input-full ${formErrors.busi_address_state ? 'fb-input-error' : ''}`} />
                {formErrors.busi_address_state && <span className="fb-field-error">{formErrors.busi_address_state}</span>}
                <div className="fb-postal-row">
                  <input type="text" placeholder="Zip Code / Pincode / Postal Code"
                    value={form.busi_address_postal_code}
                    onChange={e => handleBPostalChange(e.target.value)}
                    className={`fb-input fb-postal-input ${formErrors.busi_address_postal_code ? 'fb-input-error' : ''}`} />
                  {bPostalError && <span className="fb-field-error fb-postal-error">{bPostalError}</span>}
                </div>
                <input type="text" placeholder="City / Town / Village"
                  value={form.busi_address_city}
                  onChange={e => handleChange('busi_address_city', e.target.value)}
                  className={`fb-input fb-input-full ${formErrors.busi_address_city ? 'fb-input-error' : ''}`} />
                {formErrors.busi_address_city && <span className="fb-field-error">{formErrors.busi_address_city}</span>}
                <input type="text" placeholder="County / District"
                  value={form.busi_address_county}
                  onChange={e => handleChange('busi_address_county', e.target.value)}
                  className="fb-input fb-input-full" />
              </div>
              {/* Address verification button */}
              <div className="fb-address-verify-row">
                {bAddressVerified ? (
                  <span className="fb-verified-badge"><FiCheck size={14} /> Address Verified</span>
                ) : (
                  <button type="button" className="fb-btn-verify" onClick={handleBVerifyAddress}
                    disabled={bAddressVerifying || !form.busi_address_1 || !form.busi_address_city || !form.busi_address_country}>
                    {bAddressVerifying ? 'Verifying...' : 'Verify Address'}
                  </button>
                )}
                <small className="fb-field-hint">Uses Google Places API (or equivalent) based on country.</small>
              </div>
            </fieldset>

            {/* Mailing Address */}
            <fieldset className="fb-fieldset fb-address-fieldset fb-mailing-fieldset">
              <legend>Mailing Address</legend>
              <div className="fb-mailing-checkboxes">
                <label className="fb-checkbox-label">
                  <input type="checkbox" checked={bMailSameAsPhysical}
                    onChange={e => handleBMailSameToggle(e.target.checked)} />
                  Same as Physical
                </label>
                <label className="fb-checkbox-label">
                  <input type="checkbox" checked={bMailAddNew}
                    onChange={e => handleBMailAddNewToggle(e.target.checked)} />
                  Add New
                </label>
              </div>
              {bMailSameAsPhysical && (
                <small className="fb-field-hint fb-mail-same-hint">
                  Mailing address will use the physical address above.
                </small>
              )}
              {bMailAddNew && !bMailSameAsPhysical && (
                <div className="fb-address-stack">
                  <input type="text" placeholder="Address 1"
                    value={form.busi_mail_address_1}
                    onChange={e => handleChange('busi_mail_address_1', e.target.value)}
                    className="fb-input fb-input-full" />
                  <input type="text" placeholder="Address 2"
                    value={form.busi_mail_address_2}
                    onChange={e => handleChange('busi_mail_address_2', e.target.value)}
                    className="fb-input fb-input-full" />
                  <select
                    value={form.busi_mail_address_country}
                    onChange={e => { handleChange('busi_mail_address_country', e.target.value); setBMailPostalError(''); }}
                    className="fb-input fb-input-full"
                  >
                    <option value="">Select Country</option>
                    {countries.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <input type="text" placeholder="State / Province"
                    value={form.busi_mail_address_state}
                    onChange={e => handleChange('busi_mail_address_state', e.target.value)}
                    className="fb-input fb-input-full" />
                  <div className="fb-postal-row">
                    <input type="text" placeholder="Zip Code / Postal Code"
                      value={form.busi_mail_address_postal_code}
                      onChange={e => handleBMailPostalChange(e.target.value)}
                      className="fb-input fb-postal-input" />
                    {bMailPostalError && <span className="fb-field-error fb-postal-error">{bMailPostalError}</span>}
                  </div>
                  <input type="text" placeholder="City / Town / Village"
                    value={form.busi_mail_address_city}
                    onChange={e => handleChange('busi_mail_address_city', e.target.value)}
                    className="fb-input fb-input-full" />
                  <input type="text" placeholder="County / District"
                    value={form.busi_mail_address_county}
                    onChange={e => handleChange('busi_mail_address_county', e.target.value)}
                    className="fb-input fb-input-full" />
                </div>
              )}
            </fieldset>

            </div>{/* end Business Details tab */}

            {/* ---- Locations & Units tab ---- */}
            <div style={{ display: activeBizFormTab === 'locations' ? 'block' : 'none' }}>
              <div className="fb-info-banner fb-loc-intro">
                <strong>Locations &amp; Units.</strong> Add each place this business operates from.
                A location can contain multiple units (departments/sections), and each unit can have
                its own staff. Mark whether a location or unit tracks its own profitability.
              </div>

              {locations.length === 0 && (
                <div className="fb-loc-empty">
                  <FiMapPin size={30} />
                  <p>No locations added yet.</p>
                </div>
              )}

              {locations.map((loc, li) => (
                <fieldset className="fb-fieldset fb-loc-card" key={li}>
                  <legend>
                    <FiMapPin size={13} /> {loc.loc_name.trim() || `Location ${li + 1}`}
                  </legend>
                  <button type="button" className="fb-loc-remove" title="Remove location"
                    onClick={() => removeLocation(li)}><FiTrash2 size={15} /></button>

                  <div className="fb-name-row" style={{ gridTemplateColumns: '2fr 1fr' }}>
                    <input type="text" placeholder="Location name (e.g. Downtown Branch)"
                      value={loc.loc_name}
                      onChange={e => updateLocation(li, { loc_name: e.target.value })}
                      className="fb-input fb-input-full" />
                    <input type="text" placeholder="Code (optional)"
                      value={loc.loc_code}
                      onChange={e => updateLocation(li, { loc_code: e.target.value })}
                      className="fb-input fb-input-full" />
                  </div>

                  <div className="fb-name-row" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 8 }}>
                    <input type="text" placeholder="Phone (optional)"
                      value={loc.loc_phone}
                      onChange={e => updateLocation(li, { loc_phone: e.target.value })}
                      className="fb-input fb-input-full" />
                    <input type="email" placeholder="Email (optional)"
                      value={loc.loc_email}
                      onChange={e => updateLocation(li, { loc_email: e.target.value })}
                      className="fb-input fb-input-full" />
                  </div>

                  {/* Location address */}
                  <div className="fb-address-stack" style={{ marginTop: 8 }}>
                    <input type="text" placeholder="Address 1"
                      value={loc.loc_address_1}
                      onChange={e => updateLocation(li, { loc_address_1: e.target.value })}
                      className="fb-input fb-input-full" />
                    <input type="text" placeholder="Address 2"
                      value={loc.loc_address_2}
                      onChange={e => updateLocation(li, { loc_address_2: e.target.value })}
                      className="fb-input fb-input-full" />
                    <select value={loc.loc_country}
                      onChange={e => updateLocation(li, { loc_country: e.target.value })}
                      className="fb-input fb-input-full">
                      <option value="">Select Country</option>
                      {countries.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input type="text" placeholder="State / Province"
                      value={loc.loc_state}
                      onChange={e => updateLocation(li, { loc_state: e.target.value })}
                      className="fb-input fb-input-full" />
                    <input type="text" placeholder="Zip Code / Pincode / Postal Code"
                      value={loc.loc_postal_code}
                      onChange={e => updateLocation(li, { loc_postal_code: e.target.value })}
                      className="fb-input fb-input-full" />
                    <input type="text" placeholder="City / Town / Village"
                      value={loc.loc_city}
                      onChange={e => updateLocation(li, { loc_city: e.target.value })}
                      className="fb-input fb-input-full" />
                    <input type="text" placeholder="County / District"
                      value={loc.loc_county}
                      onChange={e => updateLocation(li, { loc_county: e.target.value })}
                      className="fb-input fb-input-full" />
                  </div>

                  <div className="fb-loc-flags">
                    <label className="fb-checkbox-label">
                      <input type="checkbox" checked={loc.tracks_own_profitability}
                        onChange={e => updateLocation(li, { tracks_own_profitability: e.target.checked })} />
                      This location tracks its own profitability
                    </label>
                    <label className="fb-checkbox-label">
                      <input type="checkbox" checked={loc.has_multiple_units}
                        onChange={e => toggleLocationUnits(li, e.target.checked)} />
                      This location has multiple units
                    </label>
                  </div>

                  {/* Units within this location */}
                  {loc.has_multiple_units && (
                    <div className="fb-unit-wrap">
                      {loc.units.map((unit, ui) => (
                        <div className="fb-unit-card" key={ui}>
                          <div className="fb-unit-head">
                            <span className="fb-unit-title"><FiBriefcase size={12} /> {unit.unit_name.trim() || `Unit ${ui + 1}`}</span>
                            <button type="button" className="fb-loc-remove fb-unit-remove" title="Remove unit"
                              onClick={() => removeUnit(li, ui)}><FiTrash2 size={13} /></button>
                          </div>
                          <div className="fb-name-row" style={{ gridTemplateColumns: '2fr 1fr' }}>
                            <input type="text" placeholder="Unit name (e.g. Kitchen, Sales)"
                              value={unit.unit_name}
                              onChange={e => updateUnit(li, ui, { unit_name: e.target.value })}
                              className="fb-input fb-input-full" />
                            <input type="text" placeholder="Code"
                              value={unit.unit_code}
                              onChange={e => updateUnit(li, ui, { unit_code: e.target.value })}
                              className="fb-input fb-input-full" />
                          </div>
                          <label className="fb-checkbox-label" style={{ marginTop: 6 }}>
                            <input type="checkbox" checked={unit.tracks_own_profitability}
                              onChange={e => updateUnit(li, ui, { tracks_own_profitability: e.target.checked })} />
                            This unit tracks its own profitability
                          </label>

                          {/* Staff for this unit */}
                          <div className="fb-staff-wrap">
                            <div className="fb-staff-label"><FiUsers size={12} /> Staff</div>
                            {unit.staff.map((st, si) => (
                              <div className="fb-staff-row" key={si}>
                                <SearchableDropdown
                                  options={staffPeopleOptions}
                                  value={st.staff_user_id}
                                  onChange={v => updateStaff(li, ui, si, { staff_user_id: v, staff_name: v ? personName(v) : st.staff_name })}
                                  placeholder="Pick a person…"
                                  wrapperClassName="fb-staff-picker"
                                />
                                {!st.staff_user_id && (
                                  <input type="text" placeholder="…or type a name"
                                    value={st.staff_name}
                                    onChange={e => updateStaff(li, ui, si, { staff_name: e.target.value })}
                                    className="fb-input fb-staff-name" />
                                )}
                                <input type="text" placeholder="Role / title"
                                  value={st.staff_role}
                                  onChange={e => updateStaff(li, ui, si, { staff_role: e.target.value })}
                                  className="fb-input fb-staff-role" />
                                <button type="button" className="fb-loc-remove fb-staff-remove" title="Remove staff"
                                  onClick={() => removeStaff(li, ui, si)}><FiX size={14} /></button>
                              </div>
                            ))}
                            <button type="button" className="fb-btn-add-sub" onClick={() => addStaff(li, ui)}>
                              <FiPlus size={13} /> Add Staff
                            </button>
                          </div>
                        </div>
                      ))}
                      <button type="button" className="fb-btn-add-sub" onClick={() => addUnit(li)}>
                        <FiPlus size={14} /> Add Unit
                      </button>
                    </div>
                  )}
                </fieldset>
              ))}

              <button type="button" className="fb-btn-add fb-btn-add-loc" onClick={addLocation}>
                <FiPlus size={16} /> Add Location
              </button>
            </div>

            {/* ---- Employees tab ---- */}
            <div style={{ display: activeBizFormTab === 'employees' ? 'block' : 'none' }}>
              <div className="fb-info-banner fb-loc-intro">
                <strong>Employees.</strong> Your staff roster for this business — used for scheduling
                and future staff messaging. The <em>nickname</em> is the privacy-safe name shown in
                schedules/messages. <strong>Name, Position and Email are required</strong>; everything
                else is optional. Assign each person to one or more locations/units.
              </div>

              {employees.length === 0 && (
                <div className="fb-loc-empty">
                  <FiUsers size={30} />
                  <p>No employees added yet.</p>
                </div>
              )}

              {employees.map((emp, ei) => (
                <fieldset className="fb-fieldset fb-loc-card fb-emp-card" key={ei}>
                  <legend><FiUsers size={13} /> {emp.emp_nickname.trim() || emp.emp_name.trim() || `Employee ${ei + 1}`}</legend>
                  <button type="button" className="fb-loc-remove" title="Remove employee"
                    onClick={() => removeEmployee(ei)}><FiTrash2 size={15} /></button>

                  {/* Name + Nickname */}
                  <div className="fb-name-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <input type="text" placeholder="Full name *"
                      value={emp.emp_name}
                      onChange={e => updateEmployee(ei, { emp_name: e.target.value })}
                      className="fb-input fb-input-full" />
                    <input type="text" placeholder="Nickname (shown in schedule/messages)"
                      value={emp.emp_nickname}
                      onChange={e => updateEmployee(ei, { emp_nickname: e.target.value })}
                      className="fb-input fb-input-full" />
                  </div>

                  {/* Position + Code */}
                  <div className="fb-name-row" style={{ gridTemplateColumns: '2fr 1fr', marginTop: 8 }}>
                    <input type="text" placeholder="Position / job title *"
                      value={emp.position}
                      onChange={e => updateEmployee(ei, { position: e.target.value })}
                      className="fb-input fb-input-full" />
                    <input type="text" placeholder="Employee code (optional)"
                      value={emp.emp_code}
                      onChange={e => updateEmployee(ei, { emp_code: e.target.value })}
                      className="fb-input fb-input-full" />
                  </div>

                  {/* Email + Phone */}
                  <div className="fb-name-row" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 8 }}>
                    <input type="email" placeholder="Email *"
                      value={emp.email}
                      onChange={e => updateEmployee(ei, { email: e.target.value })}
                      className="fb-input fb-input-full" />
                    <input type="text" placeholder="Mobile phone"
                      value={emp.phone}
                      onChange={e => updateEmployee(ei, { phone: e.target.value })}
                      className="fb-input fb-input-full" />
                  </div>

                  {/* Employment type + Status */}
                  <div className="fb-name-row" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 8 }}>
                    <select value={emp.employment_type}
                      onChange={e => updateEmployee(ei, { employment_type: e.target.value })}
                      className="fb-input fb-input-full">
                      <option value="">Employment type…</option>
                      {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select value={emp.status}
                      onChange={e => updateEmployee(ei, { status: e.target.value })}
                      className="fb-input fb-input-full">
                      {EMP_STATUSES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                    </select>
                  </div>

                  {/* Pay: type + rate + period + currency */}
                  <div className="fb-emp-pay-row">
                    <select value={emp.pay_type}
                      onChange={e => updateEmployee(ei, { pay_type: e.target.value })}
                      className="fb-input">
                      <option value="">Pay type…</option>
                      {PAY_TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                    <input type="number" min="0" step="0.01" inputMode="decimal" placeholder="Rate"
                      value={emp.pay_rate}
                      onChange={e => updateEmployee(ei, { pay_rate: e.target.value })}
                      className="fb-input" />
                    <select value={emp.pay_period}
                      onChange={e => updateEmployee(ei, { pay_period: e.target.value })}
                      className="fb-input">
                      <option value="">Period…</option>
                      {PAY_PERIODS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
                    </select>
                    <input type="text" placeholder="Currency" maxLength={5}
                      value={emp.pay_currency}
                      onChange={e => updateEmployee(ei, { pay_currency: e.target.value })}
                      className="fb-input fb-emp-currency" />
                  </div>

                  {/* Hire + end dates */}
                  <div className="fb-name-row" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 8 }}>
                    <label className="fb-emp-datefield">
                      <span>Hire date</span>
                      <input type="date" value={emp.hire_date}
                        onChange={e => updateEmployee(ei, { hire_date: e.target.value })}
                        className="fb-input fb-input-full" />
                    </label>
                    <label className="fb-emp-datefield">
                      <span>End date</span>
                      <input type="date" value={emp.end_date}
                        onChange={e => updateEmployee(ei, { end_date: e.target.value })}
                        className="fb-input fb-input-full" />
                    </label>
                  </div>

                  {/* Location/unit assignment (multi) */}
                  <div className="fb-emp-assign">
                    <div className="fb-emp-assign-label"><FiMapPin size={12} /> Assigned locations / units</div>
                    {assignmentOptions.length === 0 ? (
                      <small className="fb-field-hint">Add locations in the “Locations &amp; Units” tab and save first, then assign here.</small>
                    ) : (
                      <>
                        <SearchableDropdown
                          multiSelect
                          options={assignmentOptions}
                          selectedValues={emp.assignments}
                          onToggleValue={(v: string) => toggleEmployeeAssignment(ei, v)}
                          placeholder={emp.assignments.length ? `${emp.assignments.length} selected` : 'Select locations / units'}
                          wrapperClassName="fb-emp-assign-picker"
                        />
                        {emp.assignments.length > 0 && (
                          <div className="fb-emp-assign-chips">
                            {emp.assignments.map(v => (
                              <span className="fb-emp-chip" key={v}>
                                {assignmentLabel(v)}
                                <button type="button" onClick={() => toggleEmployeeAssignment(ei, v)} title="Remove"><FiX size={11} /></button>
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Optional home address */}
                  <details className="fb-emp-address">
                    <summary>Home address (optional)</summary>
                    <div className="fb-address-stack" style={{ marginTop: 8 }}>
                      <input type="text" placeholder="Address 1" value={emp.emp_address_1}
                        onChange={e => updateEmployee(ei, { emp_address_1: e.target.value })} className="fb-input fb-input-full" />
                      <input type="text" placeholder="Address 2" value={emp.emp_address_2}
                        onChange={e => updateEmployee(ei, { emp_address_2: e.target.value })} className="fb-input fb-input-full" />
                      <select value={emp.emp_country} onChange={e => updateEmployee(ei, { emp_country: e.target.value })} className="fb-input fb-input-full">
                        <option value="">Select Country</option>
                        {countries.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input type="text" placeholder="State / Province" value={emp.emp_state}
                        onChange={e => updateEmployee(ei, { emp_state: e.target.value })} className="fb-input fb-input-full" />
                      <input type="text" placeholder="Zip / Postal Code" value={emp.emp_postal_code}
                        onChange={e => updateEmployee(ei, { emp_postal_code: e.target.value })} className="fb-input fb-input-full" />
                      <input type="text" placeholder="City / Town / Village" value={emp.emp_city}
                        onChange={e => updateEmployee(ei, { emp_city: e.target.value })} className="fb-input fb-input-full" />
                      <input type="text" placeholder="County / District" value={emp.emp_county}
                        onChange={e => updateEmployee(ei, { emp_county: e.target.value })} className="fb-input fb-input-full" />
                    </div>
                  </details>

                  {/* Notes */}
                  <textarea placeholder="Notes (optional)" value={emp.notes}
                    onChange={e => updateEmployee(ei, { notes: e.target.value })}
                    className="fb-input fb-input-full fb-emp-notes" rows={2} />
                </fieldset>
              ))}

              <button type="button" className="fb-btn-add fb-btn-add-loc" onClick={addEmployee}>
                <FiPlus size={16} /> Add Employee
              </button>
            </div>

            {/* ---- Form Actions (shared across both tabs) ---- */}
            <div className="fb-form-actions">
              <button type="submit" className="fb-btn-submit">
                <FiCheck size={16} /> {editingId ? 'Update' : 'Submit'}
              </button>
              <button type="button" className="fb-btn-cancel" onClick={closeForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* ============ Business Cards List ============ */}
      {businesses.length === 0 && !showForm ? (
        <div className="fb-family-empty">
          <FiUsers size={40} />
          <p>No businesses added yet.</p>
        </div>
      ) : businesses.length > 0 && (
        <div className="fb-family-list fb-family-grid">
          {/* First tile: add a business */}
          {!showForm && (
            <button type="button" className="fb-family-add-tile" onClick={openAddForm}>
              <FiPlus size={20} />
              <span>Add Business</span>
            </button>
          )}
          {businesses.slice().sort((x, y) =>
            String(x.busi_legal_name || '').localeCompare(String(y.busi_legal_name || ''), undefined, { sensitivity: 'base' })
          ).map(b => (
            <div key={b.business_id} className="fb-family-card">
              <div className="fb-family-card-clickable" onClick={() => { setSelectedBizId(String(b.business_id)); openEditForm(b); }}>
                <div className="fb-family-card-avatar fb-biz-avatar">
                  {(b.busi_acronym || b.busi_legal_name?.[0] || '?')}
                </div>
                <div className="fb-family-card-info">
                  <div className="fb-family-card-name">
                    {b.busi_legal_name}
                    {b.busi_acronym && <span className="fb-family-card-nickname">({b.busi_acronym})</span>}
                  </div>
                  {b.busi_operating_name && (
                    <div className="fb-family-card-relationship">Operating as: {b.busi_operating_name}</div>
                  )}
                  <div className="fb-family-card-details">
                    {b.busi_type && <span className="fb-biz-type-tag">{b.busi_type}</span>}
                    {b.busi_email && <span>{b.busi_email}</span>}
                    {b.busi_email_verified === 1 && <span className="fb-family-verified-tag">Email verified</span>}
                    {b.busi_phone && <span>{b.busi_phone}</span>}
                  </div>
                  <div className="fb-family-card-meta">
                    {b.busi_address_city && <span>{b.busi_address_city}{b.busi_address_state ? `, ${b.busi_address_state}` : ''}</span>}
                    {b.busi_address_verified === 1 && <span className="fb-family-verified-tag">Addr verified</span>}
                    {b.busi_registration_number && <span>Reg# {b.busi_registration_number}</span>}
                  </div>
                </div>
              </div>
              <div className="fb-family-card-actions">
                <button className="fb-btn-icon" title="Edit" onClick={() => {
                  setSelectedBizId(String(b.business_id));
                  openEditForm(b);
                }}><FiEdit2 size={16} /></button>
                <button className="fb-btn-icon fb-btn-icon-danger" title="Remove"
                  onClick={() => handleRemove(b.business_id)}><FiTrash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================
// Client Family Sub-Tab
// Shows family members of a personal client (owner_id = client's user_id)
// Reuses FamilyFormFields and helper functions from YourFamilyTab
// =============================================

interface ClientSubTabProps {
  clientUser: Record<string, any>;
}

const ClientFamilySubTab: React.FC<ClientSubTabProps> = ({ clientUser }) => {
  const clientId = clientUser.user_id;
  const [allUsers, setAllUsers] = useState<Record<string, any>[]>(() => getTableData('users'));
  const countries = useMemo(() => getCountries(), []);
  const [members, setMembers] = useState<Record<string, any>[]>(() => getFamilyMembers(clientId));
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [form, setForm] = useState<FamilyFormFields>(EMPTY_FAMILY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    loadTable('users').then(rows => {
      setAllUsers(rows);
      setMembers(getFamilyMembers(clientId, rows));
    }).catch(() => {});
  }, [clientId]);

  const handleChange = useCallback((field: keyof FamilyFormFields, value: string) => {
    setFormDirty(true);
    setForm(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => ({ ...prev, [field]: '' }));
  }, []);

  const openAddForm = useCallback(() => {
    setForm({ ...EMPTY_FAMILY_FORM, relationship: 'Spouse', address_country: clientUser.address_country || '' });
    setEditingId(null); setShowForm(true); setFormErrors({});
  }, [clientUser]);

  const openEditForm = useCallback((m: Record<string, any>) => {
    setForm({
      first_name: m.first_name || '', middle_name: m.middle_name || '', last_name: m.last_name || '',
      relationship: m.relationship || '', email: m.email || '', gender: m.gender || '',
      nickname: m.nickname || '', screen_name: m.screen_name || '', user_name: m.user_name || '',
      address_country: m.address_country || '', address_street: m.address_street || '',
      address_city: m.address_city || '', address_state: m.address_state || '',
      address_postal_code: m.address_postal_code || '', country_code: m.country_code || '',
      mail_address_street: m.mail_address_street || '', mail_address_city: m.mail_address_city || '',
      mail_address_state: m.mail_address_state || '', mail_address_postal_code: m.mail_address_postal_code || '',
      mail_address_country: m.mail_address_country || '',
    });
    setEditingId(m.user_id); setShowForm(true); setFormErrors({});
  }, []);

  const closeForm = useCallback(() => { setFormDirty(false); setShowForm(false); setEditingId(null); }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!form.first_name.trim()) errors.first_name = 'First name is required';
    if (!form.last_name.trim()) errors.last_name = 'Last name is required';
    if (!form.relationship) errors.relationship = 'Relationship is required';
    if (form.email.trim() && !isValidEmail(form.email.trim())) errors.email = 'Invalid email format';
    else if (form.email.trim() && !isEmailUnique(form.email.trim(), editingId ?? undefined, allUsers, clientId, true))
      errors.email = 'This email is already in use';
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const nickname = form.nickname.trim() || ((form.first_name[0] || '') + (form.last_name[0] || '')).toUpperCase();
    let userName = form.user_name.trim();
    if (!userName) {
      const fn = form.first_name.toLowerCase().replace(/[^a-z0-9]/g, '');
      const ln = form.last_name.toLowerCase().replace(/[^a-z0-9]/g, '');
      userName = fn.slice(0, 4).padEnd(4, 'x') + '.' + ln.slice(0, 4).padEnd(4, 'x');
    }

    const commonFields: Record<string, any> = {
      first_name: form.first_name.trim(), middle_name: form.middle_name.trim() || null, last_name: form.last_name.trim(),
      relationship: form.relationship, email: form.email.trim(), gender: form.gender,
      nickname, screen_name: form.screen_name.trim() || null, user_name: userName,
      display_name: `${form.first_name.trim()} ${form.last_name.trim()}`,
      address_country: form.address_country || null, address_street: form.address_street || null,
      address_city: form.address_city || null, address_state: form.address_state || null,
      address_postal_code: form.address_postal_code || null, country_code: form.country_code || null,
    };

    try {
      if (editingId) {
        await saveRow('users', editingId, { ...commonFields, updated_at: now });
      } else {
        await addRow('users', {
          ...commonFields, owner_id: clientId, user_type_id: 9,
          password_hash: 'HASH_invite_pending', data_access_scope: 'self',
          notification: 1, is_active: 0, is_deleted: 0, created_at: now, updated_at: now,
        });
      }
    } catch (err: any) {
      setSavedMsg(err.message || 'Save failed'); setTimeout(() => setSavedMsg(''), 3000); return;
    }

    const rows = await loadTable('users').catch(() => getTableData('users'));
    setAllUsers(rows); setMembers(getFamilyMembers(clientId, rows));
    closeForm();
    setSavedMsg(editingId ? 'Family member updated!' : 'Family member added!');
    setTimeout(() => setSavedMsg(''), 3000);
  }, [form, clientId, editingId, allUsers, closeForm]);

  const handleRemove = useCallback(async (userId: number | string) => {
    if (!window.confirm('Remove this family member?')) return;
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    try { await saveRow('users', userId, { is_deleted: 1, updated_at: now }); } catch {}
    const rows = await loadTable('users').catch(() => getTableData('users'));
    setAllUsers(rows); setMembers(getFamilyMembers(clientId, rows));
    setSavedMsg('Family member removed.'); setTimeout(() => setSavedMsg(''), 3000);
  }, [clientId]);

  const RELATIONSHIPS = ['Spouse', 'Child', 'Parent', 'Sibling', 'Other'];

  // "Save & continue" for this form (modal).
  useEffect(() => {
    registerSaveHandler(submitSaver(handleSubmit));
    return () => registerSaveHandler(null);
  }, [handleSubmit]);

  return (
    <div className="fb-client-subtab-content">
      <div className="fb-info-banner" style={{ margin: '12px 0' }}>
        <p>Manage family members of <strong>{clientUser.first_name} {clientUser.last_name}</strong>.</p>
        <p>These are the client's family — not your own.</p>
      </div>
      <div className="fb-family-action-bar">
        <span className="fb-family-count"><FiUsers size={16} /> {members.length} family member{members.length !== 1 ? 's' : ''}</span>
        {savedMsg && <span className="fb-saved-msg">{savedMsg}</span>}
      </div>

      {showForm && (
        <div className="fb-family-form-card">
          <div className="fb-family-form-header">
            <h4>{editingId ? 'Edit Family Member' : 'Add Family Member'}</h4>
            <button className="fb-btn-icon" onClick={closeForm} title="Cancel"><FiX size={18} /></button>
          </div>
          <form className="fb-form" onSubmit={handleSubmit}>
            <div className="fb-name-row">
              <fieldset className="fb-fieldset"><legend>First Name *</legend>
                <input type="text" placeholder="First Name" value={form.first_name} onChange={e => handleChange('first_name', e.target.value)}
                  className={`fb-input fb-input-full ${formErrors.first_name ? 'fb-input-error' : ''}`} />
                {formErrors.first_name && <span className="fb-field-error">{formErrors.first_name}</span>}
              </fieldset>
              <fieldset className="fb-fieldset"><legend>Middle Name</legend>
                <input type="text" placeholder="Middle Name" value={form.middle_name} onChange={e => handleChange('middle_name', e.target.value)} className="fb-input fb-input-full" />
              </fieldset>
              <fieldset className="fb-fieldset"><legend>Last Name *</legend>
                <input type="text" placeholder="Last Name" value={form.last_name} onChange={e => handleChange('last_name', e.target.value)}
                  className={`fb-input fb-input-full ${formErrors.last_name ? 'fb-input-error' : ''}`} />
                {formErrors.last_name && <span className="fb-field-error">{formErrors.last_name}</span>}
              </fieldset>
            </div>
            <fieldset className="fb-fieldset"><legend>Relationship *</legend>
              <select value={form.relationship} onChange={e => handleChange('relationship', e.target.value)}
                className={`fb-input fb-input-full ${formErrors.relationship ? 'fb-input-error' : ''}`}>
                <option value="">Select Relationship</option>
                {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              {formErrors.relationship && <span className="fb-field-error">{formErrors.relationship}</span>}
            </fieldset>
            <fieldset className="fb-fieldset"><legend>Email</legend>
              <input type="email" placeholder="user@domain.com" value={form.email} onChange={e => handleChange('email', e.target.value)}
                className={`fb-input fb-input-full ${formErrors.email ? 'fb-input-error' : ''}`} />
              {formErrors.email && <span className="fb-field-error">{formErrors.email}</span>}
            </fieldset>
            <fieldset className="fb-fieldset"><legend>Gender</legend>
              <select value={form.gender} onChange={e => handleChange('gender', e.target.value)} className="fb-input fb-input-full">
                <option value="">Select Gender</option>
                <option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option>
              </select>
            </fieldset>
            <fieldset className="fb-fieldset fb-address-fieldset"><legend>Address</legend>
              <div className="fb-address-stack">
                <select value={form.address_country} onChange={e => handleChange('address_country', e.target.value)} className="fb-input fb-input-full">
                  <option value="">Select Country</option>
                  {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input type="text" placeholder="Street Address" value={form.address_street} onChange={e => handleChange('address_street', e.target.value)} className="fb-input fb-input-full" />
                <input type="text" placeholder="City / Town / Village" value={form.address_city} onChange={e => handleChange('address_city', e.target.value)} className="fb-input fb-input-full" />
                <input type="text" placeholder="State / Province" value={form.address_state} onChange={e => handleChange('address_state', e.target.value)} className="fb-input fb-input-full" />
                <input type="text" placeholder="Zip / Postal Code" value={form.address_postal_code} onChange={e => handleChange('address_postal_code', e.target.value)} className="fb-input fb-input-full" />
              </div>
            </fieldset>
            <div className="fb-form-actions">
              <button type="submit" className="fb-btn-submit"><FiCheck size={16} /> {editingId ? 'Update' : 'Add Member'}</button>
              <button type="button" className="fb-btn-cancel" onClick={closeForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {members.length === 0 && !showForm ? (
        <div className="fb-family-empty">
          <FiUsers size={40} />
          <p>No family members added yet for this client.</p>
          <button className="fb-btn-add" onClick={openAddForm}><FiPlus size={16} /> Add First Family Member</button>
        </div>
      ) : members.length > 0 && (
        <div className="fb-family-list fb-family-grid">
          {!showForm && (
            <button type="button" className="fb-family-add-tile" onClick={openAddForm}>
              <FiPlus size={20} />
              <span>Add Family Member</span>
            </button>
          )}
          {sortFamilyMembers(members).map(m => (
            <div key={m.user_id} className="fb-family-card">
              <div className="fb-family-card-clickable" onClick={() => openEditForm(m)}>
                <div className="fb-family-card-avatar">{(m.first_name?.[0] || '?')}{(m.last_name?.[0] || '')}</div>
                <div className="fb-family-card-info">
                  <div className="fb-family-card-name">{m.first_name} {m.middle_name ? m.middle_name + ' ' : ''}{m.last_name}
                    {m.nickname && <span className="fb-family-card-nickname">({m.nickname})</span>}
                  </div>
                  <div className="fb-family-card-relationship">{m.relationship}</div>
                  <div className="fb-family-card-details">
                    {m.email && <span>{m.email}</span>}
                    {m.mobile_number && <span>{m.mobile_number}</span>}
                  </div>
                </div>
              </div>
              <div className="fb-family-card-actions">
                <button className="fb-btn-icon" onClick={() => openEditForm(m)} title="Edit"><FiEdit2 size={16} /></button>
                <button className="fb-btn-icon fb-btn-danger" onClick={() => handleRemove(m.user_id)} title="Remove"><FiTrash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================
// Client Businesses Sub-Tab
// Shows businesses of a personal client (owner_id = client's user_id, is_client_business = 1)
// =============================================

const ClientBusinessesSubTab: React.FC<ClientSubTabProps> = ({ clientUser }) => {
  const clientId = clientUser.user_id;
  const accountOwner = useMemo(() => getAccountOwner(), []);
  const [allUsers, setAllUsers] = useState<Record<string, any>[]>(() => getTableData('users'));
  const [allBusinesses, setAllBusinesses] = useState<Record<string, any>[]>(() => getTableData('user_businesses'));
  const countries = useMemo(() => getCountries(), []);

  const getClientBiz = useCallback((bizData?: Record<string, any>[]) => {
    const biz = bizData || getTableData('user_businesses');
    // eslint-disable-next-line eqeqeq
    return biz.filter(b => b.owner_id == clientId && b.is_deleted != 1 && b.is_client_business == 1);
  }, [clientId]);

  const [businesses, setBusinesses] = useState<Record<string, any>[]>(() => getClientBiz());
  const { checkBusinessMove, moveModal } = useBusinessMoveCapture(Number(clientId) || null, (m) => { setSavedMsg(m); setTimeout(() => setSavedMsg(''), 4000); });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [form, setForm] = useState<BusinessFormFields>(EMPTY_BUSINESS_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    Promise.all([loadTable('users'), loadTable('user_businesses'), loadTable('site_mstr_countries')]).then(([usersRows, bizRows, countriesRows]) => {
      setAllUsers(usersRows); setAllBusinesses(bizRows);
      setBusinesses(getClientBiz(bizRows));
    }).catch(() => {});
  }, [clientId, getClientBiz]);

  const handleChange = useCallback((field: keyof BusinessFormFields, value: string) => {
    setFormDirty(true);
    setForm(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => ({ ...prev, [field]: '' }));
  }, []);

  const openAddForm = useCallback(() => {
    setForm({ ...EMPTY_BUSINESS_FORM, busi_address_country: clientUser.address_country || '' });
    setEditingId(null); setShowForm(true); setFormErrors({});
  }, [clientUser]);

  const openEditForm = useCallback((b: Record<string, any>) => {
    setForm({
      busi_legal_name: b.busi_legal_name || '', busi_operating_name: b.busi_operating_name || '',
      busi_display_name: b.busi_display_name || '', busi_acronym: b.busi_acronym || '',
      busi_type: b.busi_type || '', busi_registration_number: b.busi_registration_number || '',
      busi_phone: b.busi_phone || '', busi_email: b.busi_email || '',
      base_currency: String(b.base_currency || '').trim().toUpperCase(),
      busi_country_code: b.busi_country_code || '', busi_address_1: b.busi_address_1 || '',
      busi_address_2: b.busi_address_2 || '', busi_address_country: b.busi_address_country || '',
      busi_address_state: b.busi_address_state || '', busi_address_postal_code: b.busi_address_postal_code || '',
      busi_address_city: b.busi_address_city || '', busi_address_county: b.busi_address_county || '',
      busi_mail_address_1: b.busi_mail_address_1 || '', busi_mail_address_2: b.busi_mail_address_2 || '',
      busi_mail_address_country: b.busi_mail_address_country || '', busi_mail_address_state: b.busi_mail_address_state || '',
      busi_mail_address_postal_code: b.busi_mail_address_postal_code || '', busi_mail_address_city: b.busi_mail_address_city || '',
      busi_mail_address_county: b.busi_mail_address_county || '',
    });
    setEditingId(b.business_id); setShowForm(true); setFormErrors({});
  }, []);

  const closeForm = useCallback(() => { setFormDirty(false); setShowForm(false); setEditingId(null); }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!form.busi_legal_name.trim()) errors.busi_legal_name = 'Legal name is required';
    if (form.busi_email.trim() && !isValidEmail(form.busi_email.trim())) errors.busi_email = 'Invalid email format';
    if (form.busi_address_postal_code && form.busi_address_country) {
      const { valid, hint } = isValidPostalCode(form.busi_address_postal_code, form.busi_address_country);
      if (!valid) errors.busi_address_postal_code = hint;
    }
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const commonFields: Record<string, any> = {
      busi_legal_name: form.busi_legal_name.trim(), busi_operating_name: form.busi_operating_name.trim() || null,
      busi_display_name: form.busi_display_name.trim() || null, busi_acronym: form.busi_acronym.trim() || null,
      busi_type: form.busi_type || null, busi_registration_number: form.busi_registration_number.trim() || null,
      base_currency: form.base_currency || null,
      busi_phone: form.busi_phone.trim() || null, busi_email: form.busi_email.trim() || null,
      busi_country_code: form.busi_country_code || null, busi_address_1: form.busi_address_1.trim() || null,
      busi_address_2: form.busi_address_2.trim() || null, busi_address_country: form.busi_address_country || null,
      busi_address_state: form.busi_address_state.trim() || null, busi_address_postal_code: form.busi_address_postal_code.trim() || null,
      busi_address_city: form.busi_address_city.trim() || null, busi_address_county: form.busi_address_county.trim() || null,
      busi_mail_address_1: form.busi_mail_address_1.trim() || null, busi_mail_address_2: form.busi_mail_address_2.trim() || null,
      busi_mail_address_country: form.busi_mail_address_country || null, busi_mail_address_state: form.busi_mail_address_state.trim() || null,
      busi_mail_address_postal_code: form.busi_mail_address_postal_code.trim() || null,
      busi_mail_address_city: form.busi_mail_address_city.trim() || null, busi_mail_address_county: form.busi_mail_address_county.trim() || null,
    };

    try {
      if (editingId) {
        const prevBiz = businesses.find((b: any) => b.business_id == editingId);
        const oldState = String(prevBiz?.busi_address_state || '').trim().toUpperCase();
        const prevSince = String(prevBiz?.created_at || '').slice(0, 10) || '2000-01-01';
        await saveRow('user_businesses', editingId, { ...commonFields, updated_at: now });
        const newState = String(form.busi_address_state || '').trim().toUpperCase();
        if (oldState && newState && oldState !== newState && form.busi_address_country) {
          checkBusinessMove(Number(editingId), oldState, newState, String(form.busi_address_country), prevSince);
        }
      } else {
        const inserted = await addRow('user_businesses', {
          ...commonFields, owner_id: clientId, is_client_business: 1,
          is_active: 0, is_deleted: 0, created_at: now, updated_at: now,
        });
        if (accountOwner) {
          await createClientRelationship(accountOwner.user_id, 'business', undefined, inserted.business_id);
        }
      }
    } catch (err: any) {
      setSavedMsg(err.message || 'Save failed'); setTimeout(() => setSavedMsg(''), 3000); return;
    }

    const bizRows = await loadTable('user_businesses').catch(() => getTableData('user_businesses'));
    setAllBusinesses(bizRows); setBusinesses(getClientBiz(bizRows));
    closeForm();
    setSavedMsg(editingId ? 'Business updated!' : 'Business added!');
    setTimeout(() => setSavedMsg(''), 3000);
  }, [form, clientId, editingId, accountOwner, closeForm, getClientBiz]);

  const handleRemove = useCallback(async (bizId: number | string) => {
    if (!window.confirm('Remove this business?')) return;
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    try { await saveRow('user_businesses', bizId, { is_deleted: 1, updated_at: now }); } catch {}
    if (accountOwner) await removeClientRelationship(accountOwner.user_id, 'business', bizId);
    const bizRows = await loadTable('user_businesses').catch(() => getTableData('user_businesses'));
    setAllBusinesses(bizRows); setBusinesses(getClientBiz(bizRows));
    setSavedMsg('Business removed.'); setTimeout(() => setSavedMsg(''), 3000);
  }, [clientId, accountOwner, getClientBiz]);

  // "Save & continue" for this form (modal).
  useEffect(() => {
    registerSaveHandler(submitSaver(handleSubmit));
    return () => registerSaveHandler(null);
  }, [handleSubmit]);

  return (
    <div className="fb-client-subtab-content">
      {moveModal}
      <div className="fb-info-banner" style={{ margin: '12px 0' }}>
        <p>Manage businesses for <strong>{clientUser.first_name} {clientUser.last_name}</strong>.</p>
        <p>Add any business this person owns, manages, or does bookkeeping for.</p>
      </div>
      <div className="fb-family-action-bar">
        <span className="fb-family-count"><FiUsers size={16} /> {businesses.length} business{businesses.length !== 1 ? 'es' : ''}</span>
        {savedMsg && <span className="fb-saved-msg">{savedMsg}</span>}
      </div>

      {showForm && (
        <div className="fb-family-form-card">
          <div className="fb-family-form-header">
            <h4>{editingId ? 'Edit Business' : 'Add Business'}</h4>
            <button className="fb-btn-icon" onClick={closeForm} title="Cancel"><FiX size={18} /></button>
          </div>
          <form className="fb-form" onSubmit={handleSubmit} noValidate>
            <fieldset className="fb-fieldset"><legend>Legal Name of Business *</legend>
              <input type="text" placeholder="Full Legal Name" value={form.busi_legal_name} onChange={e => handleChange('busi_legal_name', e.target.value)}
                className={`fb-input fb-input-full ${formErrors.busi_legal_name ? 'fb-input-error' : ''}`} required />
              {formErrors.busi_legal_name && <span className="fb-field-error">{formErrors.busi_legal_name}</span>}
            </fieldset>
            <div className="fb-name-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <fieldset className="fb-fieldset"><legend>Operating as Name</legend>
                <input type="text" placeholder="DBA name" value={form.busi_operating_name} onChange={e => handleChange('busi_operating_name', e.target.value)} className="fb-input fb-input-full" />
              </fieldset>
              <fieldset className="fb-fieldset"><legend>Display name in Portal</legend>
                <input type="text" placeholder="Portal display name" value={form.busi_display_name} onChange={e => handleChange('busi_display_name', e.target.value)} className="fb-input fb-input-full" />
              </fieldset>
            </div>
            <div className="fb-name-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <fieldset className="fb-fieldset"><legend>Acronym</legend>
                <input type="text" placeholder="Short acronym" value={form.busi_acronym} onChange={e => handleChange('busi_acronym', e.target.value)} className="fb-input fb-input-full" />
              </fieldset>
              <fieldset className="fb-fieldset"><legend>Type</legend>
                <select value={form.busi_type} onChange={e => handleChange('busi_type', e.target.value)} className="fb-input fb-input-full">
                  <option value="">Select Type</option>
                  {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </fieldset>
            </div>
            <fieldset className="fb-fieldset"><legend>Registration Number</legend>
              <input type="text" placeholder="Business Registration Number" value={form.busi_registration_number} onChange={e => handleChange('busi_registration_number', e.target.value)} className="fb-input fb-input-full" />
            </fieldset>
            <BaseCurrencyField
              value={form.base_currency}
              country={form.busi_address_country}
              ownerId={clientId}
              users={allUsers}
              onChange={v => handleChange('base_currency', v)}
            />
            <div className="fb-name-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <fieldset className="fb-fieldset"><legend>Business Phone</legend>
                <PhoneInput value={form.busi_phone} onChange={v => handleChange('busi_phone', v)} defaultCountry={form.busi_address_country} inputClassName="fb-input" />
              </fieldset>
              <fieldset className="fb-fieldset"><legend>Business Email</legend>
                <input type="email" placeholder="business@domain.com" value={form.busi_email} onChange={e => handleChange('busi_email', e.target.value)}
                  className={`fb-input fb-input-full ${formErrors.busi_email ? 'fb-input-error' : ''}`} />
                {formErrors.busi_email && <span className="fb-field-error">{formErrors.busi_email}</span>}
              </fieldset>
            </div>
            <fieldset className="fb-fieldset fb-address-fieldset"><legend>Physical Address</legend>
              <div className="fb-address-stack">
                <input type="text" placeholder="Address Line 1" value={form.busi_address_1} onChange={e => handleChange('busi_address_1', e.target.value)} className="fb-input fb-input-full" />
                <input type="text" placeholder="Address Line 2" value={form.busi_address_2} onChange={e => handleChange('busi_address_2', e.target.value)} className="fb-input fb-input-full" />
                <select value={form.busi_address_country} onChange={e => handleChange('busi_address_country', e.target.value)} className="fb-input fb-input-full">
                  <option value="">Select Country</option>
                  {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input type="text" placeholder="State / Province" value={form.busi_address_state} onChange={e => handleChange('busi_address_state', e.target.value)} className="fb-input fb-input-full" />
                <input type="text" placeholder="Zip / Postal Code" value={form.busi_address_postal_code} onChange={e => handleChange('busi_address_postal_code', e.target.value)}
                  className={`fb-input fb-input-full ${formErrors.busi_address_postal_code ? 'fb-input-error' : ''}`} />
                {formErrors.busi_address_postal_code && <span className="fb-field-error">{formErrors.busi_address_postal_code}</span>}
                <input type="text" placeholder="City / Town / Village" value={form.busi_address_city} onChange={e => handleChange('busi_address_city', e.target.value)} className="fb-input fb-input-full" />
                <input type="text" placeholder="County / District" value={form.busi_address_county} onChange={e => handleChange('busi_address_county', e.target.value)} className="fb-input fb-input-full" />
              </div>
            </fieldset>
            <div className="fb-form-actions">
              <button type="submit" className="fb-btn-submit"><FiCheck size={16} /> {editingId ? 'Update' : 'Add Business'}</button>
              <button type="button" className="fb-btn-cancel" onClick={closeForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {businesses.length === 0 && !showForm ? (
        <div className="fb-family-empty">
          <FiUsers size={40} />
          <p>No businesses added yet for this client.</p>
          <button className="fb-btn-add" onClick={openAddForm}><FiPlus size={16} /> Add First Business</button>
        </div>
      ) : businesses.length > 0 && (
        <div className="fb-family-list fb-family-grid">
          {!showForm && (
            <button type="button" className="fb-family-add-tile" onClick={openAddForm}>
              <FiPlus size={20} />
              <span>Add Business</span>
            </button>
          )}
          {businesses.slice().sort((x, y) =>
            String(x.busi_legal_name || '').localeCompare(String(y.busi_legal_name || ''), undefined, { sensitivity: 'base' })
          ).map(b => (
            <div key={`biz-${b.business_id}`} className="fb-family-card">
              <div className="fb-family-card-clickable" onClick={() => openEditForm(b)}>
                <div className="fb-family-card-avatar fb-biz-avatar">{(b.busi_acronym || b.busi_legal_name?.[0] || '?')}</div>
                <div className="fb-family-card-info">
                  <div className="fb-family-card-name">{b.busi_legal_name}
                    {b.busi_acronym && <span className="fb-family-card-nickname">({b.busi_acronym})</span>}
                    <span className="fb-biz-type-tag" style={{ marginLeft: 8, background: '#1565c0' }}>Business</span>
                  </div>
                  {b.busi_operating_name && <div className="fb-family-card-relationship">Operating as: {b.busi_operating_name}</div>}
                  <div className="fb-family-card-details">
                    {b.busi_type && <span className="fb-biz-type-tag">{b.busi_type}</span>}
                    {b.busi_email && <span>{b.busi_email}</span>}
                    {b.busi_phone && <span>{b.busi_phone}</span>}
                  </div>
                </div>
              </div>
              <div className="fb-family-card-actions">
                <button className="fb-btn-icon" onClick={() => openEditForm(b)} title="Edit"><FiEdit2 size={16} /></button>
                <button className="fb-btn-icon fb-btn-danger" onClick={() => handleRemove(b.business_id)} title="Remove"><FiTrash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================
// Your Clients Tab
// Hierarchy: Account Owner → Family → Businesses → CLIENTS
// Clients = people or businesses the Account Owner manages
// professionally (as a bookkeeper, accountant, tax pro).
// Two sub-types:
//   - Personal Client → stored in users table (relationship = 'Personal Client')
//   - Business Client → stored in user_businesses table (is_client_business = 1)
// Both create a row in user_client_relationships for engagement tracking.
// Supports future ownership transfer to another accountant.
// =============================================

/** Get personal clients under the account owner */
function getPersonalClients(ownerId: number | string, usersData?: Record<string, any>[]): Record<string, any>[] {
  const users = usersData || getTableData('users');
  // eslint-disable-next-line eqeqeq
  return users.filter(u => u.owner_id == ownerId && u.is_deleted !== 1 && u.relationship === 'Personal Client');
}

/** Get business clients under the account owner */
function getClientBusinesses(ownerId: number | string, businessesData?: Record<string, any>[]): Record<string, any>[] {
  const businesses = businessesData || getTableData('user_businesses');
  // eslint-disable-next-line eqeqeq
  return businesses.filter(b => b.owner_id == ownerId && b.is_deleted != 1 && b.is_client_business == 1);
}

/** Create a client_relationships row */
async function createClientRelationship(
  accountantId: number | string,
  clientType: 'personal' | 'business',
  clientUserId?: number | string,
  clientBusinessId?: number | string,
) {
  const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
  const row: Record<string, any> = {
    accountant_user_id: accountantId,
    client_user_id: clientType === 'personal' ? clientUserId : null,
    client_business_id: clientType === 'business' ? clientBusinessId : null,
    client_type: clientType,
    engagement_status: 'active',
    engagement_start_date: now,
    engagement_end_date: null,
    transferred_from_user_id: null,
    transferred_to_user_id: null,
    transferred_at: null,
    transfer_notes: null,
    notes: null,
    is_active: 1, is_deleted: 0,
    created_at: now, updated_at: now,
  };
  try {
    const inserted = await addRow('user_client_relationships', row);
    return inserted.relationship_id;
  } catch {
    // Fallback already handled in addRow
    return row.relationship_id;
  }
}

/** Soft-delete a client_relationships row by client reference */
async function removeClientRelationship(
  accountantId: number | string,
  clientType: 'personal' | 'business',
  clientRefId: number | string,
) {
  const data = await loadTable('user_client_relationships');
  const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
  const row = data.find(r => {
    // eslint-disable-next-line eqeqeq
    if (r.accountant_user_id != accountantId || r.is_deleted === 1) return false;
    if (clientType === 'personal') {
      // eslint-disable-next-line eqeqeq
      return r.client_type === 'personal' && r.client_user_id == clientRefId;
    }
    // eslint-disable-next-line eqeqeq
    return r.client_type === 'business' && r.client_business_id == clientRefId;
  });
  if (row) {
    try {
      await saveRow('user_client_relationships', row.relationship_id, {
        is_deleted: 1, engagement_status: 'terminated', engagement_end_date: now, updated_at: now,
      });
    } catch {
      // Fallback handled in saveRow
    }
  }
}

const YourClientsTab: React.FC = () => {
  const [owner, setOwner] = useState(() => getAccountOwner());
  const ownerId = owner?.user_id;
  const [allUsers, setAllUsers] = useState<Record<string, any>[]>(() => getTableData('users'));
  const [allBusinesses, setAllBusinesses] = useState<Record<string, any>[]>(() => getTableData('user_businesses'));
  const [countries, setCountries] = useState<string[]>(() => getCountries());

  // Client lists
  const [personalClients, setPersonalClients] = useState(() => ownerId ? getPersonalClients(ownerId) : []);
  const [businessClients, setBusinessClients] = useState(() => ownerId ? getClientBusinesses(ownerId) : []);

  // Expand a client row to reveal that client's businesses + family inline.
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const toggleExpanded = useCallback((id: number | string) => {
    const key = String(id);
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);
  // A client's own businesses / family members (children of the client user).
  const businessesOfClient = useCallback(
    // eslint-disable-next-line eqeqeq
    (clientId: number | string) => allBusinesses.filter(b => b.owner_id == clientId && b.is_deleted != 1),
    [allBusinesses],
  );
  const familyOfClient = useCallback(
    // eslint-disable-next-line eqeqeq
    (clientId: number | string) => allUsers.filter(u => u.owner_id == clientId && u.is_deleted != 1 && u.relationship !== 'Personal Client'),
    [allUsers],
  );

  // Load from PostgreSQL if available
  useEffect(() => {
    Promise.all([
      loadTable('users'),
      loadTable('user_businesses'),
      loadTable('site_mstr_countries'),
    ]).then(([usersRows, bizRows, countriesRows]) => {
      setAllUsers(usersRows);
      setAllBusinesses(bizRows);
      const o = getAccountOwner(usersRows);
      if (o) {
        setOwner(o);
        setPersonalClients(getPersonalClients(o.user_id, usersRows));
        setBusinessClients(getClientBusinesses(o.user_id, bizRows));
      }
      setCountries(getCountries(countriesRows));
    }).catch(() => {});
  }, []);

  // Which form is open: null, 'personal', or 'business'
  const [formType, setFormType] = useState<'personal' | 'business' | null>(null);
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [savedMsg, setSavedMsg] = useState('');
  const { checkBusinessMove, moveModal } = useBusinessMoveCapture(Number(ownerId) || null, (m) => { setSavedMsg(m); setTimeout(() => setSavedMsg(''), 4000); });
  // "Add a Business Client" flow: a business is added under a person, so this flow
  // opens the personal-client form + shows existing clients to attach the biz to.
  const [bizClientFlow, setBizClientFlow] = useState(false);
  // Sub-tab within personal client edit: 'info' | 'family' | 'businesses'
  const [clientSubTab, setClientSubTab] = useState<'info' | 'family' | 'businesses'>('info');

  // ===================== PERSONAL CLIENT FORM STATE =====================
  const [pForm, setPForm] = useState<FamilyFormFields>({ ...EMPTY_FAMILY_FORM, relationship: 'Personal Client' });
  const [pFormErrors, setPFormErrors] = useState<Record<string, string>>({});
  const [pMobilePhones, setPMobilePhones] = useState<PhoneEntry[]>([{ type: 'Primary Mobile', number: '' }]);
  const [pLandlinePhones, setPLandlinePhones] = useState<PhoneEntry[]>([{ type: 'Home Phone', number: '' }]);
  const [pPhoneError, setPPhoneError] = useState('');
  const [pEmailError, setPEmailError] = useState('');
  const [pEmailVerifyState, setPEmailVerifyState] = useState<'idle' | 'sent' | 'verified'>('idle');
  const [pVerifyCode, setPVerifyCode] = useState('');
  const [pGeneratedCode, setPGeneratedCode] = useState('');
  const [pVerifyError, setPVerifyError] = useState('');
  const [pAddressVerified, setPAddressVerified] = useState(false);
  const [pAddressVerifying, setPAddressVerifying] = useState(false);
  const [pPostalError, setPPostalError] = useState('');
  const [pMailSameAsPhysical, setPMailSameAsPhysical] = useState(false);
  const [pMailAddNew, setPMailAddNew] = useState(false);
  const [pMailPostalError, setPMailPostalError] = useState('');
  const [pOwnerPhoneConfirmed, setPOwnerPhoneConfirmed] = useState<Set<string>>(new Set());
  const [pOwnerEmailConfirmed, setPOwnerEmailConfirmed] = useState(false);

  // ===================== BUSINESS CLIENT FORM STATE =====================
  const [bForm, setBForm] = useState<BusinessFormFields>(EMPTY_BUSINESS_FORM);
  const [bFormErrors, setBFormErrors] = useState<Record<string, string>>({});
  const [bEmailVerifyState, setBEmailVerifyState] = useState<'idle' | 'sent' | 'verified'>('idle');
  const [bVerifyCode, setBVerifyCode] = useState('');
  const [bGeneratedCode, setBGeneratedCode] = useState('');
  const [bVerifyError, setBVerifyError] = useState('');
  const [bAddressVerified, setBAddressVerified] = useState(false);
  const [bAddressVerifying, setBAddressVerifying] = useState(false);
  const [bPostalError, setBPostalError] = useState('');
  const [ownerPhoneConfirmedCB, setOwnerPhoneConfirmedCB] = useState(false);
  const [ownerEmailConfirmedCB, setOwnerEmailConfirmedCB] = useState(false);
  const [bMailSameAsPhysical, setBMailSameAsPhysical] = useState(false);
  const [bMailAddNew, setBMailAddNew] = useState(false);
  const [bMailPostalError, setBMailPostalError] = useState('');

  const refreshClients = useCallback(async () => {
    if (!ownerId) return;
    try {
      const [usersRows, bizRows] = await Promise.all([
        loadTable('users'),
        loadTable('user_businesses'),
      ]);
      setAllUsers(usersRows);
      setAllBusinesses(bizRows);
      setPersonalClients(getPersonalClients(ownerId, usersRows));
      setBusinessClients(getClientBusinesses(ownerId, bizRows));
    } catch {
      setPersonalClients(getPersonalClients(ownerId));
      setBusinessClients(getClientBusinesses(ownerId));
    }
  }, [ownerId]);

  // ===================== PERSONAL CLIENT HANDLERS =====================
  const handlePChange = useCallback((field: keyof FamilyFormFields, value: string) => {
    setFormDirty(true);
    setPForm(prev => ({ ...prev, [field]: value }));
    setPFormErrors(prev => ({ ...prev, [field]: '' }));
    if (field.startsWith('address_')) setPAddressVerified(false);
  }, []);

  const formatPhone = (raw: string): string => raw ? raw.replace(/[^+\d]/g, '') : '';

  const handlePMobileChange = useCallback((idx: number, field: 'type' | 'number', value: string) => {
    setPMobilePhones(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
    setPPhoneError('');
  }, []);
  const handlePLandlineChange = useCallback((idx: number, field: 'type' | 'number', value: string) => {
    setPLandlinePhones(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
    setPPhoneError('');
  }, []);
  const addPMobile = useCallback(() => {
    setPMobilePhones(prev => prev.length >= MAX_PHONES_PER_SECTION ? prev : [...prev, { type: nextMobileLabel(prev), number: '' }]);
  }, []);
  const addPLandline = useCallback(() => {
    setPLandlinePhones(prev => prev.length >= MAX_PHONES_PER_SECTION ? prev : [...prev, { type: nextLandlineLabel(prev), number: '' }]);
  }, []);
  const removePMobile = useCallback((idx: number) => {
    setPMobilePhones(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
    setPPhoneError('');
  }, []);
  const removePLandline = useCallback((idx: number) => {
    setPLandlinePhones(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
    setPPhoneError('');
  }, []);

  // Personal email verification
  const handlePSendVerification = useCallback(() => {
    if (!isValidEmail(pForm.email)) { setPEmailError('Please enter a valid email (e.g. user@domain.com or u@d.c)'); return; }
    setPEmailError('');
    const code = String(Math.floor(100000 + Math.random() * 900000));
    setPGeneratedCode(code); setPEmailVerifyState('sent'); setPVerifyError('');
    console.log('[BK] Personal client email verification code (mock):', code, 'to:', pForm.email);
  }, [pForm.email]);
  const handlePVerifyCode = useCallback(() => {
    if (pVerifyCode.trim() === pGeneratedCode) { setPEmailVerifyState('verified'); setPVerifyError(''); }
    else setPVerifyError('Invalid code. Please check the code from the verification link.');
  }, [pVerifyCode, pGeneratedCode]);

  // Personal address verification
  const handlePVerifyAddress = useCallback(() => {
    setPAddressVerifying(true);
    setTimeout(() => {
      if (pForm.address_street && pForm.address_city && pForm.address_state && pForm.address_postal_code && pForm.address_country)
        setPAddressVerified(true);
      setPAddressVerifying(false);
    }, 1500);
  }, [pForm]);
  const handlePPostalChange = useCallback((value: string) => {
    handlePChange('address_postal_code', value);
    if (value && pForm.address_country) {
      const { valid, hint } = isValidPostalCode(value, pForm.address_country);
      setPPostalError(valid ? '' : hint);
    } else setPPostalError('');
  }, [handlePChange, pForm.address_country]);

  // Personal mailing address
  const handlePMailPostalChange = useCallback((value: string) => {
    handlePChange('mail_address_postal_code', value);
    if (value && pForm.mail_address_country) {
      const { valid, hint } = isValidPostalCode(value, pForm.mail_address_country);
      setPMailPostalError(valid ? '' : hint);
    } else setPMailPostalError('');
  }, [handlePChange, pForm.mail_address_country]);
  const handlePMailSameToggle = useCallback((checked: boolean) => {
    setPMailSameAsPhysical(checked);
    if (checked) { setPMailAddNew(false); setPForm(prev => ({ ...prev, mail_address_street: prev.address_street, mail_address_city: prev.address_city, mail_address_state: prev.address_state, mail_address_postal_code: prev.address_postal_code, mail_address_country: prev.address_country })); setPMailPostalError(''); }
  }, []);
  const handlePMailAddNewToggle = useCallback((checked: boolean) => {
    setPMailAddNew(checked);
    if (checked) { setPMailSameAsPhysical(false); setPForm(prev => ({ ...prev, mail_address_street: '', mail_address_city: '', mail_address_state: '', mail_address_postal_code: '', mail_address_country: '' })); setPMailPostalError(''); }
  }, []);

  // ===================== BUSINESS CLIENT HANDLERS =====================
  const handleBChange = useCallback((field: keyof BusinessFormFields, value: string) => {
    setFormDirty(true);
    setBForm(prev => ({ ...prev, [field]: value }));
    setBFormErrors(prev => ({ ...prev, [field]: '' }));
    if (field.startsWith('busi_address_')) setBAddressVerified(false);
  }, []);

  // Business email verification
  const handleBSendVerification = useCallback(() => {
    if (!isValidEmail(bForm.busi_email)) { setBFormErrors(prev => ({ ...prev, busi_email: 'Please enter a valid email' })); return; }
    setBFormErrors(prev => ({ ...prev, busi_email: '' }));
    const code = String(Math.floor(100000 + Math.random() * 900000));
    setBGeneratedCode(code); setBEmailVerifyState('sent'); setBVerifyError('');
    console.log('[BK] Client business email verification code (mock):', code, 'to:', bForm.busi_email);
  }, [bForm.busi_email]);
  const handleBVerifyCode = useCallback(() => {
    if (bVerifyCode.trim() === bGeneratedCode) { setBEmailVerifyState('verified'); setBVerifyError(''); }
    else setBVerifyError('Invalid code.');
  }, [bVerifyCode, bGeneratedCode]);

  // Business address verification
  const handleBVerifyAddress = useCallback(() => {
    setBAddressVerifying(true);
    setTimeout(() => {
      if (bForm.busi_address_1 && bForm.busi_address_city && bForm.busi_address_state && bForm.busi_address_postal_code && bForm.busi_address_country)
        setBAddressVerified(true);
      setBAddressVerifying(false);
    }, 1500);
  }, [bForm]);
  const handleBPostalChange = useCallback((value: string) => {
    handleBChange('busi_address_postal_code', value);
    if (value && bForm.busi_address_country) {
      const { valid, hint } = isValidPostalCode(value, bForm.busi_address_country);
      setBPostalError(valid ? '' : hint);
    } else setBPostalError('');
  }, [handleBChange, bForm.busi_address_country]);

  // Business mailing address
  const handleBMailPostalChange = useCallback((value: string) => {
    handleBChange('busi_mail_address_postal_code', value);
    if (value && bForm.busi_mail_address_country) {
      const { valid, hint } = isValidPostalCode(value, bForm.busi_mail_address_country);
      setBMailPostalError(valid ? '' : hint);
    } else setBMailPostalError('');
  }, [handleBChange, bForm.busi_mail_address_country]);
  const handleBMailSameToggle = useCallback((checked: boolean) => {
    setBMailSameAsPhysical(checked);
    if (checked) { setBMailAddNew(false); setBForm(prev => ({ ...prev, busi_mail_address_1: prev.busi_address_1, busi_mail_address_2: prev.busi_address_2, busi_mail_address_country: prev.busi_address_country, busi_mail_address_state: prev.busi_address_state, busi_mail_address_postal_code: prev.busi_address_postal_code, busi_mail_address_city: prev.busi_address_city, busi_mail_address_county: prev.busi_address_county })); setBMailPostalError(''); }
  }, []);
  const handleBMailAddNewToggle = useCallback((checked: boolean) => {
    setBMailAddNew(checked);
    if (checked) { setBMailSameAsPhysical(false); setBForm(prev => ({ ...prev, busi_mail_address_1: '', busi_mail_address_2: '', busi_mail_address_country: '', busi_mail_address_state: '', busi_mail_address_postal_code: '', busi_mail_address_city: '', busi_mail_address_county: '' })); setBMailPostalError(''); }
  }, []);

  // ===================== OPEN / CLOSE FORMS =====================
  const resetPersonalForm = useCallback(() => {
    setPForm({ ...EMPTY_FAMILY_FORM, relationship: 'Personal Client', address_country: owner?.address_country || '', address_street: owner?.address_street || '', address_city: owner?.address_city || '', address_state: owner?.address_state || '', address_postal_code: owner?.address_postal_code || '', country_code: owner?.country_code || '' });
    setPMobilePhones([{ type: 'Primary Mobile', number: '' }]); setPLandlinePhones([{ type: 'Home Phone', number: '' }]);
    setPFormErrors({}); setPPhoneError(''); setPEmailError(''); setPEmailVerifyState('idle');
    setPVerifyCode(''); setPVerifyError(''); setPAddressVerified(false); setPPostalError('');
    setPMailSameAsPhysical(false); setPMailAddNew(false); setPMailPostalError('');
    setPOwnerPhoneConfirmed(new Set()); setPOwnerEmailConfirmed(false);
  }, [owner]);

  const resetBusinessForm = useCallback(() => {
    setBForm({ ...EMPTY_BUSINESS_FORM, busi_address_country: owner?.address_country || '', busi_country_code: owner?.country_code || '' });
    setBFormErrors({}); setBEmailVerifyState('idle'); setBVerifyCode(''); setBVerifyError('');
    setBAddressVerified(false); setBPostalError(''); setBMailSameAsPhysical(false); setBMailAddNew(false); setBMailPostalError('');
    setOwnerPhoneConfirmedCB(false); setOwnerEmailConfirmedCB(false);
  }, [owner]);

  const openPersonalForm = useCallback(() => {
    resetPersonalForm(); setEditingId(null); setFormType('personal'); setClientSubTab('info');
  }, [resetPersonalForm]);

  // Auto-open the "Add a Personal Client" form when arrived via the
  // "Add your first client" CTA on the Client's Accounts landing.
  useEffect(() => {
    let flag = '';
    try { flag = sessionStorage.getItem('akt_open_add_personal_client') || ''; } catch {}
    if (flag === '1') {
      try { sessionStorage.removeItem('akt_open_add_personal_client'); } catch {}
      openPersonalForm();
    }
  }, [openPersonalForm]);

  const openBusinessForm = useCallback(() => {
    resetBusinessForm(); setEditingId(null); setFormType('business');
  }, [resetBusinessForm]);

  const openEditPersonal = useCallback((member: Record<string, any>, subTab?: 'info' | 'family' | 'businesses') => {
    setPForm({
      first_name: member.first_name || '', middle_name: member.middle_name || '', last_name: member.last_name || '',
      relationship: 'Personal Client', email: member.email || '', gender: member.gender || '',
      nickname: member.nickname || '', screen_name: member.screen_name || '', user_name: member.user_name || '',
      address_country: member.address_country || '', address_street: member.address_street || '',
      address_city: member.address_city || '', address_state: member.address_state || '',
      address_postal_code: member.address_postal_code || '', country_code: member.country_code || '',
      mail_address_street: member.mail_address_street || '', mail_address_city: member.mail_address_city || '',
      mail_address_state: member.mail_address_state || '', mail_address_postal_code: member.mail_address_postal_code || '',
      mail_address_country: member.mail_address_country || '',
    });
    setPMailSameAsPhysical(member.mail_same_as_physical === 1);
    setPMailAddNew(!!(member.mail_address_street || member.mail_address_city) && member.mail_same_as_physical !== 1);
    setPMailPostalError('');
    // Load phones
    const mobiles: PhoneEntry[] = [];
    if (member.mobile_number) mobiles.push({ type: 'Primary Mobile', number: member.mobile_number });
    if (member.secondary_mobile) mobiles.push({ type: 'Secondary Mobile', number: member.secondary_mobile });
    const extras: PhoneEntry[] = Array.isArray(member.extra_phones) ? member.extra_phones : [];
    extras.filter((p: PhoneEntry) => p.type?.startsWith('Mobile')).forEach((p: PhoneEntry) => mobiles.push(p));
    if (mobiles.length === 0) mobiles.push({ type: 'Primary Mobile', number: '' });
    setPMobilePhones(mobiles);
    const landlines: PhoneEntry[] = [];
    if (member.home_phone) landlines.push({ type: 'Home Phone', number: member.home_phone });
    if (member.business_phone) landlines.push({ type: 'Business Phone', number: member.business_phone });
    extras.filter((p: PhoneEntry) => p.type?.startsWith('Phone')).forEach((p: PhoneEntry) => landlines.push(p));
    if (landlines.length === 0) landlines.push({ type: 'Home Phone', number: '' });
    setPLandlinePhones(landlines);
    setPFormErrors({}); setPPhoneError(''); setPEmailError('');
    setPEmailVerifyState(member.email_verified === 1 ? 'verified' : 'idle');
    setPVerifyCode(''); setPVerifyError(''); setPAddressVerified(member.address_verified === 1); setPPostalError('');
    setPOwnerPhoneConfirmed(new Set()); setPOwnerEmailConfirmed(false);
    setEditingId(member.user_id); setFormType('personal'); setClientSubTab(subTab || 'info'); setBizClientFlow(false);
  }, []);

  const openEditBusiness = useCallback((biz: Record<string, any>) => {
    setBForm({
      busi_legal_name: biz.busi_legal_name || '', busi_operating_name: biz.busi_operating_name || '',
      busi_display_name: biz.busi_display_name || '', busi_acronym: biz.busi_acronym || '',
      busi_type: biz.busi_type || '', busi_registration_number: biz.busi_registration_number || '',
      busi_phone: biz.busi_phone || '', busi_email: biz.busi_email || '',
      base_currency: String(biz.base_currency || '').trim().toUpperCase(),
      busi_country_code: biz.busi_country_code || '', busi_address_1: biz.busi_address_1 || '',
      busi_address_2: biz.busi_address_2 || '', busi_address_country: biz.busi_address_country || '',
      busi_address_state: biz.busi_address_state || '', busi_address_postal_code: biz.busi_address_postal_code || '',
      busi_address_city: biz.busi_address_city || '', busi_address_county: biz.busi_address_county || '',
      busi_mail_address_1: biz.busi_mail_address_1 || '', busi_mail_address_2: biz.busi_mail_address_2 || '',
      busi_mail_address_country: biz.busi_mail_address_country || '', busi_mail_address_state: biz.busi_mail_address_state || '',
      busi_mail_address_postal_code: biz.busi_mail_address_postal_code || '', busi_mail_address_city: biz.busi_mail_address_city || '',
      busi_mail_address_county: biz.busi_mail_address_county || '',
    });
    setBFormErrors({}); setBEmailVerifyState(biz.busi_email_verified === 1 ? 'verified' : 'idle');
    setBVerifyCode(''); setBVerifyError(''); setBAddressVerified(biz.busi_address_verified === 1);
    setBPostalError(''); setBMailSameAsPhysical(biz.busi_mail_same_as_physical === 1);
    setBMailAddNew(!!(biz.busi_mail_address_1 || biz.busi_mail_address_city) && biz.busi_mail_same_as_physical !== 1);
    setBMailPostalError('');
    setEditingId(biz.business_id); setFormType('business');
  }, []);

  const closeForm = useCallback(() => {
    setFormDirty(false); setFormType(null); setEditingId(null); setClientSubTab('info'); setBizClientFlow(false);
  }, []);

  // ===================== SUBMIT PERSONAL CLIENT =====================
  const handleSubmitPersonal = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ownerId || !owner) return;
    let createdClient: Record<string, any> | null = null;  // for the business-client flow redirect

    const errors: Record<string, string> = {};
    if (!pForm.first_name.trim()) errors.first_name = 'First name is required';
    if (!pForm.last_name.trim()) errors.last_name = 'Last name is required';
    if (pForm.email.trim() && !isValidEmail(pForm.email.trim())) errors.email = 'Invalid email format (minimum: u@d.c)';
    else if (pForm.email.trim() && !isEmailUnique(pForm.email.trim(), editingId ?? undefined, allUsers, ownerId)) {
      if (emailMatchesOwner(pForm.email.trim(), owner) && !pOwnerEmailConfirmed) {
        errors.email = '__owner_email__';
      } else if (!emailMatchesOwner(pForm.email.trim(), owner)) {
        errors.email = 'This email is already in use';
      }
    }
    if (pForm.nickname.trim() && !isNicknameUnique(ownerId, pForm.nickname.trim(), editingId ?? undefined, allUsers))
      errors.nickname = 'This nickname is already used in your account';
    if (pForm.screen_name.trim() && !isScreenNameUnique(pForm.screen_name.trim(), editingId ?? undefined, allUsers))
      errors.screen_name = 'This screen name is already taken';
    if (pForm.user_name.trim() && !/^[a-zA-Z0-9]{4,}\.[a-zA-Z0-9]{4,}$/.test(pForm.user_name.trim()))
      errors.user_name = 'Format: 4+ letters/numbers, a dot, then 4+ letters/numbers';

    const allPhoneNums = [...pMobilePhones, ...pLandlinePhones].map(p => formatPhone(p.number)).filter(Boolean);
    if (allPhoneNums.length !== new Set(allPhoneNums).size) { setPPhoneError('Each phone number must be unique.'); setPFormErrors(errors); return; }
    for (const p of [...pMobilePhones, ...pLandlinePhones]) {
      const clean = formatPhone(p.number);
      if (clean && phoneConflictsWithOwner(p.number, owner) && !pOwnerPhoneConfirmed.has(clean)) {
        setPPhoneError(`__owner_phone__:${clean}`); setPFormErrors(errors); return;
      }
    }
    setPPhoneError('');

    if (pForm.address_postal_code && pForm.address_country) {
      const { valid, hint } = isValidPostalCode(pForm.address_postal_code, pForm.address_country);
      if (!valid) { setPPostalError(hint); setPFormErrors(errors); return; }
    }
    if (pMailAddNew && !pMailSameAsPhysical && pForm.mail_address_postal_code && pForm.mail_address_country) {
      const { valid, hint } = isValidPostalCode(pForm.mail_address_postal_code, pForm.mail_address_country);
      if (!valid) { setPMailPostalError(hint); setPFormErrors(errors); return; }
    }
    if (Object.keys(errors).length > 0) { setPFormErrors(errors); return; }

    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const nickname = pForm.nickname.trim() || ((pForm.first_name[0] || '') + (pForm.last_name[0] || '')).toUpperCase();
    let userName = pForm.user_name.trim();
    if (!userName) {
      const fn = pForm.first_name.toLowerCase().replace(/[^a-z0-9]/g, '');
      const ln = pForm.last_name.toLowerCase().replace(/[^a-z0-9]/g, '');
      userName = fn.slice(0, 4).padEnd(4, 'x') + '.' + ln.slice(0, 4).padEnd(4, 'x');
    }

    const mPrimary = pMobilePhones.find(p => p.type === 'Primary Mobile');
    const mSecondary = pMobilePhones.find(p => p.type === 'Secondary Mobile');
    const extraMobiles = pMobilePhones.filter(p => p.type !== 'Primary Mobile' && p.type !== 'Secondary Mobile' && p.number.trim()).map(p => ({ type: p.type, number: formatPhone(p.number) }));
    const lHome = pLandlinePhones.find(p => p.type === 'Home Phone');
    const lBusiness = pLandlinePhones.find(p => p.type === 'Business Phone');
    const extraLandlines = pLandlinePhones.filter(p => p.type !== 'Home Phone' && p.type !== 'Business Phone' && p.number.trim()).map(p => ({ type: p.type, number: formatPhone(p.number) }));
    const allExtras = [...extraMobiles, ...extraLandlines];

    const commonFields: Record<string, any> = {
      first_name: pForm.first_name.trim(), middle_name: pForm.middle_name.trim() || null, last_name: pForm.last_name.trim(),
      relationship: 'Personal Client', email: pForm.email.trim(), gender: pForm.gender,
      nickname, screen_name: pForm.screen_name.trim() || null, user_name: userName,
      display_name: `${pForm.first_name.trim()} ${pForm.last_name.trim()}`,
      mobile_number: formatPhone(mPrimary?.number || '') || null, secondary_mobile: formatPhone(mSecondary?.number || '') || null,
      home_phone: formatPhone(lHome?.number || '') || null, business_phone: formatPhone(lBusiness?.number || '') || null,
      extra_phones: allExtras.length > 0 ? allExtras : null,
      country_code: pForm.country_code || null, address_street: pForm.address_street || null,
      address_city: pForm.address_city || null, address_state: pForm.address_state || null,
      address_postal_code: pForm.address_postal_code || null, address_country: pForm.address_country || null,
      address_verified: pAddressVerified ? 1 : 0,
      mail_same_as_physical: pMailSameAsPhysical ? 1 : 0,
      mail_address_street: pMailSameAsPhysical ? (pForm.address_street || null) : (pForm.mail_address_street || null),
      mail_address_city: pMailSameAsPhysical ? (pForm.address_city || null) : (pForm.mail_address_city || null),
      mail_address_state: pMailSameAsPhysical ? (pForm.address_state || null) : (pForm.mail_address_state || null),
      mail_address_postal_code: pMailSameAsPhysical ? (pForm.address_postal_code || null) : (pForm.mail_address_postal_code || null),
      mail_address_country: pMailSameAsPhysical ? (pForm.address_country || null) : (pForm.mail_address_country || null),
      email_verified: pEmailVerifyState === 'verified' ? 1 : 0,
    };

    try {
      if (editingId) {
        await saveRow('users', editingId, { ...commonFields, updated_at: now });
        console.log('[BK] Updated personal client user_id:', editingId);
      } else {
        // Free-plan gate: 1 client unless platform/support or subscribed (see entitlements.ts)
        const gate = canAddPersonalClient(Number(ownerId));
        if (!gate.allowed) {
          setSavedMsg(gate.reason);
          setTimeout(() => setSavedMsg(''), 5000);
          return;
        }
        const newRowData: Record<string, any> = {
          ...commonFields,
          owner_id: ownerId, user_type_id: 9,
          password_hash: 'HASH_invite_pending',
          data_access_scope: 'self',
          notification: 1,
          is_active: 0, is_deleted: 0,
          created_at: now, updated_at: now,
        };
        const inserted = await addRow('users', newRowData);
        const newId = inserted.user_id;
        await createClientRelationship(ownerId, 'personal', newId);
        createdClient = { ...newRowData, user_id: newId };
        console.log('[BK] Added personal client user_id:', newId, 'under owner_id:', ownerId);
      }
    } catch (err: any) {
      setSavedMsg(err.message || 'Save failed');
      setTimeout(() => setSavedMsg(''), 3000);
      return;
    }

    await refreshClients();
    // Business-client flow: after creating the person, jump straight to their
    // Businesses tab to add the business.
    if (bizClientFlow && createdClient) {
      setBizClientFlow(false);
      openEditPersonal(createdClient, 'businesses');
    } else {
      closeForm();
    }
    setSavedMsg(editingId ? 'Personal client updated!' : 'Personal client added!');
    setTimeout(() => setSavedMsg(''), 3000);
  }, [pForm, ownerId, owner, editingId, pMobilePhones, pLandlinePhones, pEmailVerifyState, pAddressVerified, pMailSameAsPhysical, pMailAddNew, pOwnerPhoneConfirmed, pOwnerEmailConfirmed, allUsers, refreshClients, closeForm, bizClientFlow, openEditPersonal]);

  // ===================== SUBMIT BUSINESS CLIENT =====================
  const handleSubmitBusiness = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ownerId || !owner) return;

    const errors: Record<string, string> = {};
    if (!bForm.busi_legal_name.trim()) errors.busi_legal_name = 'Legal name is required';
    if (bForm.busi_email.trim() && !isValidEmail(bForm.busi_email.trim())) errors.busi_email = 'Invalid email format';
    else if (bForm.busi_email.trim() && !isBusinessEmailUnique(bForm.busi_email.trim(), editingId ?? undefined, allUsers, allBusinesses, ownerId)) {
      if (isBusinessEmailMatchesOwner(bForm.busi_email.trim(), ownerId, allUsers) && !ownerEmailConfirmedCB) errors.busi_email = '__owner_email__';
      else if (!isBusinessEmailMatchesOwner(bForm.busi_email.trim(), ownerId, allUsers)) errors.busi_email = 'This email is already in use';
    }
    if (bForm.busi_phone.trim() && !isBusinessPhoneUnique(bForm.busi_phone.trim(), ownerId, editingId ?? undefined, allUsers, allBusinesses)) {
      if (isBusinessPhoneMatchesOwner(bForm.busi_phone.trim(), ownerId, allUsers) && !ownerPhoneConfirmedCB) errors.busi_phone = '__owner_phone__';
      else if (!isBusinessPhoneMatchesOwner(bForm.busi_phone.trim(), ownerId, allUsers)) errors.busi_phone = 'This phone number is already in use';
    }
    if (bForm.busi_acronym.trim() && !isAcronymUnique(ownerId, bForm.busi_acronym.trim(), editingId ?? undefined, allUsers, allBusinesses))
      errors.busi_acronym = 'This acronym is already used in your account';
    // Physical address — all optional
    if (bForm.busi_address_postal_code && bForm.busi_address_country) {
      const { valid, hint } = isValidPostalCode(bForm.busi_address_postal_code, bForm.busi_address_country);
      if (!valid) { setBPostalError(hint); errors.busi_address_postal_code = hint; }
    }
    if (bMailAddNew && !bMailSameAsPhysical && bForm.busi_mail_address_postal_code && bForm.busi_mail_address_country) {
      const { valid, hint } = isValidPostalCode(bForm.busi_mail_address_postal_code, bForm.busi_mail_address_country);
      if (!valid) { setBMailPostalError(hint); errors.busi_mail_address_postal_code = hint; }
    }
    if (Object.keys(errors).length > 0) { setBFormErrors(errors); return; }

    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const mailFields = {
      busi_mail_same_as_physical: bMailSameAsPhysical ? 1 : 0,
      busi_mail_address_1: bMailSameAsPhysical ? (bForm.busi_address_1 || null) : (bForm.busi_mail_address_1 || null),
      busi_mail_address_2: bMailSameAsPhysical ? (bForm.busi_address_2 || null) : (bForm.busi_mail_address_2 || null),
      busi_mail_address_country: bMailSameAsPhysical ? (bForm.busi_address_country || null) : (bForm.busi_mail_address_country || null),
      busi_mail_address_state: bMailSameAsPhysical ? (bForm.busi_address_state || null) : (bForm.busi_mail_address_state || null),
      busi_mail_address_postal_code: bMailSameAsPhysical ? (bForm.busi_address_postal_code || null) : (bForm.busi_mail_address_postal_code || null),
      busi_mail_address_city: bMailSameAsPhysical ? (bForm.busi_address_city || null) : (bForm.busi_mail_address_city || null),
      busi_mail_address_county: bMailSameAsPhysical ? (bForm.busi_address_county || null) : (bForm.busi_mail_address_county || null),
    };

    const commonFields: Record<string, any> = {
      busi_legal_name: bForm.busi_legal_name.trim(), busi_operating_name: bForm.busi_operating_name.trim() || null,
      busi_display_name: bForm.busi_display_name.trim() || null, busi_acronym: bForm.busi_acronym.trim() || null,
      busi_type: bForm.busi_type || null, busi_registration_number: bForm.busi_registration_number.trim() || null,
      base_currency: bForm.base_currency || null,
      busi_phone: formatPhone(bForm.busi_phone) || null, busi_email: bForm.busi_email.trim() || null,
      busi_email_verified: bEmailVerifyState === 'verified' ? 1 : 0,
      busi_country_code: bForm.busi_country_code || null,
      busi_address_1: bForm.busi_address_1.trim() || null, busi_address_2: bForm.busi_address_2.trim() || null,
      busi_address_country: bForm.busi_address_country || null, busi_address_state: bForm.busi_address_state.trim() || null,
      busi_address_postal_code: bForm.busi_address_postal_code.trim() || null,
      busi_address_city: bForm.busi_address_city.trim() || null, busi_address_county: bForm.busi_address_county.trim() || null,
      busi_address_verified: bAddressVerified ? 1 : 0, ...mailFields,
    };

    try {
      if (editingId) {
        const prevBiz = allBusinesses.find((b: any) => b.business_id == editingId);
        const oldState = String(prevBiz?.busi_address_state || '').trim().toUpperCase();
        const prevSince = String(prevBiz?.created_at || '').slice(0, 10) || '2000-01-01';
        await saveRow('user_businesses', editingId, { ...commonFields, updated_at: now });
        console.log('[BK] Updated client business business_id:', editingId);
        const newState = String(bForm.busi_address_state || '').trim().toUpperCase();
        if (oldState && newState && oldState !== newState && bForm.busi_address_country) {
          checkBusinessMove(Number(editingId), oldState, newState, String(bForm.busi_address_country), prevSince);
        }
      } else {
        const newRowData: Record<string, any> = {
          ...commonFields,
          owner_id: ownerId, is_client_business: 1,
          is_active: 0, is_deleted: 0, created_at: now, updated_at: now,
        };
        const inserted = await addRow('user_businesses', newRowData);
        const newId = inserted.business_id;
        await createClientRelationship(ownerId, 'business', undefined, newId);
        console.log('[BK] Added client business business_id:', newId, 'under owner_id:', ownerId);
      }
    } catch (err: any) {
      setSavedMsg(err.message || 'Save failed');
      setTimeout(() => setSavedMsg(''), 3000);
      return;
    }

    await refreshClients(); closeForm();
    setSavedMsg(editingId ? 'Business client updated!' : 'Business client added!');
    setTimeout(() => setSavedMsg(''), 3000);
  }, [bForm, ownerId, owner, editingId, bEmailVerifyState, bAddressVerified, bMailSameAsPhysical, bMailAddNew, ownerPhoneConfirmedCB, ownerEmailConfirmedCB, allUsers, allBusinesses, refreshClients, closeForm]);

  // ===================== REMOVE CLIENTS =====================
  const handleRemovePersonal = useCallback(async (memberId: number | string) => {
    try {
      await removeRow('users', memberId);
      if (ownerId) await removeClientRelationship(ownerId, 'personal', memberId);
      console.log('[BK] Soft-deleted personal client user_id:', memberId);
    } catch (err: any) {
      setSavedMsg(err.message || 'Remove failed');
      setTimeout(() => setSavedMsg(''), 3000);
      return;
    }
    await refreshClients();
    setSavedMsg('Personal client removed.'); setTimeout(() => setSavedMsg(''), 3000);
  }, [ownerId, refreshClients]);

  const handleRemoveBusiness = useCallback(async (bizId: number | string) => {
    try {
      await removeRow('user_businesses', bizId);
      if (ownerId) await removeClientRelationship(ownerId, 'business', bizId);
      console.log('[BK] Soft-deleted client business business_id:', bizId);
    } catch (err: any) {
      setSavedMsg(err.message || 'Remove failed');
      setTimeout(() => setSavedMsg(''), 3000);
      return;
    }
    await refreshClients();
    if (String(editingId) === String(bizId)) closeForm();
    setSavedMsg('Business client removed.'); setTimeout(() => setSavedMsg(''), 3000);
  }, [ownerId, editingId, refreshClients, closeForm]);

  // "Save & continue" for the open client form (personal or business) — modal.
  useEffect(() => {
    registerSaveHandler(formType
      ? submitSaver(formType === 'business' ? handleSubmitBusiness : handleSubmitPersonal)
      : null);
    return () => registerSaveHandler(null);
  }, [formType, handleSubmitPersonal, handleSubmitBusiness]);

  if (!owner || !ownerId) {
    return <div className="fb-no-owner">No account owner found. Please set up your account first.</div>;
  }

  const totalClients = personalClients.length + businessClients.length;

  // ===================== RENDER =====================
  return (
    <div className="fb-family-section">
      {moveModal}
      {/* Info moved to the menu ⓘ (after Payroll Setup) — see BookkeepingPage sub-nav. */}

      {/* Action Bar */}
      <div className="fb-family-action-bar">
        <span className="fb-family-count">
          <FiUsers size={16} />
          {totalClients} client{totalClients !== 1 ? 's' : ''}
          {personalClients.length > 0 && <span style={{fontSize:'0.85em',opacity:0.7}}> ({personalClients.length} personal)</span>}
          {businessClients.length > 0 && <span style={{fontSize:'0.85em',opacity:0.7}}> ({businessClients.length} business)</span>}
        </span>
        {/* With clients listed, the first tile of the grid holds these two buttons. */}
        {!formType && totalClients === 0 && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* A business is added under a person → this flow opens the personal form
                + an existing-clients list to attach the business to. */}
            <button className="fb-btn-add" onClick={() => { setBizClientFlow(true); openPersonalForm(); }}>
              <FiPlus size={16} /> Add a Business Client
            </button>
            <button className="fb-btn-add" onClick={() => { setBizClientFlow(false); openPersonalForm(); }}>
              <FiPlus size={16} /> Add a Personal Client
            </button>
          </div>
        )}
        {savedMsg && <span className="fb-saved-msg">{savedMsg}</span>}
      </div>


      {/* ============ PERSONAL CLIENT FORM ============ */}
      {formType === 'personal' && (
        <div className="fb-family-form-card">
          {/* Sub-tabs for editing an existing client */}
          {editingId ? (
            <>
              <div className="fb-family-form-header">
                <h4>Client: {pForm.first_name} {pForm.last_name}</h4>
                <button className="fb-btn-icon" onClick={closeForm} title="Close"><FiX size={18} /></button>
              </div>
              <div className="fb-client-subtabs">
                <button className={`fb-client-subtab ${clientSubTab === 'info' ? 'active' : ''}`}
                  onClick={() => setClientSubTab('info')}>Personal Info</button>
                <button className={`fb-client-subtab ${clientSubTab === 'family' ? 'active' : ''}`}
                  onClick={() => setClientSubTab('family')}>{pForm.first_name || 'Client'}'s Family</button>
                <button className={`fb-client-subtab ${clientSubTab === 'businesses' ? 'active' : ''}`}
                  onClick={() => setClientSubTab('businesses')}>{pForm.first_name || 'Client'}'s Businesses</button>
              </div>
            </>
          ) : (
            <div className="fb-family-form-header">
              <h4>{bizClientFlow ? 'Add a Business Client' : 'Add Personal Client'}</h4>
              <button className="fb-btn-icon" onClick={closeForm} title="Cancel"><FiX size={18} /></button>
            </div>
          )}

          {/* Business-client flow: explain + let the user attach to an existing person */}
          {bizClientFlow && !editingId && (
            <div className="fb-bizclient-intro">
              <div className="fb-info-banner" style={{ margin: '0 0 14px' }}>
                <p><strong>A business is managed by a person.</strong></p>
                <p>Create a profile for the business owner, a manager, or anyone related to the business — then add the business under that person. Or attach the business to an existing personal client below.</p>
              </div>
              {personalClients.length > 0 && (
                <div className="fb-bizclient-existing">
                  <div className="fb-bizclient-existing-head">Attach to an existing personal client</div>
                  {personalClients.map(c => (
                    <button key={`bcx-${c.user_id}`} type="button" className="fb-bizclient-existing-item"
                      onClick={() => { setBizClientFlow(false); openEditPersonal(c, 'businesses'); }}>
                      <span className="fb-bizclient-avatar">{(c.first_name?.[0] || '?')}{(c.last_name?.[0] || '')}</span>
                      <span className="fb-bizclient-info">
                        <span className="fb-bizclient-name">{`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email}{c.nickname ? ` (${c.nickname})` : ''}</span>
                        {c.address_city && <span className="fb-bizclient-loc">{c.address_city}{c.address_state ? `, ${c.address_state}` : ''}</span>}
                      </span>
                      <FiArrowRight size={16} className="fb-bizclient-go" />
                    </button>
                  ))}
                  <div className="fb-bizclient-or">— or create a new person below —</div>
                </div>
              )}
            </div>
          )}

          {/* Personal Info sub-tab (or new client form) */}
          {(clientSubTab === 'info' || !editingId) && (
          <form className="fb-form" onSubmit={handleSubmitPersonal}>
            {/* Name */}
            <fieldset className="fb-fieldset">
              <legend>Name *</legend>
              <div className="fb-name-row">
                <div>
                  <input type="text" placeholder="First Name *" value={pForm.first_name}
                    onChange={e => handlePChange('first_name', e.target.value)} required className="fb-input" />
                  {pFormErrors.first_name && <span className="fb-field-error">{pFormErrors.first_name}</span>}
                </div>
                <input type="text" placeholder="Middle Name" value={pForm.middle_name}
                  onChange={e => handlePChange('middle_name', e.target.value)} className="fb-input" />
                <div>
                  <input type="text" placeholder="Last Name *" value={pForm.last_name}
                    onChange={e => handlePChange('last_name', e.target.value)} required className="fb-input" />
                  {pFormErrors.last_name && <span className="fb-field-error">{pFormErrors.last_name}</span>}
                </div>
              </div>
            </fieldset>
            {/* Nickname */}
            <fieldset className="fb-fieldset">
              <legend>Nickname or Acronym</legend>
              <input type="text" placeholder="e.g. SM (auto-generated from initials if blank)"
                value={pForm.nickname} onChange={e => handlePChange('nickname', e.target.value)} maxLength={20} className="fb-input fb-input-full" />
              <small className="fb-field-hint">Must be unique within your account.</small>
              {pFormErrors.nickname && <span className="fb-field-error">{pFormErrors.nickname}</span>}
            </fieldset>
            {/* Screen Name */}
            <fieldset className="fb-fieldset">
              <legend>Screen Name</legend>
              <input type="text" placeholder="Public screen name" value={pForm.screen_name}
                onChange={e => handlePChange('screen_name', e.target.value)} maxLength={100} className="fb-input fb-input-full" />
              <small className="fb-field-hint">Must be unique across the platform.</small>
              {pFormErrors.screen_name && <span className="fb-field-error">{pFormErrors.screen_name}</span>}
            </fieldset>
            {/* Gender */}
            <fieldset className="fb-fieldset">
              <legend>Gender</legend>
              <select value={pForm.gender} onChange={e => handlePChange('gender', e.target.value)} className="fb-input fb-input-full">
                <option value="">Select Gender</option>
                <option value="Male">Male</option><option value="Female">Female</option>
                <option value="Non-binary">Non-binary</option><option value="Prefer not to say">Prefer not to say</option>
              </select>
              {pFormErrors.gender && <span className="fb-field-error">{pFormErrors.gender}</span>}
            </fieldset>
            {/* Mobile Phones */}
            <fieldset className="fb-fieldset">
              <legend>Mobile Phone(s)</legend>
              <small className="fb-field-hint">No number can be shared across fields.</small>
              {pMobilePhones.map((phone, idx) => (
                <div key={idx} className="fb-phone-entry">
                  <select value={phone.type} onChange={e => handlePMobileChange(idx, 'type', e.target.value)} className="fb-input fb-phone-type-select">
                    {getMobileTypeOptions(pMobilePhones, idx).map(t => <option key={t} value={t}>{t}</option>)}
                    {!getMobileTypeOptions(pMobilePhones, idx).includes(phone.type) && <option value={phone.type}>{phone.type}</option>}
                  </select>
                  <PhoneInput value={phone.number}
                    onChange={v => handlePMobileChange(idx, 'number', v)}
                    defaultCountry={pForm.address_country || owner?.address_country}
                    className="fb-phone-number-input" inputClassName="fb-input" />
                  {pMobilePhones.length > 1 && <button type="button" className="fb-btn-icon fb-btn-danger" onClick={() => removePMobile(idx)} title="Remove"><FiX size={16} /></button>}
                </div>
              ))}
              {pMobilePhones.length < MAX_PHONES_PER_SECTION && <button type="button" className="fb-btn-add-phone" onClick={addPMobile}><FiPlus size={14} /> Add another mobile</button>}
            </fieldset>
            {/* Other Phones */}
            <fieldset className="fb-fieldset">
              <legend>Other Phone(s)</legend>
              <small className="fb-field-hint">No mobiles here — use mobile section above.</small>
              {pLandlinePhones.map((phone, idx) => (
                <div key={idx} className="fb-phone-entry">
                  <select value={phone.type} onChange={e => handlePLandlineChange(idx, 'type', e.target.value)} className="fb-input fb-phone-type-select">
                    {getLandlineTypeOptions(pLandlinePhones, idx).map(t => <option key={t} value={t}>{t}</option>)}
                    {!getLandlineTypeOptions(pLandlinePhones, idx).includes(phone.type) && <option value={phone.type}>{phone.type}</option>}
                  </select>
                  <PhoneInput value={phone.number}
                    onChange={v => handlePLandlineChange(idx, 'number', v)}
                    defaultCountry={pForm.address_country || owner?.address_country}
                    className="fb-phone-number-input" inputClassName="fb-input" />
                  {pLandlinePhones.length > 1 && <button type="button" className="fb-btn-icon fb-btn-danger" onClick={() => removePLandline(idx)} title="Remove"><FiX size={16} /></button>}
                </div>
              ))}
              {pLandlinePhones.length < MAX_PHONES_PER_SECTION && <button type="button" className="fb-btn-add-phone" onClick={addPLandline}><FiPlus size={14} /> Add another phone</button>}
            </fieldset>
            {pPhoneError && !pPhoneError.startsWith('__owner_phone__') && <p className="fb-phone-error">{pPhoneError}</p>}
            {pPhoneError && pPhoneError.startsWith('__owner_phone__') && (
              <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '8px 12px', marginTop: 6 }}>
                <span style={{ color: '#e65100', fontWeight: 500 }}>This is your personal phone number.</span>{' '}
                <span>Use it for this client too?</span>
                <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                  <button type="button" className="fb-btn-verify" style={{ fontSize: 13, padding: '4px 14px' }}
                    onClick={() => { const num = pPhoneError.split(':')[1]; setPOwnerPhoneConfirmed(prev => new Set(prev).add(num)); setPPhoneError(''); }}>
                    Yes, use it
                  </button>
                  <button type="button" style={{ fontSize: 13, padding: '4px 14px', background: '#eee', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                    onClick={() => { const num = pPhoneError.split(':')[1]; const allPhones = [...pMobilePhones, ...pLandlinePhones]; const idx = allPhones.findIndex(p => formatPhone(p.number) === num); if (idx < pMobilePhones.length) { const updated = [...pMobilePhones]; updated[idx] = { ...updated[idx], number: '' }; setPMobilePhones(updated); } else { const li = idx - pMobilePhones.length; const updated = [...pLandlinePhones]; updated[li] = { ...updated[li], number: '' }; setPLandlinePhones(updated); } setPPhoneError(''); }}>
                    No, I'll enter a different one
                  </button>
                </div>
              </div>
            )}
            {/* User Name */}
            <fieldset className="fb-fieldset">
              <legend>User Name</legend>
              <input type="text" placeholder="firstname.lastname (e.g. john.smith)" value={pForm.user_name}
                onChange={e => handlePChange('user_name', e.target.value)} className="fb-input fb-input-full" />
              <small className="fb-field-hint">Format: 4+ letters/numbers, a dot, then 4+ letters/numbers. Auto-generated if blank.</small>
              {pFormErrors.user_name && <span className="fb-field-error">{pFormErrors.user_name}</span>}
            </fieldset>
            {/* Email */}
            <fieldset className="fb-fieldset">
              <legend>Email</legend>
              <div className="fb-email-row">
                <input type="email" placeholder="user@domain.com" value={pForm.email}
                  onChange={e => { handlePChange('email', e.target.value); setPEmailError(''); setPOwnerEmailConfirmed(false); if (pEmailVerifyState === 'verified') setPEmailVerifyState('idle'); }}
                  className={`fb-input fb-email-input ${pFormErrors.email && pFormErrors.email !== '__owner_email__' ? 'fb-input-error' : ''}`} />
                {pEmailVerifyState === 'verified' ? <span className="fb-verified-badge"><FiCheck size={14} /> Verified</span>
                  : <button type="button" className="fb-btn-verify" onClick={handlePSendVerification}>Verify Email</button>}
              </div>
              {pEmailError && <span className="fb-field-error">{pEmailError}</span>}
              {pFormErrors.email && pFormErrors.email !== '__owner_email__' && <span className="fb-field-error">{pFormErrors.email}</span>}
              {pFormErrors.email === '__owner_email__' && (
                <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '8px 12px', marginTop: 6 }}>
                  <span style={{ color: '#e65100', fontWeight: 500 }}>This is your personal email.</span>{' '}
                  <span>Use it for this client too?</span>
                  <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                    <button type="button" className="fb-btn-verify" style={{ fontSize: 13, padding: '4px 14px' }}
                      onClick={() => { setPOwnerEmailConfirmed(true); setPFormErrors(prev => { const n = { ...prev }; delete n.email; return n; }); }}>
                      Yes, use it
                    </button>
                    <button type="button" style={{ fontSize: 13, padding: '4px 14px', background: '#eee', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                      onClick={() => { handlePChange('email', ''); setPOwnerEmailConfirmed(false); setPFormErrors(prev => { const n = { ...prev }; delete n.email; return n; }); }}>
                      No, I'll enter a different one
                    </button>
                  </div>
                </div>
              )}
              {pEmailVerifyState === 'sent' && (
                <div className="fb-verify-code-section">
                  <p className="fb-verify-instruction">A verification link has been sent to <strong>{pForm.email}</strong>.</p>
                  <div className="fb-verify-code-row">
                    <input type="text" placeholder="Enter 6-digit code" value={pVerifyCode}
                      onChange={e => { setPVerifyCode(e.target.value); setPVerifyError(''); }} maxLength={6} className="fb-input fb-verify-code-input" />
                    <button type="button" className="fb-btn-verify" onClick={handlePVerifyCode}>Confirm Code</button>
                  </div>
                  {pVerifyError && <span className="fb-field-error">{pVerifyError}</span>}
                  <small className="fb-field-hint" style={{ color: '#1a7f37', fontWeight: 600 }}>(Dev mode — your code is: {pGeneratedCode})</small>
                </div>
              )}
            </fieldset>
            {/* Address */}
            <fieldset className="fb-fieldset fb-address-fieldset">
              <legend>Address</legend>
              <div className="fb-address-stack">
                <select value={pForm.address_country} onChange={e => { handlePChange('address_country', e.target.value); setPPostalError(''); }} className="fb-input fb-input-full">
                  <option value="">Select Country</option>
                  {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input type="text" placeholder="Street Address" value={pForm.address_street} onChange={e => handlePChange('address_street', e.target.value)} className="fb-input fb-input-full" />
                <input type="text" placeholder="City / Town / Village" value={pForm.address_city} onChange={e => handlePChange('address_city', e.target.value)} className="fb-input fb-input-full" />
                <input type="text" placeholder="State / Province" value={pForm.address_state} onChange={e => handlePChange('address_state', e.target.value)} className="fb-input fb-input-full" />
                <div className="fb-postal-row">
                  <input type="text" placeholder="Zip Code / Postal Code" value={pForm.address_postal_code} onChange={e => handlePPostalChange(e.target.value)} className="fb-input fb-postal-input" />
                  {pPostalError && <span className="fb-field-error fb-postal-error">{pPostalError}</span>}
                </div>
              </div>
              <div className="fb-address-verify-row">
                {pAddressVerified ? <span className="fb-verified-badge"><FiCheck size={14} /> Address Verified</span>
                  : <button type="button" className="fb-btn-verify" onClick={handlePVerifyAddress} disabled={pAddressVerifying || !pForm.address_street || !pForm.address_city || !pForm.address_country}>{pAddressVerifying ? 'Verifying...' : 'Verify Address'}</button>}
              </div>
            </fieldset>
            {/* Mailing Address */}
            <fieldset className="fb-fieldset fb-address-fieldset fb-mailing-fieldset">
              <legend>Mailing Address</legend>
              <div className="fb-mailing-checkboxes">
                <label className="fb-checkbox-label"><input type="checkbox" checked={pMailSameAsPhysical} onChange={e => handlePMailSameToggle(e.target.checked)} /> Same as Physical</label>
                <label className="fb-checkbox-label"><input type="checkbox" checked={pMailAddNew} onChange={e => handlePMailAddNewToggle(e.target.checked)} /> Add New</label>
              </div>
              {pMailSameAsPhysical && <small className="fb-field-hint fb-mail-same-hint">Mailing address will use the physical address above.</small>}
              {pMailAddNew && !pMailSameAsPhysical && (
                <div className="fb-address-stack">
                  <select value={pForm.mail_address_country} onChange={e => { handlePChange('mail_address_country', e.target.value); setPMailPostalError(''); }} className="fb-input fb-input-full">
                    <option value="">Select Country</option>
                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input type="text" placeholder="Street Address" value={pForm.mail_address_street} onChange={e => handlePChange('mail_address_street', e.target.value)} className="fb-input fb-input-full" />
                  <input type="text" placeholder="City / Town / Village" value={pForm.mail_address_city} onChange={e => handlePChange('mail_address_city', e.target.value)} className="fb-input fb-input-full" />
                  <input type="text" placeholder="State / Province" value={pForm.mail_address_state} onChange={e => handlePChange('mail_address_state', e.target.value)} className="fb-input fb-input-full" />
                  <div className="fb-postal-row">
                    <input type="text" placeholder="Zip Code / Postal Code" value={pForm.mail_address_postal_code} onChange={e => handlePMailPostalChange(e.target.value)} className="fb-input fb-postal-input" />
                    {pMailPostalError && <span className="fb-field-error fb-postal-error">{pMailPostalError}</span>}
                  </div>
                </div>
              )}
            </fieldset>
            <div className="fb-form-actions">
              <button type="submit" className="fb-btn-submit"><FiCheck size={16} /> {editingId ? 'Update' : 'Add Client'}</button>
              <button type="button" className="fb-btn-cancel" onClick={closeForm}>Cancel</button>
            </div>
          </form>
          )}
          {/* Client's Family sub-tab */}
          {clientSubTab === 'family' && editingId && (
            <ClientFamilySubTab clientUser={personalClients.find(c => c.user_id === editingId) || { user_id: editingId, first_name: pForm.first_name, last_name: pForm.last_name, address_country: pForm.address_country }} />
          )}
          {/* Client's Businesses sub-tab */}
          {clientSubTab === 'businesses' && editingId && (
            <ClientBusinessesSubTab clientUser={personalClients.find(c => c.user_id === editingId) || { user_id: editingId, first_name: pForm.first_name, last_name: pForm.last_name, address_country: pForm.address_country }} />
          )}
        </div>
      )}

      {/* ============ BUSINESS CLIENT FORM ============ */}
      {formType === 'business' && (
        <div className="fb-family-form-card">
          <div className="fb-family-form-header">
            <h4>{editingId ? 'Edit Business Client' : 'Add Business Client'}</h4>
            <button className="fb-btn-icon" onClick={closeForm} title="Cancel"><FiX size={18} /></button>
          </div>
          <form className="fb-form" onSubmit={handleSubmitBusiness} noValidate>
            {/* Legal Name */}
            <fieldset className="fb-fieldset">
              <legend>Legal Name of Business</legend>
              <input type="text" placeholder="Full Legal Name *" value={bForm.busi_legal_name}
                onChange={e => handleBChange('busi_legal_name', e.target.value)} required
                className={`fb-input fb-input-full ${bFormErrors.busi_legal_name ? 'fb-input-error' : ''}`} />
              {bFormErrors.busi_legal_name && <span className="fb-field-error">{bFormErrors.busi_legal_name}</span>}
            </fieldset>
            {/* Operating + Display Name */}
            <div className="fb-name-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <fieldset className="fb-fieldset">
                <legend>Operating as Name</legend>
                <input type="text" placeholder="DBA name" value={bForm.busi_operating_name}
                  onChange={e => handleBChange('busi_operating_name', e.target.value)} className="fb-input fb-input-full" />
              </fieldset>
              <fieldset className="fb-fieldset">
                <legend>Display name in Portal</legend>
                <input type="text" placeholder="Portal display name" value={bForm.busi_display_name}
                  onChange={e => handleBChange('busi_display_name', e.target.value)} className="fb-input fb-input-full" />
              </fieldset>
            </div>
            {/* Acronym */}
            <fieldset className="fb-fieldset">
              <legend>Acronym for Business Name</legend>
              <input type="text" placeholder="Short acronym" value={bForm.busi_acronym}
                onChange={e => handleBChange('busi_acronym', e.target.value)}
                className={`fb-input fb-input-full ${bFormErrors.busi_acronym ? 'fb-input-error' : ''}`} />
              {bFormErrors.busi_acronym && <span className="fb-field-error">{bFormErrors.busi_acronym}</span>}
              <small className="fb-field-hint">Must be unique within your account.</small>
            </fieldset>
            {/* Type */}
            <fieldset className="fb-fieldset">
              <legend>Type</legend>
              <select value={bForm.busi_type} onChange={e => handleBChange('busi_type', e.target.value)} className="fb-input fb-input-full">
                <option value="">Select Type</option>
                {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </fieldset>
            {/* Registration Number */}
            <fieldset className="fb-fieldset">
              <legend>Business Registration Number</legend>
              <input type="text" placeholder="Registration Number" value={bForm.busi_registration_number}
                onChange={e => handleBChange('busi_registration_number', e.target.value)} className="fb-input fb-input-full" />
            </fieldset>
            <BaseCurrencyField
              value={bForm.base_currency}
              country={bForm.busi_address_country}
              ownerId={ownerId}
              users={allUsers}
              onChange={v => handleBChange('base_currency', v)}
            />
            {/* Phone */}
            <fieldset className="fb-fieldset">
              <legend>Business Phone</legend>
              <PhoneInput value={bForm.busi_phone}
                onChange={v => { handleBChange('busi_phone', v); setOwnerPhoneConfirmedCB(false); }}
                defaultCountry={bForm.busi_address_country || owner?.address_country}
                hasError={!!(bFormErrors.busi_phone && bFormErrors.busi_phone !== '__owner_phone__')}
                inputClassName={`fb-input ${bFormErrors.busi_phone && bFormErrors.busi_phone !== '__owner_phone__' ? 'fb-input-error' : ''}`} />
              {bFormErrors.busi_phone && bFormErrors.busi_phone !== '__owner_phone__' && (
                <span className="fb-field-error">{bFormErrors.busi_phone}</span>
              )}
              {bFormErrors.busi_phone === '__owner_phone__' && (
                <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '8px 12px', marginTop: 6 }}>
                  <span style={{ color: '#e65100', fontWeight: 500 }}>This is your personal phone number.</span>{' '}
                  <span>Use it as the client's business phone too?</span>
                  <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                    <button type="button" className="fb-btn-verify" style={{ fontSize: 13, padding: '4px 14px' }}
                      onClick={() => { setOwnerPhoneConfirmedCB(true); setBFormErrors(prev => { const n = { ...prev }; delete n.busi_phone; return n; }); }}>
                      Yes, use it
                    </button>
                    <button type="button" style={{ fontSize: 13, padding: '4px 14px', background: '#eee', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                      onClick={() => { handleBChange('busi_phone', ''); setBFormErrors(prev => { const n = { ...prev }; delete n.busi_phone; return n; }); }}>
                      No, I'll enter a different one
                    </button>
                  </div>
                </div>
              )}
            </fieldset>
            {/* Email */}
            <fieldset className="fb-fieldset">
              <legend>Business Email</legend>
              <div className="fb-email-row">
                <input type="email" placeholder="Business Email" value={bForm.busi_email}
                  onChange={e => { handleBChange('busi_email', e.target.value); setOwnerEmailConfirmedCB(false); if (bEmailVerifyState !== 'idle') setBEmailVerifyState('idle'); }}
                  className={`fb-input fb-email-input ${bFormErrors.busi_email && bFormErrors.busi_email !== '__owner_email__' ? 'fb-input-error' : ''}`} />
                {bEmailVerifyState === 'verified' ? <span className="fb-verified-badge"><FiCheck size={14} /> Verified</span>
                  : bEmailVerifyState === 'sent' ? null
                  : <button type="button" className="fb-btn-verify" onClick={handleBSendVerification} disabled={!bForm.busi_email.trim()}>Verify Email</button>}
              </div>
              {bFormErrors.busi_email && bFormErrors.busi_email !== '__owner_email__' && (
                <span className="fb-field-error">{bFormErrors.busi_email}</span>
              )}
              {bFormErrors.busi_email === '__owner_email__' && (
                <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '8px 12px', marginTop: 6 }}>
                  <span style={{ color: '#e65100', fontWeight: 500 }}>This is your personal email.</span>{' '}
                  <span>Use it as the client's business email too?</span>
                  <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                    <button type="button" className="fb-btn-verify" style={{ fontSize: 13, padding: '4px 14px' }}
                      onClick={() => { setOwnerEmailConfirmedCB(true); setBFormErrors(prev => { const n = { ...prev }; delete n.busi_email; return n; }); }}>
                      Yes, use it
                    </button>
                    <button type="button" style={{ fontSize: 13, padding: '4px 14px', background: '#eee', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                      onClick={() => { handleBChange('busi_email', ''); setOwnerEmailConfirmedCB(false); setBFormErrors(prev => { const n = { ...prev }; delete n.busi_email; return n; }); }}>
                      No, I'll enter a different one
                    </button>
                  </div>
                </div>
              )}
              {bEmailVerifyState === 'sent' && (
                <div className="fb-verify-code-row">
                  <input type="text" placeholder="Enter 6-digit code" value={bVerifyCode}
                    onChange={e => setBVerifyCode(e.target.value)} maxLength={6} className="fb-input fb-verify-input" />
                  <button type="button" className="fb-btn-verify" onClick={handleBVerifyCode}>Confirm Code</button>
                  {bVerifyError && <span className="fb-field-error">{bVerifyError}</span>}
                  <small className="fb-field-hint" style={{ color: '#1a7f37', fontWeight: 600 }}>(Dev mode — your code is: {bGeneratedCode})</small>
                </div>
              )}
            </fieldset>
            {/* Physical Address */}
            <fieldset className="fb-fieldset fb-address-fieldset">
              <legend>Physical Address</legend>
              <div className="fb-address-stack">
                <input type="text" placeholder="Address 1" value={bForm.busi_address_1}
                  onChange={e => handleBChange('busi_address_1', e.target.value)}
                  className={`fb-input fb-input-full ${bFormErrors.busi_address_1 ? 'fb-input-error' : ''}`} />
                {bFormErrors.busi_address_1 && <span className="fb-field-error">{bFormErrors.busi_address_1}</span>}
                <input type="text" placeholder="Address 2" value={bForm.busi_address_2}
                  onChange={e => handleBChange('busi_address_2', e.target.value)} className="fb-input fb-input-full" />
                <select value={bForm.busi_address_country}
                  onChange={e => { handleBChange('busi_address_country', e.target.value); setBPostalError(''); }}
                  className={`fb-input fb-input-full ${bFormErrors.busi_address_country ? 'fb-input-error' : ''}`}>
                  <option value="">Select Country</option>
                  {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {bFormErrors.busi_address_country && <span className="fb-field-error">{bFormErrors.busi_address_country}</span>}
                <input type="text" placeholder="State / Province" value={bForm.busi_address_state}
                  onChange={e => handleBChange('busi_address_state', e.target.value)}
                  className={`fb-input fb-input-full ${bFormErrors.busi_address_state ? 'fb-input-error' : ''}`} />
                {bFormErrors.busi_address_state && <span className="fb-field-error">{bFormErrors.busi_address_state}</span>}
                <div className="fb-postal-row">
                  <input type="text" placeholder="Zip / Postal Code" value={bForm.busi_address_postal_code}
                    onChange={e => handleBPostalChange(e.target.value)}
                    className={`fb-input fb-postal-input ${bFormErrors.busi_address_postal_code ? 'fb-input-error' : ''}`} />
                  {bPostalError && <span className="fb-field-error fb-postal-error">{bPostalError}</span>}
                </div>
                <input type="text" placeholder="City / Town / Village" value={bForm.busi_address_city}
                  onChange={e => handleBChange('busi_address_city', e.target.value)}
                  className={`fb-input fb-input-full ${bFormErrors.busi_address_city ? 'fb-input-error' : ''}`} />
                {bFormErrors.busi_address_city && <span className="fb-field-error">{bFormErrors.busi_address_city}</span>}
                <input type="text" placeholder="County / District" value={bForm.busi_address_county}
                  onChange={e => handleBChange('busi_address_county', e.target.value)} className="fb-input fb-input-full" />
              </div>
              <div className="fb-address-verify-row">
                {bAddressVerified ? <span className="fb-verified-badge"><FiCheck size={14} /> Address Verified</span>
                  : <button type="button" className="fb-btn-verify" onClick={handleBVerifyAddress}
                    disabled={bAddressVerifying || !bForm.busi_address_1 || !bForm.busi_address_city || !bForm.busi_address_country}>
                    {bAddressVerifying ? 'Verifying...' : 'Verify Address'}</button>}
              </div>
            </fieldset>
            {/* Mailing Address */}
            <fieldset className="fb-fieldset fb-address-fieldset fb-mailing-fieldset">
              <legend>Mailing Address</legend>
              <div className="fb-mailing-checkboxes">
                <label className="fb-checkbox-label"><input type="checkbox" checked={bMailSameAsPhysical} onChange={e => handleBMailSameToggle(e.target.checked)} /> Same as Physical</label>
                <label className="fb-checkbox-label"><input type="checkbox" checked={bMailAddNew} onChange={e => handleBMailAddNewToggle(e.target.checked)} /> Add New</label>
              </div>
              {bMailSameAsPhysical && <small className="fb-field-hint fb-mail-same-hint">Mailing address will use the physical address above.</small>}
              {bMailAddNew && !bMailSameAsPhysical && (
                <div className="fb-address-stack">
                  <input type="text" placeholder="Address 1" value={bForm.busi_mail_address_1} onChange={e => handleBChange('busi_mail_address_1', e.target.value)} className="fb-input fb-input-full" />
                  <input type="text" placeholder="Address 2" value={bForm.busi_mail_address_2} onChange={e => handleBChange('busi_mail_address_2', e.target.value)} className="fb-input fb-input-full" />
                  <select value={bForm.busi_mail_address_country} onChange={e => { handleBChange('busi_mail_address_country', e.target.value); setBMailPostalError(''); }} className="fb-input fb-input-full">
                    <option value="">Select Country</option>
                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input type="text" placeholder="State / Province" value={bForm.busi_mail_address_state} onChange={e => handleBChange('busi_mail_address_state', e.target.value)} className="fb-input fb-input-full" />
                  <div className="fb-postal-row">
                    <input type="text" placeholder="Zip / Postal Code" value={bForm.busi_mail_address_postal_code} onChange={e => handleBMailPostalChange(e.target.value)} className="fb-input fb-postal-input" />
                    {bMailPostalError && <span className="fb-field-error fb-postal-error">{bMailPostalError}</span>}
                  </div>
                  <input type="text" placeholder="City / Town / Village" value={bForm.busi_mail_address_city} onChange={e => handleBChange('busi_mail_address_city', e.target.value)} className="fb-input fb-input-full" />
                  <input type="text" placeholder="County / District" value={bForm.busi_mail_address_county} onChange={e => handleBChange('busi_mail_address_county', e.target.value)} className="fb-input fb-input-full" />
                </div>
              )}
            </fieldset>
            <div className="fb-form-actions">
              <button type="submit" className="fb-btn-submit"><FiCheck size={16} /> {editingId ? 'Update' : 'Submit'}</button>
              <button type="button" className="fb-btn-cancel" onClick={closeForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* ============ CLIENT CARDS LIST ============ */}
      {totalClients === 0 && !formType ? (
        <div className="fb-family-empty">
          <FiUsers size={40} />
          <p>No clients added yet.</p>
        </div>
      ) : totalClients > 0 && (
        <div className="fb-family-list fb-family-grid">
          {/* First tile: add a client (a business is added under a person — see the action bar) */}
          {!formType && (
            <div className="fb-family-add-split">
              <button type="button" className="fb-family-add-tile" onClick={() => { setBizClientFlow(true); openPersonalForm(); }}>
                <FiPlus size={16} />
                <span>Add a Business Client</span>
              </button>
              <button type="button" className="fb-family-add-tile" onClick={() => { setBizClientFlow(false); openPersonalForm(); }}>
                <FiPlus size={16} />
                <span>Add a Personal Client</span>
              </button>
            </div>
          )}
          {/* Business clients */}
          {businessClients.filter(b => !(formType === 'business' && editingId == b.business_id)).slice().sort((x, y) =>
            String(x.busi_legal_name || '').localeCompare(String(y.busi_legal_name || ''), undefined, { sensitivity: 'base' })
          ).map(b => (
            <div key={`biz-${b.business_id}`} className="fb-family-card">
              <div className="fb-family-card-clickable" onClick={() => openEditBusiness(b)}>
                <div className="fb-family-card-avatar fb-biz-avatar">
                  {(b.busi_acronym || b.busi_legal_name?.[0] || '?')}
                </div>
                <div className="fb-family-card-info">
                  <div className="fb-family-card-name">
                    {b.busi_legal_name}
                    {b.busi_acronym && <span className="fb-family-card-nickname">({b.busi_acronym})</span>}
                    <span className="fb-biz-type-tag" style={{marginLeft:'8px',background:'#1565c0'}}>Business Client</span>
                  </div>
                  {b.busi_operating_name && <div className="fb-family-card-relationship">Operating as: {b.busi_operating_name}</div>}
                  <div className="fb-family-card-details">
                    {b.busi_type && <span className="fb-biz-type-tag">{b.busi_type}</span>}
                    {b.busi_email && <span>{b.busi_email}</span>}
                    {b.busi_email_verified === 1 && <span className="fb-family-verified-tag">Email verified</span>}
                    {b.busi_phone && <span>{b.busi_phone}</span>}
                  </div>
                  <div className="fb-family-card-meta">
                    {b.busi_address_city && <span>{b.busi_address_city}{b.busi_address_state ? `, ${b.busi_address_state}` : ''}</span>}
                    {b.busi_address_verified === 1 && <span className="fb-family-verified-tag">Addr verified</span>}
                    {b.busi_registration_number && <span>Reg# {b.busi_registration_number}</span>}
                  </div>
                </div>
              </div>
              <div className="fb-family-card-actions">
                <button className="fb-btn-icon" title="Edit" onClick={() => openEditBusiness(b)}><FiEdit2 size={16} /></button>
                <button className="fb-btn-icon fb-btn-danger" title="Remove" onClick={() => handleRemoveBusiness(b.business_id)}><FiTrash2 size={16} /></button>
              </div>
            </div>
          ))}
          {/* Personal clients (expandable → their businesses + family) */}
          {personalClients.filter(m => !(formType === 'personal' && editingId == m.user_id)).slice().sort((x, y) =>
            `${x.first_name || ''} ${x.last_name || ''}`.localeCompare(`${y.first_name || ''} ${y.last_name || ''}`, undefined, { sensitivity: 'base' })
          ).map(m => {
            const clientBiz = businessesOfClient(m.user_id);
            const clientFam = familyOfClient(m.user_id);
            const isExpanded = expandedClients.has(String(m.user_id));
            return (
            <div key={`pers-${m.user_id}`} className={`fb-client-group${isExpanded ? ' fb-client-group-open' : ''}`}>
              <div className="fb-family-card">
                <button
                  className="fb-client-expand"
                  title={isExpanded ? 'Hide businesses & family' : 'Show businesses & family'}
                  onClick={() => toggleExpanded(m.user_id)}
                >
                  {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                </button>
                <div className="fb-family-card-clickable" onClick={() => openEditPersonal(m)}>
                  <div className="fb-family-card-avatar">
                    {(m.first_name?.[0] || '?')}{(m.last_name?.[0] || '')}
                  </div>
                  <div className="fb-family-card-info">
                    <div className="fb-family-card-name">
                      {m.first_name} {m.middle_name ? m.middle_name + ' ' : ''}{m.last_name}
                      {m.nickname && <span className="fb-family-card-nickname">({m.nickname})</span>}
                      <span className="fb-biz-type-tag" style={{marginLeft:'8px',background:'#2e7d32'}}>Personal Client</span>
                      {clientBiz.length > 0 && <span className="fb-client-count-pill">{clientBiz.length} biz</span>}
                      {clientFam.length > 0 && <span className="fb-client-count-pill">{clientFam.length} family</span>}
                    </div>
                    <div className="fb-family-card-details">
                      <span>{m.email}</span>
                      {m.email_verified === 1 && <span className="fb-family-verified-tag">Email verified</span>}
                      {m.mobile_number && <span>{m.mobile_number}</span>}
                    </div>
                    <div className="fb-family-card-meta">
                      {m.address_city && <span>{m.address_city}{m.address_state ? `, ${m.address_state}` : ''}</span>}
                      {m.address_verified === 1 && <span className="fb-family-verified-tag">Addr verified</span>}
                    </div>
                  </div>
                </div>
                <div className="fb-family-card-actions">
                  <button className="fb-btn-icon" title="Edit" onClick={() => openEditPersonal(m)}><FiEdit2 size={16} /></button>
                  <button className="fb-btn-icon fb-btn-danger" title="Remove" onClick={() => handleRemovePersonal(m.user_id)}><FiTrash2 size={16} /></button>
                </div>
              </div>

              {isExpanded && (
                <div className="fb-client-subtree">
                  {/* Businesses */}
                  <div className="fb-client-subgroup">
                    <div className="fb-client-subgroup-head">
                      <span className="fb-client-subgroup-title"><FiBriefcase size={13} /> Businesses ({clientBiz.length})</span>
                      <button className="fb-client-sub-add" onClick={() => openEditPersonal(m, 'businesses')}>
                        <FiPlus size={12} /> Add business
                      </button>
                    </div>
                    {clientBiz.length === 0 ? (
                      <div className="fb-client-sub-empty">No businesses yet.</div>
                    ) : clientBiz.map(b => (
                      <button key={`cb-${b.business_id}`} className="fb-client-sub-item" onClick={() => openEditPersonal(m, 'businesses')}>
                        <FiBriefcase size={13} className="fb-client-sub-ico" />
                        <span className="fb-client-sub-name">{b.busi_legal_name || b.busi_operating_name || 'Business'}{b.busi_acronym ? ` (${b.busi_acronym})` : ''}</span>
                        <FiArrowRight size={13} className="fb-client-sub-go" />
                      </button>
                    ))}
                  </div>
                  {/* Family */}
                  <div className="fb-client-subgroup">
                    <div className="fb-client-subgroup-head">
                      <span className="fb-client-subgroup-title"><FiUser size={13} /> Family ({clientFam.length})</span>
                      <button className="fb-client-sub-add" onClick={() => openEditPersonal(m, 'family')}>
                        <FiPlus size={12} /> Add family
                      </button>
                    </div>
                    {clientFam.length === 0 ? (
                      <div className="fb-client-sub-empty">No family members yet.</div>
                    ) : clientFam.map(f => (
                      <button key={`cf-${f.user_id}`} className="fb-client-sub-item" onClick={() => openEditPersonal(m, 'family')}>
                        <FiUser size={13} className="fb-client-sub-ico" />
                        <span className="fb-client-sub-name">{`${f.first_name || ''} ${f.last_name || ''}`.trim() || f.email}{f.relationship ? ` · ${f.relationship}` : ''}</span>
                        <FiArrowRight size={13} className="fb-client-sub-go" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// =============================================
// Manage Account Access Tab
// Three flows:
//   1. Add bookkeeper/accountant — owner invites a professional
//   2. Give access to another user — owner shares their data
//   3. Get access from another user — owner requests access to someone else's data
// =============================================

const ACCESS_ROLES = [
  { value: 'bookkeeper', label: 'Bookkeeper' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'tax_preparer', label: 'Tax Preparer' },
  { value: 'reviewer', label: 'Reviewer (Read-only)' },
  { value: 'delegate', label: 'Delegate (General)' },
];

const ACCESS_SCOPES = [
  { value: 'full', label: 'Full Access (Read + Write)' },
  { value: 'read_only', label: 'Read Only' },
  { value: 'bookkeeping', label: 'Bookkeeping Module Only' },
  { value: 'tax_filing', label: 'Tax Filing Module Only' },
  { value: 'documents', label: 'Documents Module Only' },
];

type AccessFormMode = 'add_professional' | 'give_access' | 'get_access' | null;

interface AccessFormFields {
  email: string;
  access_role: string;
  access_scope: string;
  notes: string;
}

const EMPTY_ACCESS_FORM: AccessFormFields = { email: '', access_role: 'bookkeeper', access_scope: 'full', notes: '' };

/** Get all account_access rows where this user is the data owner */
function getAccessGrantsAsOwner(ownerId: number | string, accessData?: Record<string, any>[]): Record<string, any>[] {
  const data = accessData || getTableData('user_account_access');
  // eslint-disable-next-line eqeqeq
  return data.filter(r => r.owner_user_id == ownerId && r.is_deleted !== 1);
}

/** Get all account_access rows where this user is the accessor */
function getAccessGrantsAsAccessor(userId: number | string, accessData?: Record<string, any>[]): Record<string, any>[] {
  const data = accessData || getTableData('user_account_access');
  // eslint-disable-next-line eqeqeq
  return data.filter(r => r.accessor_user_id == userId && r.is_deleted !== 1);
}

/** Look up a user by email */
function findUserByEmail(email: string, usersData?: Record<string, any>[]): Record<string, any> | undefined {
  const users = usersData || getTableData('users');
  return users.find(u => u.email?.toLowerCase() === email.toLowerCase() && u.is_deleted !== 1);
}

const ManageAccountAccessTab: React.FC = () => {
  const [owner, setOwner] = useState(() => getAccountOwner());
  const ownerId = owner?.user_id;
  const [allUsers, setAllUsers] = useState<Record<string, any>[]>(() => getTableData('users'));
  const [allAccess, setAllAccess] = useState<Record<string, any>[]>(() => getTableData('user_account_access'));

  const [grantsAsOwner, setGrantsAsOwner] = useState(() => ownerId ? getAccessGrantsAsOwner(ownerId) : []);
  const [grantsAsAccessor, setGrantsAsAccessor] = useState(() => ownerId ? getAccessGrantsAsAccessor(ownerId) : []);

  const [formMode, setFormMode] = useState<AccessFormMode>(null);
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [form, setForm] = useState<AccessFormFields>(EMPTY_ACCESS_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [savedMsg, setSavedMsg] = useState('');

  // Load from PostgreSQL if available
  useEffect(() => {
    Promise.all([
      loadTable('users'),
      loadTable('user_account_access'),
    ]).then(([usersRows, accessRows]) => {
      setAllUsers(usersRows);
      setAllAccess(accessRows);
      const o = getAccountOwner(usersRows);
      if (o) {
        setOwner(o);
        setGrantsAsOwner(getAccessGrantsAsOwner(o.user_id, accessRows));
        setGrantsAsAccessor(getAccessGrantsAsAccessor(o.user_id, accessRows));
      }
    }).catch(() => {});
  }, []);

  const refreshGrants = useCallback(async () => {
    if (!ownerId) return;
    try {
      const [usersRows, accessRows] = await Promise.all([
        loadTable('users'),
        loadTable('user_account_access'),
      ]);
      setAllUsers(usersRows);
      setAllAccess(accessRows);
      setGrantsAsOwner(getAccessGrantsAsOwner(ownerId, accessRows));
      setGrantsAsAccessor(getAccessGrantsAsAccessor(ownerId, accessRows));
    } catch {
      setGrantsAsOwner(getAccessGrantsAsOwner(ownerId));
      setGrantsAsAccessor(getAccessGrantsAsAccessor(ownerId));
    }
  }, [ownerId]);

  const handleChange = useCallback((field: keyof AccessFormFields, value: string) => {
    setFormDirty(true);
    setForm(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => ({ ...prev, [field]: '' }));
  }, []);

  const closeForm = useCallback(() => {
    setFormDirty(false); setFormMode(null); setEditingId(null); setForm(EMPTY_ACCESS_FORM); setFormErrors({});
  }, []);

  const openForm = useCallback((mode: AccessFormMode) => {
    setForm({ ...EMPTY_ACCESS_FORM, access_role: mode === 'add_professional' ? 'bookkeeper' : 'delegate', access_scope: mode === 'get_access' ? 'read_only' : 'full' });
    setFormErrors({}); setEditingId(null); setFormMode(mode);
  }, []);

  const openEdit = useCallback((grant: Record<string, any>) => {
    setForm({ email: grant.accessor_email || '', access_role: grant.access_role || 'bookkeeper', access_scope: grant.access_scope || 'full', notes: grant.notes || '' });
    setFormErrors({}); setEditingId(grant.access_id);
    // eslint-disable-next-line eqeqeq
    setFormMode(grant.direction === 'received' ? 'get_access' : (grant.access_role === 'bookkeeper' || grant.access_role === 'accountant' || grant.access_role === 'tax_preparer') ? 'add_professional' : 'give_access');
  }, []);

  // ===================== SUBMIT =====================
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ownerId || !owner) return;

    const errors: Record<string, string> = {};
    if (!form.email.trim()) errors.email = 'Email is required';
    else if (!isValidEmail(form.email.trim())) errors.email = 'Invalid email format (minimum: u@d.c)';
    if (form.email.trim().toLowerCase() === owner.email?.toLowerCase()) errors.email = 'You cannot grant access to yourself';
    if (!form.access_role) errors.access_role = 'Role is required';
    if (!form.access_scope) errors.access_scope = 'Scope is required';

    // Check for duplicate active grant
    if (!editingId) {
      const dup = allAccess.find(r => {
        if (r.is_deleted === 1) return false;
        if (r.status === 'revoked' || r.status === 'expired' || r.status === 'declined') return false;
        if (formMode === 'get_access') {
          // eslint-disable-next-line eqeqeq
          return r.accessor_user_id == ownerId && r.accessor_email?.toLowerCase() === form.email.trim().toLowerCase();
        }
        // eslint-disable-next-line eqeqeq
        return r.owner_user_id == ownerId && r.accessor_email?.toLowerCase() === form.email.trim().toLowerCase();
      });
      if (dup) errors.email = 'An active access grant already exists for this email';
    }

    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const targetEmail = form.email.trim();
    const existingUser = findUserByEmail(targetEmail, allUsers);

    try {
      if (editingId) {
        await saveRow('user_account_access', editingId, {
          access_role: form.access_role, access_scope: form.access_scope,
          notes: form.notes.trim() || null, updated_at: now,
        });
        console.log('[BK] Updated user_account_access access_id:', editingId);
      } else {
        let accessorUserId: number | string | null = null;
        let status: string;

        if (formMode === 'get_access') {
          const dataOwner = existingUser;
          if (!dataOwner) {
            setFormErrors({ email: 'No account found with this email. The user must have an account first.' });
            return;
          }
          const row: Record<string, any> = {
            owner_user_id: dataOwner.user_id,
            accessor_user_id: ownerId,
            accessor_email: owner.email,
            access_role: form.access_role, access_scope: form.access_scope,
            status: 'pending_approval', initiated_by_user_id: ownerId,
            direction: 'received',
            invited_at: null, approved_at: null, revoked_at: null, expires_at: null,
            notes: form.notes.trim() || null,
            is_active: 1, is_deleted: 0, created_at: now, updated_at: now,
          };
          const inserted = await addRow('user_account_access', row);
          console.log('[BK] Created access request (get) access_id:', inserted.access_id, 'requesting from user_id:', dataOwner.user_id);
        } else {
          if (existingUser) {
            accessorUserId = existingUser.user_id;
            status = 'pending_approval';
          } else {
            // Create invite user
            const inviteUserData: Record<string, any> = {
              owner_id: null, user_type_id: 8,
              first_name: '', middle_name: null, last_name: '',
              nickname: '', user_name: '', display_name: targetEmail,
              screen_name: null,
              relationship: null, email: targetEmail,
              mobile_number: null, secondary_mobile: null, home_phone: null,
              business_phone: null, extra_phones: null,
              gender: null,
              country_code: null, address_street: null, address_city: null,
              address_state: null, address_postal_code: null, address_country: null,
              address_verified: 0,
              mail_same_as_physical: 0, mail_address_street: null, mail_address_city: null,
              mail_address_state: null, mail_address_postal_code: null, mail_address_country: null,
              notification: 1, password_hash: 'HASH_invite_pending',
              email_verified: 0, mobile_verified: 0, home_phone_verified: 0,
              data_access_scope: 'all', is_active: 1, is_deleted: 0, created_at: now, updated_at: now,
            };
            const insertedUser = await addRow('users', inviteUserData);
            accessorUserId = insertedUser.user_id;
            status = 'invite_sent';
            console.log('[BK] Created invite user_id:', accessorUserId, 'for email:', targetEmail);
          }

          const row: Record<string, any> = {
            owner_user_id: ownerId,
            accessor_user_id: accessorUserId,
            accessor_email: targetEmail,
            access_role: form.access_role, access_scope: form.access_scope,
            status, initiated_by_user_id: ownerId,
            direction: 'given',
            invited_at: status === 'invite_sent' ? now : null,
            approved_at: null, revoked_at: null, expires_at: null,
            notes: form.notes.trim() || null,
            is_active: 1, is_deleted: 0, created_at: now, updated_at: now,
          };
          const inserted = await addRow('user_account_access', row);
          console.log('[BK] Created access grant access_id:', inserted.access_id, 'accessor:', accessorUserId);
        }
      }
    } catch (err: any) {
      setSavedMsg(err.message || 'Save failed');
      setTimeout(() => setSavedMsg(''), 3000);
      return;
    }

    await refreshGrants(); closeForm();
    setSavedMsg(editingId ? 'Access grant updated!' : formMode === 'get_access' ? 'Access request sent!' : 'Access granted!');
    setTimeout(() => setSavedMsg(''), 3000);
  }, [form, ownerId, owner, editingId, formMode, allUsers, allAccess, refreshGrants, closeForm]);

  // ===================== ACTIONS =====================
  const handleRevoke = useCallback(async (accessId: number | string) => {
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    try {
      await saveRow('user_account_access', accessId, { status: 'revoked', revoked_at: now, is_active: 0, updated_at: now });
      console.log('[BK] Revoked user_account_access access_id:', accessId);
    } catch (err: any) {
      setSavedMsg(err.message || 'Revoke failed');
      setTimeout(() => setSavedMsg(''), 3000);
      return;
    }
    await refreshGrants();
    setSavedMsg('Access revoked.'); setTimeout(() => setSavedMsg(''), 3000);
  }, [refreshGrants]);

  const handleApprove = useCallback(async (accessId: number | string) => {
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    try {
      await saveRow('user_account_access', accessId, { status: 'active', approved_at: now, updated_at: now });
      console.log('[BK] Approved user_account_access access_id:', accessId);
    } catch (err: any) {
      setSavedMsg(err.message || 'Approve failed');
      setTimeout(() => setSavedMsg(''), 3000);
      return;
    }
    await refreshGrants();
    setSavedMsg('Access approved!'); setTimeout(() => setSavedMsg(''), 3000);
  }, [refreshGrants]);

  const handleDecline = useCallback(async (accessId: number | string) => {
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    try {
      await saveRow('user_account_access', accessId, { status: 'declined', is_active: 0, updated_at: now });
      console.log('[BK] Declined user_account_access access_id:', accessId);
    } catch (err: any) {
      setSavedMsg(err.message || 'Decline failed');
      setTimeout(() => setSavedMsg(''), 3000);
      return;
    }
    await refreshGrants();
    setSavedMsg('Access declined.'); setTimeout(() => setSavedMsg(''), 3000);
  }, [refreshGrants]);

  const handleDelete = useCallback(async (accessId: number | string) => {
    try {
      await removeRow('user_account_access', accessId);
      console.log('[BK] Soft-deleted user_account_access access_id:', accessId);
    } catch (err: any) {
      setSavedMsg(err.message || 'Delete failed');
      setTimeout(() => setSavedMsg(''), 3000);
      return;
    }
    await refreshGrants();
    setSavedMsg('Access entry removed.'); setTimeout(() => setSavedMsg(''), 3000);
  }, [refreshGrants]);

  /** Look up a user display name from user_id */
  const getUserName = useCallback((userId: number | string | null): string => {
    if (!userId) return '—';
    // eslint-disable-next-line eqeqeq
    const u = allUsers.find(x => x.user_id == userId);
    if (!u) return `User #${userId}`;
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
    return name || u.display_name || u.email || `User #${userId}`;
  }, [allUsers]);

  // "Save & continue" for this form (modal).
  useEffect(() => {
    registerSaveHandler(submitSaver(handleSubmit));
    return () => registerSaveHandler(null);
  }, [handleSubmit]);

  if (!owner || !ownerId) {
    return <div className="fb-no-owner">No account owner found. Please set up your account first.</div>;
  }

  // Separate pending incoming requests (others requesting MY data)
  const pendingIncoming = grantsAsOwner.filter(g => g.status === 'pending_approval' && g.direction === 'received');
  const activeGiven = grantsAsOwner.filter(g => g.status !== 'pending_approval' || g.direction !== 'received');
  const totalGrants = grantsAsOwner.length + grantsAsAccessor.length;

  const formTitle = formMode === 'add_professional' ? (editingId ? 'Edit Professional Access' : 'Add Bookkeeper or Accountant')
    : formMode === 'give_access' ? (editingId ? 'Edit Access Grant' : 'Give Access to Another User')
    : formMode === 'get_access' ? (editingId ? 'Edit Access Request' : 'Get Access from Another User')
    : '';

  const statusColor = (s: string) => {
    switch (s) {
      case 'active': return '#2e7d32';
      case 'pending_approval': return '#e65100';
      case 'invite_sent': return '#1565c0';
      case 'revoked': return '#b71c1c';
      case 'expired': return '#616161';
      case 'declined': return '#880e4f';
      default: return '#616161';
    }
  };

  const roleLabelMap: Record<string, string> = { bookkeeper: 'Bookkeeper', accountant: 'Accountant', tax_preparer: 'Tax Preparer', reviewer: 'Reviewer', delegate: 'Delegate' };
  const scopeLabelMap: Record<string, string> = { full: 'Full Access', read_only: 'Read Only', bookkeeping: 'Bookkeeping', tax_filing: 'Tax Filing', documents: 'Documents' };

  return (
    <div className="fb-family-section">
      {/* Info Banner */}
      <div className="fb-info-banner">
        <p><strong>Manage who can access your account data.</strong></p>
        <p>Add a bookkeeper, accountant, or tax preparer to help manage your books. You can also share or request access with other users on the platform.</p>
        <p className="fb-info-perm">
          <strong>Security:</strong> All access grants can be revoked at any time. Pending invites require approval before any data is shared.
        </p>
      </div>

      {/* Pending Incoming Requests */}
      {pendingIncoming.length > 0 && (
        <div className="fb-info-banner" style={{ borderLeft: '4px solid #e65100', background: '#fff3e0' }}>
          <p><strong>Pending Access Requests ({pendingIncoming.length})</strong></p>
          <p>The following users are requesting access to your data:</p>
          {pendingIncoming.map(g => (
            <div key={`pending-${g.access_id}`} className="fb-family-card" style={{ margin: '8px 0', background: '#fff8e1' }}>
              <div className="fb-family-card-avatar" style={{ background: '#e65100' }}>
                {(g.accessor_email?.[0] || '?').toUpperCase()}
              </div>
              <div className="fb-family-card-info">
                <div className="fb-family-card-name">{getUserName(g.accessor_user_id)}</div>
                <div className="fb-family-card-details">
                  <span>{g.accessor_email}</span>
                  <span className="fb-biz-type-tag" style={{ background: statusColor(g.status) }}>{g.status.replace('_', ' ')}</span>
                </div>
                <div className="fb-family-card-meta">
                  <span>Wants: {roleLabelMap[g.access_role] || g.access_role} — {scopeLabelMap[g.access_scope] || g.access_scope}</span>
                  {g.notes && <span>Note: {g.notes}</span>}
                </div>
              </div>
              <div className="fb-family-card-actions" style={{ gap: '6px' }}>
                <button className="fb-btn-submit" style={{ padding: '4px 12px', fontSize: '0.85rem' }} onClick={() => handleApprove(g.access_id)}><FiCheck size={14} /> Approve</button>
                <button className="fb-btn-cancel" style={{ padding: '4px 12px', fontSize: '0.85rem' }} onClick={() => handleDecline(g.access_id)}>Decline</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Bar */}
      <div className="fb-family-action-bar">
        <span className="fb-family-count">
          <FiShield size={16} />
          {totalGrants} access grant{totalGrants !== 1 ? 's' : ''}
        </span>
        {!formMode && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button className="fb-btn-add" onClick={() => openForm('add_professional')}>
              <FiUserPlus size={16} /> Add Bookkeeper / Accountant
            </button>
            <button className="fb-btn-add" onClick={() => openForm('give_access')}>
              <FiSend size={16} /> Give Access to Another User
            </button>
            <button className="fb-btn-add" onClick={() => openForm('get_access')}>
              <FiShield size={16} /> Get Access from Another User
            </button>
          </div>
        )}
        {savedMsg && <span className="fb-saved-msg">{savedMsg}</span>}
      </div>

      {/* ============ FORM ============ */}
      {formMode && (
        <div className="fb-family-form-card">
          <div className="fb-family-form-header">
            <h4>{formTitle}</h4>
            <button className="fb-btn-icon" onClick={closeForm} title="Cancel"><FiX size={18} /></button>
          </div>
          <form className="fb-form" onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <fieldset className="fb-fieldset">
              <legend>{formMode === 'get_access' ? 'Data Owner\'s Email *' : 'Recipient\'s Email *'}</legend>
              <input type="email" placeholder={formMode === 'get_access' ? 'Email of the user whose data you want access to' : 'Email of the person you are granting access'}
                value={form.email} onChange={e => handleChange('email', e.target.value)} required
                disabled={!!editingId}
                className={`fb-input fb-input-full ${formErrors.email ? 'fb-input-error' : ''}`} />
              {formErrors.email && <span className="fb-field-error">{formErrors.email}</span>}
              {formMode !== 'get_access' && !editingId && (
                <small className="fb-field-hint">If this email is not registered, an invite will be sent and a placeholder account created.</small>
              )}
              {formMode === 'get_access' && !editingId && (
                <small className="fb-field-hint">The data owner must approve your request before access is granted.</small>
              )}
            </fieldset>
            {/* Role */}
            <fieldset className="fb-fieldset">
              <legend>Access Role *</legend>
              <select value={form.access_role} onChange={e => handleChange('access_role', e.target.value)} required className="fb-input fb-input-full">
                {ACCESS_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              {formErrors.access_role && <span className="fb-field-error">{formErrors.access_role}</span>}
            </fieldset>
            {/* Scope */}
            <fieldset className="fb-fieldset">
              <legend>Access Scope *</legend>
              <select value={form.access_scope} onChange={e => handleChange('access_scope', e.target.value)} required className="fb-input fb-input-full">
                {ACCESS_SCOPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              {formErrors.access_scope && <span className="fb-field-error">{formErrors.access_scope}</span>}
            </fieldset>
            {/* Notes */}
            <fieldset className="fb-fieldset">
              <legend>Notes</legend>
              <textarea placeholder="Optional notes about this access grant" value={form.notes}
                onChange={e => handleChange('notes', e.target.value)}
                className="fb-input fb-input-full" rows={3} style={{ resize: 'vertical' }} />
            </fieldset>
            <div className="fb-form-actions">
              <button type="submit" className="fb-btn-submit">
                {formMode === 'get_access' ? <><FiSend size={16} /> {editingId ? 'Update Request' : 'Send Request'}</>
                  : <><FiCheck size={16} /> {editingId ? 'Update' : formMode === 'add_professional' ? 'Add Professional' : 'Grant Access'}</>}
              </button>
              <button type="button" className="fb-btn-cancel" onClick={closeForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* ============ ACCESS GRANTS LIST ============ */}
      {totalGrants === 0 && !formMode ? (
        <div className="fb-family-empty">
          <FiShield size={40} />
          <p>No access grants yet.</p>
        </div>
      ) : (
        <div className="fb-family-list fb-family-grid">
          {/* Access I've given (my data shared with others) */}
          {activeGiven.length > 0 && (
            <>
              <div className="fb-family-grid-heading" style={{ padding: '8px 0 4px', fontWeight: 600, fontSize: '0.95rem', color: '#333', borderBottom: '1px solid #e0e0e0' }}>
                Access You've Given (Your Data → Others)
              </div>
              {activeGiven.map(g => {
                const canEdit = g.status === 'active' || g.status === 'pending_approval' || g.status === 'invite_sent';
                return (
                <div key={`given-${g.access_id}`} className="fb-family-card">
                  <div className={canEdit ? 'fb-family-card-clickable' : 'fb-family-card-body'} onClick={canEdit ? () => openEdit(g) : undefined}>
                    <div className="fb-family-card-avatar" style={{ background: g.status === 'active' ? '#2e7d32' : g.status === 'invite_sent' ? '#1565c0' : '#757575' }}>
                      {(g.accessor_email?.[0] || '?').toUpperCase()}
                    </div>
                    <div className="fb-family-card-info">
                      <div className="fb-family-card-name">
                        {getUserName(g.accessor_user_id)}
                        <span className="fb-biz-type-tag" style={{ marginLeft: '8px', background: statusColor(g.status) }}>
                          {g.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="fb-family-card-details">
                        <span>{g.accessor_email}</span>
                        <span className="fb-biz-type-tag">{roleLabelMap[g.access_role] || g.access_role}</span>
                        <span className="fb-biz-type-tag" style={{ background: '#546e7a' }}>{scopeLabelMap[g.access_scope] || g.access_scope}</span>
                      </div>
                      <div className="fb-family-card-meta">
                        {g.approved_at && <span>Approved: {g.approved_at}</span>}
                        {g.invited_at && !g.approved_at && <span>Invited: {g.invited_at}</span>}
                        {g.revoked_at && <span>Revoked: {g.revoked_at}</span>}
                        {g.notes && <span>Note: {g.notes}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="fb-family-card-actions">
                    {canEdit && (
                      <button className="fb-btn-icon" title="Edit" onClick={() => openEdit(g)}><FiEdit2 size={16} /></button>
                    )}
                    {canEdit && (
                      <button className="fb-btn-icon fb-btn-danger" title="Revoke" onClick={() => handleRevoke(g.access_id)}><FiX size={16} /></button>
                    )}
                    {(g.status === 'revoked' || g.status === 'expired' || g.status === 'declined') && (
                      <button className="fb-btn-icon fb-btn-danger" title="Remove" onClick={() => handleDelete(g.access_id)}><FiTrash2 size={16} /></button>
                    )}
                  </div>
                </div>
                );
              })}
            </>
          )}

          {/* Access I've received (others' data shared with me) */}
          {grantsAsAccessor.length > 0 && (
            <>
              <div className="fb-family-grid-heading" style={{ padding: '12px 0 4px', fontWeight: 600, fontSize: '0.95rem', color: '#333', borderBottom: '1px solid #e0e0e0' }}>
                Access You've Received (Others' Data → You)
              </div>
              {grantsAsAccessor.map(g => (
                <div key={`rcvd-${g.access_id}`} className="fb-family-card">
                  <div className="fb-family-card-avatar" style={{ background: g.status === 'active' ? '#1565c0' : '#757575' }}>
                    {getUserName(g.owner_user_id)?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="fb-family-card-info">
                    <div className="fb-family-card-name">
                      {getUserName(g.owner_user_id)}'s data
                      <span className="fb-biz-type-tag" style={{ marginLeft: '8px', background: statusColor(g.status) }}>
                        {g.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="fb-family-card-details">
                      <span className="fb-biz-type-tag">{roleLabelMap[g.access_role] || g.access_role}</span>
                      <span className="fb-biz-type-tag" style={{ background: '#546e7a' }}>{scopeLabelMap[g.access_scope] || g.access_scope}</span>
                    </div>
                    <div className="fb-family-card-meta">
                      {g.approved_at && <span>Since: {g.approved_at}</span>}
                      {g.status === 'pending_approval' && g.direction === 'received' && <span>Awaiting owner's approval</span>}
                      {g.notes && <span>Note: {g.notes}</span>}
                    </div>
                  </div>
                  <div className="fb-family-card-actions">
                    {g.status === 'pending_approval' && g.direction === 'received' && (
                      <button className="fb-btn-icon fb-btn-danger" title="Cancel request" onClick={() => handleRevoke(g.access_id)}><FiX size={16} /></button>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================
// Main Family & Businesses Page
// =============================================
const FamilyBusinessesPage: React.FC<{ scope?: 'self' | 'clients' }> = ({ scope = 'self' }) => {
  // Your Accounts shows the owner's own entities; Client's Accounts shows the client roster.
  const visibleTabs = useMemo(
    () => scope === 'clients'
      ? TABS.filter(t => t.key === 'your-clients')
      : TABS.filter(t => t.key !== 'your-clients'),
    [scope],
  );
  const [activeTab, setActiveTab] = useState<TabKey>(scope === 'clients' ? 'your-clients' : 'about-yourself');
  const [tabResetKey, setTabResetKey] = useState(0);

  const handleTabClick = useCallback(async (tabKey: TabKey) => {
    if (!(await confirmDiscardChanges())) return;
    if (activeTab === tabKey) {
      setTabResetKey(prev => prev + 1);
    } else {
      setActiveTab(tabKey);
    }
  }, [activeTab]);

  const renderTabContent = () => {
    const k = tabResetKey;
    switch (activeTab) {
      case 'about-yourself':
        return <AboutYourselfTab key={k} />;
      case 'your-family':
        return <YourFamilyTab key={k} />;
      case 'your-businesses':
        return <YourBusinessesTab key={k} />;
      case 'your-clients':
        return <YourClientsTab key={k} />;
      default:
        return null;
    }
  };

  return (
    <div className="fb-page">
      {/* Info Banner + tab bar — only for Your Accounts. Client's Accounts has a
          single view (the client roster), so the banner/tabs are redundant there. */}
      {scope !== 'clients' && (
        <>
          <div className="fb-page-banner">
            <p>This is the place where you enter details of yourself, your family members, your business(es) and your clients (if you are bookkeeper or accountant)</p>
            <p>These names will be used in transactions when you record your income and expenses.</p>
          </div>

          <div className="fb-tabs">
            {visibleTabs.map(tab => (
              <button
                key={tab.key}
                className={`fb-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => handleTabClick(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Tab Content */}
      <div className="fb-tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export { ManageAccountAccessTab };
export default FamilyBusinessesPage;
